import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, Edit3, GripVertical, ChevronDown, ChevronRight, Search, Code2, Power, Eye, X, ExternalLink, AlertTriangle, Hash, Link2, Copy } from 'lucide-react'
import type { TransactionModel, SqlModel, ColumnDef, TreeNode, EditMask, JoinDef, ItemChange, ItemChangeRule, RowCompleteEvent, RowCompleteSettings, ListViewConfig, EnforcedCondition, EnforcedFilterOp, EnforcedFilterSet, Validation, ValidationRule, Action, ActionDef, ActionHandlerKind, ActionScope, ConfirmMode, FollowupActionDef } from './types'
import { normalizeItemChange, itemChangeSummary, lookupSummary, validationSummary, isValidationRule, legacyActionToDef, actionName, actionScope, isMergedSystemAction, ACTION_HANDLER_LABELS, ACTION_SCOPE_LABELS, CONFIRM_MODE_LABELS, normalizeFollowupAction, filterOpsForColumn, filterInputKind, acceptsCalendarDefault } from './types'
import {
  allSqlModels, childFormsOf, descendantFormNos, headerFormNo, isCalendarTokenValue,
  parentFormNoOf,
} from '@twasta/page-runtime/txn/utils'
import {
  addMissingParentKeyColumns, parentColumnNames, pkColumnsCovered, duplicateColumn, makeUnlinkedForm,
} from './formFactory'
import { useConfirm, type ConfirmOptions } from '@/components/common/ConfirmDialog'
import { settingsSections } from './setupCatalog'
import ValidationRuleEditor from './ValidationRuleEditor'
import ApprovalsEditor from './ApprovalsEditor'
import OpenActionEditor from './OpenActionEditor'
import SendActionEditor from './SendActionEditor'
import WizardActionEditor from './WizardActionEditor'
import ContentPanelSettings from './ContentPanelSettings'
import AdditionalAttributesSettings from './AdditionalAttributesSettings'
import WorkRulesEditor from './WorkRulesEditor'
import { LineCommandPanel } from './LineEntryPanel'
import ItemChangeRuleEditor from './ItemChangeRuleEditor'
import ItemChangeCodeEditor from './ItemChangeCodeEditor'
import LookupEditor, { type LookupPatch } from './LookupEditor'
import RowSecurityEditor from './RowSecurityEditor'
import DisplayFormatEditor from './DisplayFormatEditor'
import SeriesEditor, { seriesSummary } from './SeriesEditor'
import EditMaskEditor from './EditMaskEditor'
import DefaultValuePicker, {
  validateTokenDefault, isAutoGenToken, isPrefixedSeriesToken,
} from '@/components/designers/DefaultValuePicker'
import type { DisplayFormat } from '@twasta/page-runtime/txn'
import { inputType, minTextLength, maxTextLength } from '@twasta/page-runtime/txn'
import Modal from '@/components/common/Modal'
import {
  AUDIT_TOKENS, AUDIT_TOKEN_LABELS,
  CALENDAR_TOKENS, CALENDAR_TOKEN_LABELS,
  auditTokenOf, calendarTokenOf, projectVarOf, projectVarToken,
} from '@/livepreview/utils'
import { useProjectStore } from '@/store/projectStore'
import {
  type ProjectVariable, listProjectVariables,
} from '@/api/projectVariables'
import { getIntegrationDesign, type IntegrationEvent } from '@/api/integration'
import toast from 'react-hot-toast'
import { listContentSites } from '@/api/contentCollections'
import { loadReportObject, saveReportObject } from '@/api/designers'
import { blankReportModel, defaultReportJrxml } from '../report/types'

// One-line summary of a column's display format for the property card.
const DISPLAY_FORMAT_LABELS: Record<string, string> = {
  number: 'Number / currency', signed: 'Signed delta', data_bar: 'Data bar',
  progress: 'Progress bar', percent_ring: 'Percent ring', heatmap: 'Heatmap',
  rating: 'Rating', bullet: 'Target vs actual', sparkline: 'Sparkline',
  status_tag: 'Status tag', status_dot: 'Status dot', icon_map: 'Icon map',
  boolean: 'Yes / No', tags: 'Tags', date: 'Date / time', relative_time: 'Relative time',
  date_range: 'Date range', countdown: 'Countdown / SLA', case: 'Change case',
  truncate: 'Truncate', masked: 'Masked', template: 'Template', two_line: 'Two-line',
  telephone: 'Telephone', email: 'Email', url: 'Link', image: 'Image', file: 'File',
  avatar: 'Avatar', qr: 'QR code', map: 'Map pin', copy: 'Copy', background: 'Conditional background',
}
function displayFormatSummary(display?: DisplayFormat): string {
  if (!display || !display.type || display.type === 'text') return ''
  const base = DISPLAY_FORMAT_LABELS[display.type] || display.type
  if (display.type === 'heatmap' && display.scale) return `${base} (${display.scale})`
  if (display.type === 'number' && display.numberStyle && display.numberStyle !== 'decimal') return `${base} · ${display.numberStyle}`
  return base
}

interface PropertyPanelProps {
  model: TransactionModel
  selectedNode: TreeNode | null
  onModelChange: (model: TransactionModel) => void
  // Open a business-logic event's source file in the code editor (prompts to
  // save unsaved designer changes first). Provided by TransactionDesignerPage.
  onOpenCode?: (method?: string) => void
  // Project folder name — threaded from TransactionDesignerPage so panels that
  // call project-scoped APIs (e.g. the Approvals publish/versions endpoints)
  // have it directly. Falls back to the project-store value when omitted.
  project?: string
  // Wraps an in-app navigation so unsaved transaction changes prompt to save
  // first (TransactionDesignerPage's discard guard). Used by the Approvals
  // editor's "Open in Workflow Designer" so edits aren't lost on the jump.
  onGuardedNavigate?: (action: () => void) => void
  // Moves the selection (the Line Entry node keeps its selected button on
  // the node — the centre panel and this one share it).
  onSelectNode?: (node: TreeNode) => void
  // One step back in the designer's history — offered as "Undo" on the
  // toast after a delete.
  onUndo?: () => void
  // Open a Settings-workspace section (record settings live there now).
  onOpenSettings?: (node: TreeNode) => void
}


// Styles
const sectionStyle: React.CSSProperties = {
  marginBottom: 16,
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.8625rem',
  fontWeight: 600,
  color: 'var(--color-accent)',
  marginBottom: 8,
  paddingBottom: 4,
  borderBottom: '1px solid var(--color-border)',
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  marginBottom: 2,
  marginTop: 8,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 6,
  color: 'var(--color-text)',
  fontSize: '0.9375rem', minHeight: 36, boxSizing: 'border-box',
}

const checkboxRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginTop: 6,
  fontSize: '0.9375rem',
  color: 'var(--color-text)',
}

// Inline caution under an authored value that the run time cannot honour —
// shown rather than silently corrected, so the designer sees WHY.
const tokenWarnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 4,
  marginTop: 4,
  fontSize: '0.8125rem',
  lineHeight: 1.35,
  color: '#b26a00',
}

// A text button that reads as a link inside a hint line.
const linkBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  color: 'var(--color-accent)',
  cursor: 'pointer',
  textDecoration: 'underline',
}

const listItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 10px',
  background: 'var(--color-surface-2)',
  borderRadius: 6,
  marginBottom: 4,
  fontSize: '0.9375rem',
  color: 'var(--color-text)',
}

const addBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 8px',
  fontSize: '0.8125rem',
  color: 'var(--color-accent)',
  background: 'none',
  border: '1px dashed var(--color-border)',
  borderRadius: 4,
  cursor: 'pointer',
  width: '100%',
  marginTop: 4,
}

function toBool(v: boolean | string | number | undefined): boolean {
  return v === true || v === 'true' || v === '1' || v === 1
}

// Canonical column types from the shared backend type registry (legacy
// aliases NUMBER/DATETIME stay first-class so existing models keep working).
const COL_TYPES = [
  'VARCHAR', 'CHAR', 'TEXT', 'RICHTEXT',
  'NUMBER', 'INTEGER', 'BIGINT', 'SMALLINT', 'DOUBLE', 'REAL',
  'DATE', 'DATETIME', 'TIMESTAMPTZ', 'TIME', 'INTERVAL',
  'BOOLEAN', 'JSON', 'JSONB', 'UUID', 'BYTEA',
] as const

// Operator labels for the enforced-filter condition editor.
const OP_LABELS: Record<EnforcedFilterOp, string> = {
  // 'between' is spelled out: it is the one operator that changes the SHAPE of
  // the input (a from–to pair rather than a single value), and an abbreviation
  // of it beside the symbols is easy to scan past — which is how a from–to
  // range ends up authored as two conditions instead.
  between: 'from–to', eq: '=', neq: '≠', in: 'in',
  gt: '>', gte: '≥', lt: '<', lte: '≤',
  contains: 'contains', starts_with: 'starts with',
}
const JOIN_TYPES = ['inner', 'left', 'right', 'full'] as const

// Helper to deep clone model
function cloneModel(m: TransactionModel): TransactionModel {
  return JSON.parse(JSON.stringify(m))
}

// The panel body runs under one shared in-app confirmation dialog (no native
// confirm()) — mounted once here so every branch below can ask.
export default function PropertyPanel(props: PropertyPanelProps) {
  const { confirm, confirmDialog } = useConfirm()
  return (
    <>
      <PropertyPanelBody {...props} confirm={confirm} />
      {confirmDialog}
    </>
  )
}

type ColumnTab = 'general' | 'data' | 'display' | 'behaviour' | 'rules'
const COLUMN_TABS: Array<{ key: ColumnTab; label: string }> = [
  { key: 'general', label: 'General' }, { key: 'data', label: 'Data' },
  { key: 'display', label: 'Display' }, { key: 'behaviour', label: 'Behaviour' },
  { key: 'rules', label: 'Rules' },
]
type FormTab = 'general' | 'data' | 'access' | 'list'
const FORM_TABS: Array<{ key: FormTab; label: string }> = [
  { key: 'general', label: 'General' }, { key: 'data', label: 'Data source' },
  { key: 'access', label: 'Access' }, { key: 'list', label: 'List' },
]
const tabBarStyle: React.CSSProperties = {
  display: 'flex', gap: 2, padding: '0 8px', borderBottom: '1px solid var(--color-border)',
  overflowX: 'auto', flexShrink: 0,
}
const tabStyle = (on: boolean): React.CSSProperties => ({
  font: 'inherit', fontSize: '0.9375rem', border: 'none', background: 'none', cursor: 'pointer',
  padding: '10px 8px', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6,
  borderBottom: `2px solid ${on ? 'var(--color-accent)' : 'transparent'}`,
  color: on ? 'var(--color-text)' : 'var(--color-text-secondary)', fontWeight: on ? 600 : 400,
})
const tabCountStyle: React.CSSProperties = {
  fontSize: '0.8125rem', background: 'var(--color-surface-2)', borderRadius: 8, padding: '0 6px', fontWeight: 400,
}
const hintBoxStyle: React.CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.8125rem', lineHeight: 1.45,
  color: 'var(--color-text-secondary)', background: 'var(--color-surface-2)', borderRadius: 6,
  padding: '8px 10px', marginTop: 6,
}
const inspectorRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', font: 'inherit',
  fontSize: '0.9375rem', padding: '10px 8px', background: 'transparent', border: 'none',
  borderBottom: '1px solid var(--color-border)', cursor: 'pointer', color: 'var(--color-text)',
}
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden',
}
const cardHeadStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', fontSize: '0.9375rem', fontWeight: 600,
  background: 'var(--color-accent-weak)',
}

function PropertyPanelBody({ model, selectedNode, onModelChange, onOpenCode, project, onGuardedNavigate, onSelectNode, onUndo, onOpenSettings, confirm }: PropertyPanelProps & {
  confirm: (o: ConfirmOptions) => Promise<boolean>
}) {
  const navigate = useNavigate()
  // Column inspector: which columns have their database column "set
  // separately" this session (a differing db_name already is), and which
  // show the computed-value box before it has a value.
  const [separateDb, setSeparateDb] = useState<Set<string>>(new Set())
  const [showComputed, setShowComputed] = useState<Set<string>>(new Set())
  const [newGroupDraft, setNewGroupDraft] = useState<string | null>(null)
  useEffect(() => { setNewGroupDraft(null) }, [selectedNode?.formIndex, selectedNode?.columnIndex])

  // Delete a column after an in-app confirmation; the toast offers Undo.
  const deleteColumn = async (fi: number, ci: number) => {
    const c = model.sql_models[fi]?.sql_model?.columns[ci]?.column
    if (!c) return
    const ok = await confirm({
      title: 'Delete column?', message: <>Delete column <b>{c.name}</b>? Its rules, lookup and logic go with it.</>,
      confirmLabel: 'Delete column',
    })
    if (!ok) return
    const m = cloneModel(model)
    m.sql_models[fi].sql_model.columns.splice(ci, 1)
    onModelChange(m)
    onSelectNode?.({ type: 'form', formIndex: fi, label: m.sql_models[fi].sql_model.form_title || '' })
    if (onUndo) {
      toast((t) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          Column {c.name} deleted
          <button className="btn btn-ghost btn-sm" onClick={() => { onUndo(); toast.dismiss(t.id) }}>Undo</button>
        </span>
      ), { duration: 6000 })
    }
  }
  // Does the application have a website? "Publish on website" means nothing
  // without one, so the block is offered only when it does. null = not known
  // yet (loading, or the lookup failed — then the block stays visible rather
  // than hiding a setting the designer may need).
  const [hasWebsite, setHasWebsite] = useState<boolean | null>(null)
  const [websiteLookupFailed, setWebsiteLookupFailed] = useState(false)
  useEffect(() => {
    if (!project) return
    let cancelled = false
    listContentSites(project)
      .then(r => { if (!cancelled) setHasWebsite((r.sites || []).length > 0) })
      .catch(err => {
        console.warn('[PropertyPanel] could not list the application\'s websites', err)
        if (!cancelled) setWebsiteLookupFailed(true)
      })
    return () => { cancelled = true }
  }, [project])
  // Structured field/form validation rules (the 4-type editor).
  const [ruleValidationOpen, setRuleValidationOpen] = useState(false)
  const [editingRuleValidation, setEditingRuleValidation] = useState<{
    value: Validation
    formColumns: string[]
    headerColumns: string[]
    onSave: (v: Validation) => void
  } | null>(null)
  const [ruleEditorOpen, setRuleEditorOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<{
    rule: ItemChangeRule | null
    triggerColumn: string
    formColumns: string[]
    headerColumns: string[]
    onSave: (rule: ItemChange) => void
  } | null>(null)
  // item_change Business Logic / Call API editor (the non-rule modes).
  const [codeEditorOpen, setCodeEditorOpen] = useState(false)
  const [editingCode, setEditingCode] = useState<{
    value: ItemChange | null
    initialMode: 'business_logic' | 'rest_api'
    formColumns: string[]
    headerColumns: string[]
    onSave: (ic: ItemChange) => void
    title?: string
    helpText?: React.ReactNode
  } | null>(null)
  const [seriesEditorOpen, setSeriesEditorOpen] = useState(false)
  // Addressed by position rather than by column object so the editor always
  // patches the live model, even after an intervening edit.
  const [editingSeries, setEditingSeries] = useState<{ fi: number; ci: number } | null>(null)
  const [lookupEditorOpen, setLookupEditorOpen] = useState(false)
  const [editingLookup, setEditingLookup] = useState<{
    column: ColumnDef
    formColumns: string[]
    headerColumns: string[]
    onSave: (patch: LookupPatch) => void
    /** A column's own lookup (not a filter condition's) — offers the
     *  "match written values" rules too. */
    matchRules?: boolean
  } | null>(null)

  const [displayEditorOpen, setDisplayEditorOpen] = useState(false)
  const [editingDisplay, setEditingDisplay] = useState<{
    column: ColumnDef
    formColumns: string[]
    onSave: (display: DisplayFormat | undefined) => void
  } | null>(null)

  // Project-level variables — fetched once per project; the column-
  // property "Project Variable" select lists these. Prefer the explicit
  // ``project`` prop (threaded from the designer page) and fall back to the
  // project-store value, which can lag during loads.
  const storeProjectId = useProjectStore((s) => s.currentProject?.application.folder_name) || ''
  const projectId = project || storeProjectId
  const [projectVars, setProjectVars] = useState<ProjectVariable[]>([])
  useEffect(() => {
    if (!projectId) { setProjectVars([]); return }
    let cancelled = false
    listProjectVariables(projectId)
      .then((vs) => { if (!cancelled) setProjectVars(vs) })
      .catch(() => { if (!cancelled) setProjectVars([]) })
    return () => { cancelled = true }
  }, [projectId])

  const getForm = useCallback((fi?: number): SqlModel | null => {
    if (fi === undefined) return null
    return model.sql_models[fi]?.sql_model || null
  }, [model])

  const getColumn = useCallback((fi?: number, ci?: number): ColumnDef | null => {
    if (fi === undefined || ci === undefined) return null
    return model.sql_models[fi]?.sql_model?.columns[ci]?.column || null
  }, [model])

  // Update a column field
  const updateColumn = (fi: number, ci: number, field: keyof ColumnDef, value: unknown) => {
    const m = cloneModel(model)
    const col = m.sql_models[fi].sql_model.columns[ci].column
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(col as any)[field] = value
    onModelChange(m)
  }

  // Update a form field
  const updateForm = (fi: number, field: keyof SqlModel, value: unknown) => {
    const m = cloneModel(model)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(m.sql_models[fi].sql_model as any)[field] = value
    onModelChange(m)
  }

  // select_grid action → Line Entry source (kind business_logic) + a button on
  // its target line form. The function is reused as-is (the engine passes it
  // the header values flat, as select_grid did) and the action is removed.
  const convertSelectGrid = (from: { kind: 'object' } | { kind: 'form'; fi: number }, index: number) => {
    const m = cloneModel(model)
    const list = (from.kind === 'object' ? m.actions : m.sql_models[from.fi].sql_model.actions) || []
    const def = legacyActionToDef(list[index])
    if (!def.business_logic?.method) {
      toast.error('Only a select grid backed by a business-logic function can be converted.')
      return
    }
    const entries = allSqlModels(m)
    const target = entries.find(e => e.formNo === String(def.target_form_no || ''))
      || entries.find(e => e.formNo !== headerFormNo(m))
    if (!target) {
      toast.error('This transaction has no line form to receive the lines.')
      return
    }
    const sm = target.sm
    const le = { ...(sm.line_entry || {}) }
    const taken = new Set([...(le.sources || []).map(x => x.name), ...(le.commands || []).map(x => x.name)])
    let name = String(def.name || 'lines').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'lines'
    for (let n = 2; taken.has(name); n += 1) name = `${name.replace(/_\d+$/, '')}_${n}`
    le.sources = [...(le.sources || []), {
      name, label: def.name || name,
      from: { kind: 'business_logic', business_logic: { method: def.business_logic.method,
        ...(def.business_logic.language ? { language: def.business_logic.language } : {}) } },
      picker: { item_change: true, validate: 'limits' },
    }]
    le.commands = [...(le.commands || []), {
      name, label: def.name || name, source: name, documents: 'none', picker: 'review',
    }]
    sm.line_entry = le
    list.splice(index, 1)
    if (from.kind === 'object') m.actions = list
    else m.sql_models[from.fi].sql_model.actions = list
    onModelChange(m)
    toast.success(`"${def.name}" is now a line source on ${sm.form_title || `form ${target.formNo}`} — open its Line Entry to add documents, a link and limits.`)
  }

  // Merge a patch into the form's list_view config (creating it on first edit).
  const updateListView = (fi: number, patch: Partial<ListViewConfig>) => {
    const m = cloneModel(model)
    const sm = m.sql_models[fi].sql_model
    sm.list_view = { ...(sm.list_view || {}), ...patch }
    onModelChange(m)
  }

  // Replace the form's enforced filter SETS. Persists enforced_filter_sets and
  // clears the legacy single-list shapes once the designer has upgraded.
  const setFilterSets = (fi: number, sets: EnforcedFilterSet[]) => {
    const m = cloneModel(model)
    const sm = m.sql_models[fi].sql_model
    const lv: ListViewConfig = { ...(sm.list_view || {}) }
    // Drop sets that ended up with no condition so we never persist an empty
    // set the engine would skip anyway.
    const clean = sets
      .map(s => ({ ...s, conditions: s.conditions.filter(c => !!c.column) }))
      .filter(s => s.conditions.length > 0)
    if (clean.length) {
      // Exactly one default; fall back to the first set.
      if (!clean.some(s => s.is_default)) clean[0].is_default = true
      lv.enforced_filter_sets = clean
    } else {
      delete lv.enforced_filter_sets
    }
    delete lv.enforced_filters  // superseded by enforced_filter_sets
    delete lv.enforced_filter
    sm.list_view = lv
    onModelChange(m)
  }

  // Update edit mask
  const updateEditMask = (fi: number, ci: number, mask: EditMask) => {
    const m = cloneModel(model)
    m.sql_models[fi].sql_model.columns[ci].column.edit_mask = mask
    onModelChange(m)
  }

  // Open the structured validation-rule editor (field/form validations).
  const openValidationRuleEditor = (
    value: Validation, formColumns: string[], headerColumns: string[],
    onSave: (v: Validation) => void,
  ) => {
    setEditingRuleValidation({ value, formColumns, headerColumns, onSave })
    setRuleValidationOpen(true)
  }

  // Unified "Item Change" control — used by both a column's item_change and the
  // form-level row_add / row_edit events. Three modes: Rule (lookup +
  // expression fills → ItemChangeRuleEditor), Business Logic and Call API
  // (→ ItemChangeCodeEditor). ``triggerColumn`` is the column whose change
  // fires the rule, or '' for a form-level event (no trigger).
  const IC_MODE_LABEL: Record<'rule' | 'business_logic' | 'rest_api', string> = {
    rule: 'Rule', business_logic: 'Code', rest_api: 'Call API',
  }
  const renderIcControl = (
    raw: ItemChange | string | null | undefined,
    onChange: (ic: ItemChange) => void,
    formColumns: string[],
    headerColumns: string[],
    triggerColumn: string,
    allowedModes: Array<'rule' | 'business_logic' | 'rest_api'> = ['rule', 'business_logic', 'rest_api'],
    editorOpts?: { title?: string; helpText?: React.ReactNode },
  ) => {
    const ic = normalizeItemChange(raw)
    const mode: 'rule' | 'business_logic' | 'rest_api' =
      ic?.mode === 'rule' ? 'rule'
        : ic?.mode === 'rest_api' ? 'rest_api'
          : 'business_logic'
    const summary = itemChangeSummary(raw)
    const switchMode = async (target: 'rule' | 'business_logic' | 'rest_api') => {
      if (ic && target === mode) return
      if (summary && target !== mode) {
        const ok = await confirm({
          title: `Switch to ${IC_MODE_LABEL[target]}?`,
          message: `Your existing ${IC_MODE_LABEL[mode]} setup will be cleared.`,
          confirmLabel: `Switch to ${IC_MODE_LABEL[target]}`,
        })
        if (!ok) return
      }
      if (target === 'rule') {
        onChange({
          mode: 'rule', source_table: '',
          match_mode: triggerColumn ? 'simple' : 'advanced', fill: [],
        })
      } else if (target === 'business_logic') {
        onChange({ mode: 'business_logic', business_logic: { method: '', description: '' } })
      } else {
        onChange({ mode: 'rest_api', rest_api: { url: '', method: 'POST', on_error: 'allow', params: [] } })
      }
    }
    const openEdit = () => {
      if (mode === 'rule') {
        setEditingRule({
          rule: ic?.mode === 'rule' ? ic : null,
          triggerColumn, formColumns, headerColumns, onSave: onChange,
        })
        setRuleEditorOpen(true)
      } else {
        setEditingCode({
          value: ic && (ic.mode === 'business_logic' || ic.mode === 'rest_api') ? ic : null,
          initialMode: mode,
          formColumns, headerColumns, onSave: onChange,
          title: editorOpts?.title, helpText: editorOpts?.helpText,
        })
        setCodeEditorOpen(true)
      }
    }
    const codeMethod = ic?.mode === 'business_logic' ? (ic.business_logic?.method || '') : ''
    const segBtn = (active: boolean): React.CSSProperties => ({
      padding: '4px 8px', fontSize: '0.8125rem',
      border: '1px solid var(--color-border)',
      background: active ? 'var(--color-accent)' : 'var(--color-bg)',
      color: active ? 'white' : 'var(--color-text)', cursor: 'pointer',
    })
    const modes: Array<'rule' | 'business_logic' | 'rest_api'> = allowedModes
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <div style={{ display: 'inline-flex', borderRadius: 4, overflow: 'hidden' }}>
            {modes.map((m, i) => (
              <button
                key={m}
                type="button"
                style={{
                  ...segBtn(mode === m),
                  borderTopLeftRadius: i === 0 ? 4 : 0,
                  borderBottomLeftRadius: i === 0 ? 4 : 0,
                  borderTopRightRadius: i === modes.length - 1 ? 4 : 0,
                  borderBottomRightRadius: i === modes.length - 1 ? 4 : 0,
                  borderRight: i === modes.length - 1 ? undefined : 'none',
                }}
                onClick={() => void switchMode(m)}
              >
                {IC_MODE_LABEL[m]}
              </button>
            ))}
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={openEdit}
            title={`Edit ${IC_MODE_LABEL[mode]}`}
          >
            <Edit3 size={14} />
          </button>
          {mode === 'business_logic' && onOpenCode && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onOpenCode(codeMethod)}
              title="Open this function in the code editor"
            >
              <Code2 size={14} />
            </button>
          )}
          {summary && (
            <span style={{
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
            }}>{summary}</span>
          )}
        </div>
        {!summary && (
          <div style={{ ...fieldLabelStyle, marginTop: 4, fontStyle: 'italic' }}>
            Not configured. Click <Edit3 size={11} style={{ verticalAlign: 'text-bottom' }} /> to set up.
          </div>
        )}
      </>
    )
  }

  // Shared Business-Logic / Call-API control (no Rule mode, no trigger column).
  // Used by actions, cross-updates and follow-up actions so they offer the same
  // structured editor — function name, description, language, functional
  // specification + Open-in-code-editor — as item_change / validations.
  const renderBlApi = (
    raw: ItemChange | string | null | undefined,
    onChange: (ic: ItemChange) => void,
    formColumns: string[] = [],
    headerColumns: string[] = [],
  ) => renderIcControl(raw, onChange, formColumns, headerColumns, '', ['business_logic', 'rest_api'], {
    title: 'Code',
    helpText: (
      <>
        Choose how this runs: a <strong>Code</strong> call (a business-logic
        function you author in PL/pgSQL, Python or Java) or a <strong>Call API</strong>
        request to an external endpoint. Give it a function name, description,
        language and a functional specification — then open the code editor to
        write or generate the code.
      </>
    ),
  })

  // Form-level row_add / row_edit control (no trigger column).
  const renderRowEvent = (
    fi: number, form: SqlModel, key: 'row_add' | 'row_edit',
  ) => {
    const formColumns = form.columns.map(c => c.column?.name || '').filter(Boolean)
    const headerColumns = parentColumnNames(model, fi)
    return renderIcControl(
      form[key], (ic) => updateForm(fi, key, ic), formColumns, headerColumns, '',
    )
  }

  // Form-level row_complete: the same 3-mode control, plus the two
  // row_complete-only settings — which child form it rebuilds, and the
  // columns whose change makes the call worth making. Re-choosing the
  // definition keeps those settings.
  const renderRowComplete = (fi: number, form: SqlModel) => {
    const formColumns = form.columns.map(c => c.column?.name || '').filter(Boolean)
    const headerColumns = parentColumnNames(model, fi)
    const raw = form.row_complete
    const settings: RowCompleteSettings = (raw && typeof raw === 'object') ? raw : {}
    const formNo = allSqlModels(model)[fi]?.formNo ?? form.form_no
    const kids = childFormsOf(model, formNo)
    const write = (patch: Partial<RowCompleteEvent> & { ic?: ItemChange }) => {
      const base = patch.ic ?? normalizeItemChange(raw)
      if (!base) return
      const next: RowCompleteEvent = {
        ...base,
        ...(settings.rebuild ? { rebuild: settings.rebuild } : {}),
        ...(settings.when_changed?.length ? { when_changed: settings.when_changed } : {}),
      }
      if ('rebuild' in patch) {
        if (patch.rebuild) next.rebuild = patch.rebuild
        else delete next.rebuild
      }
      if ('when_changed' in patch) {
        if (patch.when_changed && patch.when_changed.length) next.when_changed = patch.when_changed
        else delete next.when_changed
      }
      updateForm(fi, 'row_complete', next)
    }
    const watched = new Set(settings.when_changed || [])
    const configured = !!normalizeItemChange(raw)
    return (
      <>
        {renderIcControl(raw, (ic) => write({ ic }), formColumns, headerColumns, '')}
        {configured && (
          <>
            <label style={fieldLabelStyle}>Rebuild form</label>
            <select
              style={inputStyle}
              value={settings.rebuild?.form_no || ''}
              disabled={kids.length === 0}
              onChange={e => write({
                rebuild: e.target.value ? { form_no: e.target.value, mode: 'replace' } : undefined,
              })}
            >
              <option value="">{kids.length ? '— none —' : '— this form has no child forms —'}</option>
              {kids.map(k => (
                <option key={k.formNo} value={k.formNo}>
                  Form {k.formNo} — {k.sm.form_title || 'Untitled'}
                </option>
              ))}
            </select>
            <div style={{ ...fieldLabelStyle, marginTop: 2, fontStyle: 'italic' }}>
              Rows the function returns for this form REPLACE the row&apos;s existing
              ones. It can still return <code>unchanged</code> (keep them) or
              <code> clear</code> (delete them, add none).
            </div>
            <label style={fieldLabelStyle}>Run only when these columns change (optional)</label>
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '2px 10px', maxHeight: 120, overflow: 'auto',
              border: '1px solid var(--color-border)', borderRadius: 4, padding: '4px 6px',
            }}>
              {formColumns.map(c => {
                // Stored as the row's FORM key (the key the client compares).
                const key = c.replace(/\s+/g, '_').toLowerCase()
                return (
                  <label key={c} style={{ fontSize: '0.8125rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <input
                      type="checkbox"
                      checked={watched.has(key)}
                      onChange={e => {
                        const next = new Set(Array.from(watched).filter(x => x !== key))
                        if (e.target.checked) next.add(key)
                        write({ when_changed: Array.from(next) })
                      }}
                    />
                    {c}
                  </label>
                )
              })}
            </div>
            <div style={{ ...fieldLabelStyle, marginTop: 2, fontStyle: 'italic' }}>
              None ticked: it runs every time a row is finished, and the function
              decides from <code>_old</code> whether anything changed.
            </div>
          </>
        )}
      </>
    )
  }

  // Transaction-level hook list (pre_commit / post_commit). Each entry is an
  // ItemChange restricted to Business Logic / Call API (no Rule — these aren't
  // column fills); reuses the same editors + plpgsql/python/java language picker
  // as a column item_change.
  // Renders a list of business-logic / Call-API hooks. `formIndex` selects
  // WHERE the list lives: undefined = the transaction level, a number = that
  // sql_model. Form-level cross_updates fire once per row of that form and
  // roll the save back on failure, so they are a different thing from the
  // transaction-level (legacy, non-blocking) list even though the editor UI is
  // identical — hence one renderer, two homes.
  const renderTxnHookList = (
    key: 'pre_commit' | 'post_commit' | 'cross_updates', title: string, help: string,
    noun = 'Hook', formIndex?: number, withEditors = true,
  ) => {
    const formSm = formIndex === undefined
      ? undefined
      : model.sql_models?.[formIndex]?.sql_model
    const bag = (formIndex === undefined ? model : formSm || {}) as unknown as
      Record<string, (ItemChange | string)[] | undefined>
    const items: (ItemChange | string)[] = bag[key] || []
    // A form-level hook's payload is that form's own row (plus `header`), so
    // offer its columns; a transaction-level one only ever sees the header.
    const headerCols = (
      (formSm || model.sql_models?.[0]?.sql_model)?.columns || []
    ).map(c => c.column?.name || '').filter(Boolean)
    const setItems = (next: (ItemChange | string)[]) => {
      const m = cloneModel(model)
      if (formIndex === undefined) {
        ;(m as unknown as Record<string, (ItemChange | string)[]>)[key] = next
      } else {
        const sm = m.sql_models?.[formIndex]?.sql_model
        if (!sm) return
        ;(sm as unknown as Record<string, (ItemChange | string)[]>)[key] = next
      }
      onModelChange(m)
    }
    return (
      <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
        <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 6, color: 'var(--color-text)' }}>{title}</div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>{help}</p>
        <div style={sectionStyle}>
          <div style={{ ...sectionTitleStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{noun}s ({items.length})</span>
            <button
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 4 }}
              onClick={() => setItems([...items, { mode: 'business_logic', business_logic: { method: '', description: '' } }])}
            >
              <Plus size={12} /> Add {noun}
            </button>
          </div>
          {items.length === 0 && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic', padding: '4px 0' }}>
              No {noun.toLowerCase()}s yet. Click "Add {noun}" to call a business-logic function or an external API.
            </div>
          )}
          {items.map((it, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                {renderBlApi(
                  it,
                  (ic) => setItems(items.map((x, j) => (j === i ? ic : x))),
                  headerCols, [],
                )}
              </div>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 4, flexShrink: 0 }}
                title="Remove hook"
                onClick={() => setItems(items.filter((_, j) => j !== i))}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        {withEditors && renderIcEditors()}
      </div>
    )
  }

  // The Item-Change Rule + Business-Logic/Call-API modal editors, driven by the
  // shared editingRule / editingCode state. renderIcControl / renderBlApi only
  // FLIP that state open — the modals must be MOUNTED in whichever branch is on
  // screen, so every branch that uses those controls (pre/post-commit,
  // cross-updates, actions, follow-ups, columns, forms) renders this fragment.
  const renderIcEditors = () => (
    <>
      <ItemChangeRuleEditor
        open={ruleEditorOpen}
        onClose={() => setRuleEditorOpen(false)}
        value={editingRule?.rule || null}
        triggerColumnName={editingRule?.triggerColumn || ''}
        formLevel={!editingRule?.triggerColumn}
        formColumnNames={editingRule?.formColumns || []}
        headerColumnNames={editingRule?.headerColumns || []}
        project={projectId}
        onSave={(rule) => editingRule?.onSave(rule)}
      />
      <ItemChangeCodeEditor
        open={codeEditorOpen}
        onClose={() => setCodeEditorOpen(false)}
        value={editingCode?.value || null}
        initialMode={editingCode?.initialMode || 'business_logic'}
        formColumnNames={editingCode?.formColumns || []}
        headerColumnNames={editingCode?.headerColumns || []}
        onSave={(ic) => editingCode?.onSave(ic)}
        onOpenCode={onOpenCode}
        title={editingCode?.title}
        helpText={editingCode?.helpText}
        project={projectId}
      />
    </>
  )

  // ── One enforced-filter condition's INPUT configuration ────────────────
  //
  // A filter value is user input like any other, so it gets the same two
  // affordances a form field has — a reference picker to choose from, and
  // validations that must pass before it is used. Both reuse the column
  // editors verbatim (LookupEditor / ValidationRuleEditor), which is why a
  // condition stores the same keys a column does.

  // The reference picker. A condition that configures none INHERITS the
  // column's, so the value is picked in the filter the same way it is picked
  // in the form; "Don't offer a picker here" writes an explicit blank.
  const renderFilterLookupRow = (
    fi: number,
    col: ColumnDef | undefined,
    cond: EnforcedCondition,
    patch: (p: Partial<EnforcedCondition>) => void,
  ) => {
    const ownSpec = (cond.lookup ?? '').trim()
    const ownSql = (cond.lookup_sql || '').trim()
    // A key present but undefined (just cleared this session) is NOT its own
    // configuration — it means "back to the column's", the same thing the key
    // being absent means after the model round-trips through JSON.
    const hasOwn = cond.lookup !== undefined || !!ownSql
    const inherited = (col?.lookup || '').trim() || (col?.lookup_sql || '').trim()
    const off = hasOwn && !ownSpec && !ownSql
    const summary = ownSpec || (ownSql ? 'Advanced (SQL)' : '')
    const formColumns = model.sql_models[fi].sql_model.columns
      .map(cc => cc.column?.name || '').filter(Boolean)
    const open = () => {
      // LookupEditor edits a COLUMN's lookup fields; a condition carries the
      // same keys, so it is handed a column-shaped view of the condition.
      setEditingLookup({
        column: {
          ...(col || ({} as ColumnDef)),
          lookup: cond.lookup ?? (col?.lookup || ''),
          lookup_sql: cond.lookup_sql ?? col?.lookup_sql,
          lookup_filter: cond.lookup_filter ?? col?.lookup_filter,
          lookup_filter_rules: cond.lookup_filter_rules ?? col?.lookup_filter_rules,
          lookup_title: cond.lookup_title ?? col?.lookup_title,
          lookup_sql_data_col: cond.lookup_sql_data_col ?? col?.lookup_sql_data_col,
        } as ColumnDef,
        formColumns,
        headerColumns: parentColumnNames(model, fi),
        onSave: (lp) => patch({
          lookup: lp.lookup || '',
          lookup_sql: (lp.lookup_sql || '').trim() || undefined,
          lookup_filter: (lp.lookup_filter || '').trim() || undefined,
          lookup_filter_rules: lp.lookup_filter_rules || undefined,
          lookup_title: (lp.lookup_title || '').trim() || undefined,
          lookup_sql_data_col: (lp.lookup_sql_data_col || '').trim() || undefined,
        }),
      })
      setLookupEditorOpen(true)
    }
    return (
      <div style={{ marginTop: 8 }}>
        <label style={{ ...fieldLabelStyle, marginTop: 0 }}>Pick the value from</label>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <button
            type="button"
            onClick={open}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 8px', textAlign: 'left', background: 'var(--color-bg)',
              border: `1px ${summary ? 'solid' : 'dashed'} var(--color-border)`,
              borderRadius: 6, cursor: 'pointer',
              color: summary ? 'var(--color-text)' : 'var(--color-accent)',
              fontSize: '0.8425rem',
            }}
          >
            <Search size={13} style={{ flexShrink: 0 }} />
            <span style={{
              flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {summary || (off ? 'No picker — type the value'
                : inherited ? `From the column: ${inherited}`
                  : 'Set up a reference picker…')}
            </span>
          </button>
          {(summary || (inherited && !off)) && (
            <button className="btn btn-ghost btn-sm"
              title={summary ? 'Remove this picker' : "Don't offer the column's picker here"}
              onClick={() => patch({
                lookup: '', lookup_sql: undefined, lookup_filter: undefined,
                lookup_filter_rules: undefined, lookup_title: undefined,
                lookup_sql_data_col: undefined,
              })}>
              <Trash2 size={13} />
            </button>
          )}
          {off && (
            <button className="btn btn-ghost btn-sm" title="Use the column's picker again"
              onClick={() => patch({ lookup: undefined })}>
              <Plus size={13} />
            </button>
          )}
        </div>
      </div>
    )
  }

  // Checks run against the RESOLVED filter value before the list query. Same
  // rule shape and same editor as a column's validations; an 'E' refuses the
  // list with its message, a 'W'/'P' shows on the filter bar and the list
  // still runs.
  const renderFilterValidations = (
    fi: number,
    cond: EnforcedCondition,
    onChange: (rules: ValidationRule[]) => void,
  ) => {
    const rules = (cond.validations || []).filter(isValidationRule)
    const formColumns = model.sql_models[fi].sql_model.columns
      .map(cc => cc.column?.name || '').filter(Boolean)
    const openEditor = (value: Validation, save: (v: Validation) => void) =>
      openValidationRuleEditor(value, formColumns, parentColumnNames(model, fi), save)
    return (
      <div style={{ marginTop: 8 }}>
        <label style={{ ...fieldLabelStyle, marginTop: 0 }}>Check the value</label>
        {rules.map((v, vi) => (
          <div key={vi} style={listItemStyle}>
            <span style={{
              flex: 1, fontSize: '0.8425rem', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              opacity: v.disabled ? 0.55 : 1,
            }}>{validationSummary(v)}</span>
            <button title={v.disabled ? 'Enable check' : 'Disable check'}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                color: v.disabled ? 'var(--color-text-secondary)' : 'var(--color-accent)',
              }}
              onClick={() => onChange(rules.map((r, i) =>
                i === vi ? { ...r, disabled: !r.disabled } : r))}>
              <Power size={13} />
            </button>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', padding: 2 }}
              onClick={() => openEditor(v, (nv) => {
                if (!isValidationRule(nv)) return
                onChange(rules.map((r, i) => (i === vi ? nv : r)))
              })}>
              <Edit3 size={13} />
            </button>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 2 }}
              onClick={() => onChange(rules.filter((_, i) => i !== vi))}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        <button style={addBtnStyle} onClick={() => openEditor('', (nv) => {
          if (!isValidationRule(nv)) return
          onChange([...rules, nv])
        })}>
          <Plus size={12} /> Add check
        </button>
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
          Reads this condition&apos;s value as <code>{`{form.${(colOfAny(cond.column)?.name) || cond.column.toUpperCase()}}`}</code>
          {' '}and the other conditions of the set the same way. A range&apos;s upper
          bound is <code>{`{form.${((colOfAny(cond.column)?.name) || cond.column.toUpperCase())}_TO}`}</code>.
          An <strong>Error</strong> refuses the list; a <strong>Warning</strong> or
          <strong> Prompt</strong> shows on the filter bar and the list still runs.
        </div>
      </div>
    )
  }

  // A column by db_name anywhere in the transaction — the filter helpers above
  // run outside the form branch that has its own `colOf`.
  function colOfAny(dbn: string): ColumnDef | undefined {
    for (const w of model.sql_models) {
      for (const cw of w.sql_model.columns || []) {
        const c = cw.column
        if (c && (c.db_name || c.name) === dbn) return c
      }
    }
    return undefined
  }

  // All forms in the transaction — offered to "child form must have an entry".
  const formList = useMemo(
    () => model.sql_models.map((s) => ({
      form_no: s.sql_model.form_no || '',
      title: s.sql_model.form_title || '',
    })),
    [model],
  )

  // A form's table joins (Form inspector › Data source).
  const renderJoinsEditor = (fi: number) => {
    const form = model.sql_models[fi]?.sql_model
    if (!form) return null
    const joins = form.joins?.join_predicates?.joins || []
    return (
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Join Configuration</div>
          {joins.map((j, ji) => (
            <div key={ji} style={{ padding: 8, background: 'var(--color-bg)', borderRadius: 4, marginBottom: 8, border: '1px solid var(--color-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <GripVertical size={14} style={{ color: 'var(--color-text-secondary)' }} />
                <span style={{ fontWeight: 600, fontSize: '0.8625rem', flex: 1 }}>{j.table || `Table ${ji + 1}`}</span>
                {!j.main_table && (
                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 2 }}
                    onClick={() => {
                      const m = cloneModel(model)
                      m.sql_models[fi].sql_model.joins.join_predicates.joins.splice(ji, 1)
                      onModelChange(m)
                    }}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>

              <label style={fieldLabelStyle}>Table Name</label>
              <input style={inputStyle} value={j.table}
                onChange={e => {
                  const m = cloneModel(model)
                  m.sql_models[fi].sql_model.joins.join_predicates.joins[ji].table = e.target.value
                  onModelChange(m)
                }} />

              <label style={fieldLabelStyle}>Description</label>
              <input style={inputStyle} value={j.table_descr || ''}
                onChange={e => {
                  const m = cloneModel(model)
                  m.sql_models[fi].sql_model.joins.join_predicates.joins[ji].table_descr = e.target.value
                  onModelChange(m)
                }} />

              <div style={checkboxRowStyle}>
                <input type="radio" name={`main-table-${fi}`} checked={j.main_table}
                  onChange={() => {
                    const m = cloneModel(model)
                    m.sql_models[fi].sql_model.joins.join_predicates.joins.forEach((jj: JoinDef, jji: number) => {
                      jj.main_table = jji === ji
                    })
                    onModelChange(m)
                  }} />
                <span>Main Table</span>
              </div>

              {!j.main_table && (
                <JoinFields
                  join={j}
                  onUpdate={(updatedJoin) => {
                    const m = cloneModel(model)
                    m.sql_models[fi].sql_model.joins.join_predicates.joins[ji] = updatedJoin
                    onModelChange(m)
                  }}
                />
              )}
            </div>
          ))}

          <button style={addBtnStyle} onClick={() => {
            const m = cloneModel(model)
            if (!m.sql_models[fi].sql_model.joins) {
              m.sql_models[fi].sql_model.joins = { join_predicates: { joins: [] } }
            }
            m.sql_models[fi].sql_model.joins.join_predicates.joins.push({
              table: '',
              main_table: false,
              table_descr: '',
              join_table: form.main_table,
              join_type: 'inner',
              column: '',
              join_column: '',
            })
            onModelChange(m)
          }}>
            <Plus size={12} /> Add Join Table
          </button>
        </div>
    )
  }

  if (!selectedNode) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)', fontSize: '0.9625rem', textAlign: 'center', marginTop: 40 }}>
        Click a field, a button or a form to edit it here
      </div>
    )
  }

  // ---- COLUMN PROPERTIES ----
  // Five tabs — General · Data · Display · Behaviour · Rules — holding every
  // control the single long column panel had. The tab rides on the selection
  // (TreeNode.tab) so search and the "Set up on this column" rows can open
  // the right one.
  if (selectedNode.type === 'column') {
    const col = getColumn(selectedNode.formIndex, selectedNode.columnIndex)
    if (!col) return null
    const fi = selectedNode.formIndex!
    const ci = selectedNode.columnIndex!
    const tab: ColumnTab = (COLUMN_TABS.some(t => t.key === selectedNode.tab)
      ? selectedNode.tab : 'general') as ColumnTab
    const setTab = (t: ColumnTab) => onSelectNode?.({ ...selectedNode, tab: t })
    const formCols = model.sql_models[fi].sql_model.columns
    // The database column FOLLOWS the column name until the author says
    // otherwise — "Set it separately" — or the two already differ.
    const linkKey = `${fi}:${ci}`
    const linked = (!col.db_name || col.db_name === col.name) && !separateDb.has(linkKey)
    const groupNames = Array.from(new Set(formCols.map(c => (c.column?.group_name || '').trim()).filter(Boolean)))
    const curGroup = (col.group_name || '').trim()
    if (curGroup && !groupNames.includes(curGroup)) groupNames.push(curGroup)
    const rulesCount = (Array.isArray(col.validations) ? col.validations : []).length
    const icSummary = itemChangeSummary(col.item_change)
    const lkSummary = lookupSummary(col)
    const dfSummary = displayFormatSummary(col.display)
    const setupRows: Array<{ title: string; detail: string; on: boolean; tab: ColumnTab }> = [
      { title: 'Lookup', detail: lkSummary || 'None', on: !!lkSummary, tab: 'behaviour' },
      { title: 'When it changes', detail: icSummary ? icSummary.replace(/^Business Logic/, 'Code') : 'None', on: !!icSummary, tab: 'behaviour' },
      { title: 'Validations', detail: rulesCount ? `${rulesCount} on` : 'None', on: rulesCount > 0, tab: 'rules' },
      { title: 'Default value', detail: (col.default_value || '').trim() || 'None', on: !!(col.default_value || '').trim(), tab: 'behaviour' },
      { title: 'In lists', detail: dfSummary || 'Plain text', on: !!dfSummary, tab: 'display' },
      ...(col.secure && col.secure.enabled !== false
        ? [{ title: 'Sensitive', detail: col.secure.mode === 'hash' ? 'One-way hash' : 'Encrypted at rest', on: true, tab: 'data' as ColumnTab }]
        : []),
    ]
    const stateBox = (on: boolean): React.CSSProperties => ({
      display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9375rem', cursor: 'pointer',
      border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`,
      background: on ? 'var(--color-accent-weak)' : 'transparent', borderRadius: 6, padding: '7px 10px',
    })

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div role="tablist" aria-label="Column settings" style={tabBarStyle}>
          {COLUMN_TABS.map(t => (
            <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} style={tabStyle(tab === t.key)}>
              {t.label}
              {t.key === 'rules' && rulesCount > 0 && <span style={tabCountStyle}>{rulesCount}</span>}
            </button>
          ))}
        </div>
        <div style={{ padding: '12px 14px 20px', overflow: 'auto', flex: 1 }}>

        {tab === 'general' && (
          <>
            <label style={{ ...fieldLabelStyle, marginTop: 0 }}>Column name</label>
            <input style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} value={col.name} onChange={e => {
              const val = e.target.value
              const m = cloneModel(model)
              const c = m.sql_models[fi].sql_model.columns[ci].column
              // Preserve any user-supplied heading. Only seed a heading
              // from the new name when it's still empty — so editing the
              // name later doesn't overwrite the designer's chosen casing.
              const prev = (c.heading || '').trim()
              c.name = val
              if (linked) c.db_name = val
              if (!prev) {
                c.heading = val.replace(/_/g, ' ')
              }
              onModelChange(m)
            }} />
            <div style={hintBoxStyle}>
              <Link2 size={14} style={{ flexShrink: 0, marginTop: 2, color: 'var(--color-accent)' }} />
              {linked ? (
                <span>Database column follows the name.{' '}
                  <button type="button" style={linkBtnStyle} onClick={() => {
                    setSeparateDb(prev => new Set(prev).add(linkKey))
                    setTab('data')
                  }}>Set it separately</button>
                </span>
              ) : (
                <span>Database column <code>{col.db_name}</code> is set separately (Data tab).{' '}
                  <button type="button" style={linkBtnStyle} onClick={() => {
                    setSeparateDb(prev => { const n = new Set(prev); n.delete(linkKey); return n })
                    updateColumn(fi, ci, 'db_name', col.name)
                  }}>Follow the name again</button>
                </span>
              )}
            </div>

            <label style={fieldLabelStyle}>Heading</label>
            <input style={inputStyle} value={col.heading} onChange={e => updateColumn(fi, ci, 'heading', e.target.value)} />

            <label style={fieldLabelStyle}>Description</label>
            <textarea style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }} value={col.descr}
              onChange={e => updateColumn(fi, ci, 'descr', e.target.value)} />

            <label style={fieldLabelStyle}>Group</label>
            <select
              style={inputStyle}
              value={groupNames.includes(curGroup) ? curGroup : ''}
              onChange={e => {
                const v = e.target.value
                if (v === '__new__') { setNewGroupDraft(''); return }
                setNewGroupDraft(null)
                updateColumn(fi, ci, 'group_name', v)
              }}
            >
              <option value="">Others (no group)</option>
              {groupNames.map(gname => <option key={gname} value={gname}>{gname}</option>)}
              <option value="__new__">New group…</option>
            </select>
            {newGroupDraft !== null && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input autoFocus style={{ ...inputStyle, flex: 1 }} placeholder="New group name" value={newGroupDraft}
                  onChange={e => setNewGroupDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newGroupDraft.trim()) { updateColumn(fi, ci, 'group_name', newGroupDraft.trim()); setNewGroupDraft(null) }
                    if (e.key === 'Escape') setNewGroupDraft(null)
                  }} />
                <button className="btn btn-secondary btn-sm" disabled={!newGroupDraft.trim()}
                  onClick={() => { updateColumn(fi, ci, 'group_name', newGroupDraft.trim()); setNewGroupDraft(null) }}>Move</button>
              </div>
            )}

            <div style={{ ...fieldLabelStyle, marginTop: 14, marginBottom: 6 }}>Field state</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6 }}>
              <label style={stateBox(toBool(col.mandatory))}>
                <input type="checkbox" checked={toBool(col.mandatory)} onChange={e => updateColumn(fi, ci, 'mandatory', e.target.checked)} /> Mandatory
              </label>
              <label style={stateBox(toBool(col.protect))} title="Read-only on screen">
                <input type="checkbox" checked={toBool(col.protect)} onChange={e => updateColumn(fi, ci, 'protect', e.target.checked)} /> Protected
              </label>
              <label style={stateBox(toBool(col.hidden))}>
                <input type="checkbox" checked={toBool(col.hidden)} onChange={e => updateColumn(fi, ci, 'hidden', e.target.checked)} /> Hidden
              </label>
              <label style={stateBox(toBool(col.key))}>
                <input type="checkbox" checked={toBool(col.key)} onChange={e => updateColumn(fi, ci, 'key', e.target.checked)} /> Primary key
              </label>
            </div>

            <div style={{ ...fieldLabelStyle, marginTop: 14, marginBottom: 6 }}>Set up on this column</div>
            <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
              {setupRows.map((r, i) => (
                <button key={r.title} type="button" onClick={() => setTab(r.tab)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                  padding: '9px 12px', background: 'transparent', border: 'none', cursor: 'pointer',
                  borderTop: i ? '1px solid var(--color-border)' : 'none', color: 'var(--color-text)',
                }}>
                  <span aria-hidden style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
                    background: r.on ? 'var(--color-accent)' : 'transparent',
                    border: r.on ? 'none' : '1.5px solid var(--color-text-faint)',
                  }} />
                  <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '0.9375rem', fontWeight: 500 }}>{r.title}</span>
                    <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.detail}</span>
                  </span>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--color-accent)' }}>{COLUMN_TABS.find(t => t.key === r.tab)?.label}</span>
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', borderTop: '1px solid var(--color-border)', marginTop: 16, paddingTop: 12 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => {
                const m = cloneModel(model)
                const ni = duplicateColumn(m.sql_models[fi].sql_model, ci)
                onModelChange(m)
                const nc = m.sql_models[fi].sql_model.columns[ni]?.column
                onSelectNode?.({ type: 'column', formIndex: fi, columnIndex: ni, label: nc?.name || '' })
              }}>
                <Copy size={13} /> Duplicate
              </button>
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-error)' }} onClick={() => void deleteColumn(fi, ci)}>
                <Trash2 size={13} /> Delete column…
              </button>
            </div>
          </>
        )}

        {tab === 'data' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0 10px' }}>
              <div>
          <label style={{ ...fieldLabelStyle, marginTop: 0 }}>Type</label>
          <select style={{ ...inputStyle, cursor: 'pointer' }} value={col.col_type}
            onChange={e => updateColumn(fi, ci, 'col_type', e.target.value)}>
            {COL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            {/* Legacy/native values not in the canonical list stay selectable */}
            {col.col_type && !(COL_TYPES as readonly string[]).includes((col.col_type || '').toUpperCase()) && (
              <option value={col.col_type}>{col.col_type}</option>
            )}
          </select>
              </div>
              <div>
          <label style={{ ...fieldLabelStyle, marginTop: 0 }}>Size</label>
          <input style={inputStyle} value={col.db_size} onChange={e => updateColumn(fi, ci, 'db_size', e.target.value)} />
              </div>
            </div>
          {/* Optional minimum for typed text. Blank = no minimum (the column
              behaves exactly as before). Enforced on save; the form shows a
              live counter inside the field. Text columns only. */}
          {inputType(col) === 'text' && (() => {
            const min = minTextLength(col)
            const max = maxTextLength(col)
            const tooBig = min != null && max != null && min > max
            return (
              <>
                <label style={fieldLabelStyle}>Min length</label>
                <input
                  type="number" min={1} step={1} style={inputStyle}
                  placeholder="No minimum"
                  value={col.min_length == null ? '' : String(col.min_length)}
                  onChange={e => {
                    const raw = e.target.value.trim()
                    const m = cloneModel(model)
                    const c = m.sql_models[fi].sql_model.columns[ci].column
                    const n = Math.trunc(Number(raw))
                    if (!raw || !Number.isFinite(n) || n <= 0) delete c.min_length
                    else c.min_length = n
                    onModelChange(m)
                  }}
                />
                <div style={{ fontSize: '0.8125rem', marginTop: 4, lineHeight: 1.45, color: tooBig ? '#d97706' : 'var(--color-text-secondary)' }}>
                  {tooBig
                    ? `Larger than the column size (${max}) — no value could ever be saved.`
                    : 'A typed value must have at least this many characters (rich text counts its visible text). Blank values are left to Mandatory.'}
                </div>
              </>
            )
          })()}
          <label style={fieldLabelStyle}>Table</label>
          <input style={inputStyle} value={col.table_name} onChange={e => updateColumn(fi, ci, 'table_name', e.target.value)} />

            <label style={fieldLabelStyle}>Database column</label>
            <input
              style={{ ...inputStyle, fontFamily: 'var(--font-mono)', opacity: linked ? 0.7 : 1 }}
              value={col.db_name}
              disabled={linked}
              title={linked ? 'Follows the column name — use "Set it separately" to change it' : undefined}
              onChange={e => updateColumn(fi, ci, 'db_name', e.target.value)}
            />
            {linked && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                Follows the column name.{' '}
                <button type="button" style={linkBtnStyle} onClick={() => setSeparateDb(prev => new Set(prev).add(linkKey))}>
                  Set it separately
                </button>
              </div>
            )}

            {(col.sql_expression || showComputed.has(linkKey)) ? (
              <>
          <label style={fieldLabelStyle}>Computed value (SQL expression)</label>
          <textarea
            style={{ ...inputStyle, minHeight: 50, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.8425rem' }}
            value={col.sql_expression || ''}
            onChange={e => updateColumn(fi, ci, 'sql_expression', e.target.value || undefined)}
            placeholder={'e.g. fn_descr({' + (model.sql_models[fi].sql_model.columns[0]?.column?.table_name || 'TABLE') + '}.reas_code)'}
            title="Worked out by the database each time the record is read. Name a table of this form as {TABLE}. The field is read-only, never saved, and needs no table column."
          />
              </>
            ) : (
              <button style={{ ...addBtnStyle, marginTop: 12, padding: '8px 10px' }} onClick={() => setShowComputed(prev => new Set(prev).add(linkKey))}>
                <Plus size={12} /> Computed value (SQL expression)
              </button>
            )}

          {/* Sensitive data — storage-level encryption, not a display option.
              Kept next to the other column flags because that is where an
              author decides what a field IS, and encrypting is a property of
              the field, not of one screen. */}
          {(() => {
            const secure = col.secure && col.secure.enabled !== false ? col.secure : null
            const setSecure = (patch: Partial<NonNullable<typeof col.secure>> | null) => {
              const m = cloneModel(model)
              const c = m.sql_models[fi].sql_model.columns[ci].column
              if (patch === null) delete c.secure
              else c.secure = { mode: 'encrypt', search: 'none', mask: 'partial', ...(c.secure || {}), ...patch }
              onModelChange(m)
            }
            // The designer picks how the role list reads: an allowed list
            // (only these roles see it) or a denied list (everyone with a
            // role except these). Each mode keeps its own list, so switching
            // back and forth does not lose what was typed.
            const denyMode = secure?.reveal === 'deny'
            const roles = ((denyMode ? secure?.hide_roles : secure?.reveal_roles) || []).join(', ')
            return (
              <div style={{ marginTop: 12, padding: 10, borderRadius: 6, border: '1px solid var(--color-border)' }}>
                <label style={checkboxRowStyle}>
                  <input
                    type="checkbox"
                    checked={!!secure}
                    onChange={e => setSecure(e.target.checked ? {} : null)}
                  />
                  <span>Sensitive — encrypt at rest</span>
                </label>
                <div style={{ fontSize: '0.8125rem', opacity: 0.7, marginTop: 4, lineHeight: 1.45 }}>
                  The value is encrypted before it reaches the database, so a
                  dump, a replica or a reporting connection sees nothing readable.
                </div>

                {secure && (
                  <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                    <div>
                      <label style={fieldLabelStyle}>Mode</label>
                      <select
                        style={inputStyle}
                        value={secure.mode || 'encrypt'}
                        onChange={e => setSecure({ mode: e.target.value as 'encrypt' | 'hash' })}
                      >
                        <option value="encrypt">Encrypt — can be read back by permitted roles</option>
                        <option value="hash">One-way hash — can only be verified, never read</option>
                      </select>
                    </div>

                    {secure.mode !== 'hash' && (
                      <>
                        <label style={checkboxRowStyle}>
                          <input
                            type="checkbox"
                            checked={secure.search === 'exact'}
                            onChange={e => setSecure({ search: e.target.checked ? 'exact' : 'none' })}
                          />
                          <span>Searchable by exact value</span>
                        </label>
                        <div style={{ fontSize: '0.8125rem', opacity: 0.7, marginTop: -4, lineHeight: 1.45 }}>
                          Required if this column is filtered, looked up, or must
                          be unique. Adds a search index beside the encrypted
                          value; partial ("contains") search and sorting are never
                          possible on an encrypted column.
                        </div>

                        <div>
                          <label style={fieldLabelStyle}>Who may see the real value</label>
                          <select
                            style={inputStyle}
                            value={denyMode ? 'deny' : 'allow'}
                            onChange={e => setSecure({ reveal: e.target.value as 'allow' | 'deny' })}
                          >
                            <option value="allow">Only the roles listed (allowed list)</option>
                            <option value="deny">Everyone except the roles listed (denied list)</option>
                          </select>
                        </div>

                        <div>
                          <label style={fieldLabelStyle}>
                            {denyMode ? 'Roles that must NOT see the real value' : 'Roles that may see the real value'}
                          </label>
                          <input
                            style={inputStyle}
                            value={roles}
                            placeholder={denyMode
                              ? 'e.g. INTERN, VENDOR'
                              : 'e.g. HR-ADMIN, FINANCE  (empty = nobody)'}
                            onChange={e => {
                              const list = e.target.value
                                .split(',').map(r => r.trim()).filter(Boolean)
                              setSecure(denyMode ? { hide_roles: list } : { reveal_roles: list })
                            }}
                          />
                          <div style={{ fontSize: '0.8125rem', opacity: 0.7, marginTop: 4, lineHeight: 1.45 }}>
                            {denyMode
                              ? (roles
                                ? 'Every signed-in user sees the value unless they hold one of these roles. Users with no role (e.g. anonymous site visitors) always see the mask.'
                                : 'No roles listed — every signed-in user with a role sees the value. Users with no role (e.g. anonymous site visitors) always see the mask.')
                              : (roles
                                ? 'Only users holding one of these roles see the value.'
                                : 'No roles listed — the value can be entered but never read back by anyone.')}
                          </div>
                        </div>

                        <div>
                          <label style={fieldLabelStyle}>Everyone else sees</label>
                          <select
                            style={inputStyle}
                            value={secure.mask || 'partial'}
                            onChange={e => setSecure({ mask: e.target.value as 'partial' | 'replace' | 'null' | 'hash' })}
                          >
                            <option value="partial">Last 4 characters (•••••6789)</option>
                            <option value="replace">A fixed label</option>
                            <option value="null">Nothing (blank)</option>
                            <option value="hash">A short non-reversible code</option>
                          </select>
                        </div>

                        {secure.mask === 'replace' && (
                          <div>
                            <label style={fieldLabelStyle}>Label shown instead</label>
                            <input
                              style={inputStyle}
                              value={secure.replacement || ''}
                              placeholder="CONFIDENTIAL"
                              onChange={e => setSecure({ replacement: e.target.value })}
                            />
                          </div>
                        )}

                        <div>
                          <label style={fieldLabelStyle}>On an offline device</label>
                          <select
                            style={inputStyle}
                            value={secure.offline || 'deny'}
                            onChange={e => setSecure({ offline: e.target.value as 'deny' | 'masked' })}
                          >
                            <option value="deny">Do not send this column to the device</option>
                            <option value="masked">Send the masked value only</option>
                          </select>
                        </div>
                      </>
                    )}

                    <div style={{
                      fontSize: '0.8125rem', lineHeight: 1.5, padding: '8px 10px',
                      borderRadius: 5, background: 'var(--color-bg)',
                      border: '1px solid var(--color-border)',
                    }}>
                      <strong>Before you deploy:</strong> existing rows keep their
                      current plaintext until they are next saved. The encryption
                      key lives with the platform, not in the application database —
                      if it is lost, these values cannot be recovered.
                    </div>
                  </div>
                )}
              </div>
            )
          })()}
          </>
        )}

        {tab === 'display' && (
          <>
            <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 2 }}>On the form — input</div>
          {/* The widget picker + its value panel, shared with the document
              repository's metadata fields — one place a mask can be added, so
              the two screens cannot offer different sets. */}
          <EditMaskEditor
            value={col.edit_mask}
            onChange={(mask) => updateEditMask(fi, ci, mask)}
            labelStyle={fieldLabelStyle}
            inputStyle={inputStyle}
            addBtnStyle={addBtnStyle}
            extra={(mask) => (
              /* An image/document column may be backed by a document
                 REPOSITORY (obj_type K) instead of the flat document store:
                 the file then gets rights, versions, retention and an audit
                 trail, while the column still stores a URL and the widget is
                 unchanged. Left blank — the ordinary case — nothing changes.
                 A transaction concern, so it lives here rather than inside the
                 shared widget editor. */
              (mask === 'image' || mask === 'document') ? (
                <div style={{ marginTop: 8, padding: 8, background: 'var(--color-bg-secondary)', borderRadius: 4 }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                    Store in a document repository (optional)
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                    <input style={{ ...inputStyle, flex: 1 }}
                      placeholder="Repository object, e.g. CONTRACTS"
                      value={col.repository?.obj || ''}
                      onChange={e => {
                        const obj = e.target.value.trim()
                        updateColumn(fi, ci, 'repository',
                          obj ? { ...(col.repository || {}), obj } : undefined)
                      }} />
                    <input style={{ ...inputStyle, flex: 1 }}
                      placeholder="Document type"
                      value={col.repository?.doctype || ''}
                      disabled={!col.repository?.obj}
                      onChange={e => updateColumn(fi, ci, 'repository',
                        { ...(col.repository || { obj: '' }), doctype: e.target.value.trim() })} />
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                    Leave blank to keep the ordinary file store. With a repository set,
                    uploads become controlled documents — a replacement becomes the next
                    version, and opening the file is governed by the repository&rsquo;s
                    rights, not this transaction&rsquo;s. The document type must already
                    exist in that repository.
                  </div>
                </div>
              ) : null
            )}
          />
            {lkSummary && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 8 }}>
                A lookup is set on this column, so the search box comes from{' '}
                <button type="button" style={linkBtnStyle} onClick={() => setTab('behaviour')}>Behaviour › Lookup</button>.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0 10px' }}>
              <div>
          <label style={fieldLabelStyle}>Text case</label>
          <select
            style={inputStyle}
            value={col.text_case || ''}
            onChange={e => updateColumn(fi, ci, 'text_case', e.target.value || undefined)}
            title="Typed text is converted as it is entered, and the engine stores it that way whoever sends it"
          >
            <option value="">As typed</option>
            <option value="upper">UPPER CASE (codes)</option>
            <option value="lower">lower case</option>
          </select>
              </div>
              <div>
                <label style={fieldLabelStyle}>Input pattern</label>
                <input style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} value={col.format} onChange={e => updateColumn(fi, ci, 'format', e.target.value)} placeholder="e.g. #,##0.00" title="Value mask applied as the user types" />
              </div>
            </div>
            <label style={checkboxRowStyle} title="Show no caption on the form (the list still uses the heading)">
              <input type="checkbox" checked={toBool(col.hide_label)} onChange={e => updateColumn(fi, ci, 'hide_label', e.target.checked || undefined)} />
              <span>Hide the caption</span>
            </label>
          <label style={fieldLabelStyle}>Show beside another field (same line)</label>
          <select
            style={inputStyle}
            value={col.inline_with || ''}
            onChange={e => updateColumn(fi, ci, 'inline_with', e.target.value || undefined)}
            title="Draw this field on the same line as another, inside its cell, with no caption — e.g. a description beside its code"
          >
            <option value="">— Own place on the form —</option>
            {model.sql_models[fi].sql_model.columns
              .map(w => w.column?.name || '')
              .filter(n => n && n !== col.name)
              .map(n => <option key={n} value={n}>{n}</option>)}
          </select>

            <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 16, paddingTop: 12 }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>In lists and grids</div>
          <label style={fieldLabelStyle}>Display format</label>
          {(() => {
            const summary = displayFormatSummary(col.display)
            const formColumns = model.sql_models[fi].sql_model.columns
              .map(c => c.column?.name || '')
              .filter(n => n && n !== col.name)
            const openDisplayEdit = () => {
              setEditingDisplay({
                column: col,
                formColumns,
                onSave: (display) => {
                  const m = cloneModel(model)
                  const c = m.sql_models[fi].sql_model.columns[ci].column
                  if (display) c.display = display
                  else delete c.display
                  onModelChange(m)
                },
              })
              setDisplayEditorOpen(true)
            }
            return summary ? (
              <button
                type="button"
                onClick={openDisplayEdit}
                title="Edit display format"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '7px 10px', textAlign: 'left', background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  color: 'var(--color-text)', cursor: 'pointer',
                }}
              >
                <Eye size={14} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: '0.8825rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', flexShrink: 0 }}>
                  <Edit3 size={12} /> Edit
                </span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={openDisplayEdit}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 6, width: '100%', padding: '8px 10px', background: 'var(--color-bg)',
                    border: '1px dashed var(--color-border)', borderRadius: 6,
                    color: 'var(--color-accent)', cursor: 'pointer', fontSize: '0.8425rem', fontWeight: 600,
                  }}
                >
                  <Eye size={14} /> Set display format…
                </button>
                <div style={{ ...fieldLabelStyle, marginTop: 4, fontStyle: 'italic' }}>
                  Show this column as a tag, bar, ring, link, icon… in the list (display only).
                </div>
              </>
            )
          })()}
              <label style={fieldLabelStyle}>Heading in lists</label>
              <input style={inputStyle} value={col.list_heading || ''} placeholder={col.heading || col.name}
                onChange={e => updateColumn(fi, ci, 'list_heading', e.target.value || undefined)} />
              <label style={fieldLabelStyle}>Shown in the list</label>
              <select style={inputStyle}
                value={col.list_hidden === undefined || col.list_hidden === '' ? '' : (toBool(col.list_hidden) ? '1' : '0')}
                onChange={e => updateColumn(fi, ci, 'list_hidden', e.target.value === '' ? undefined : e.target.value)}>
                <option value="">Same as the form (follows Hidden)</option>
                <option value="0">Always list it</option>
                <option value="1">Leave it out of the list</option>
              </select>
            </div>

            <details style={{ borderTop: '1px solid var(--color-border)', marginTop: 16, paddingTop: 10 }}>
              <summary style={{ fontSize: '0.9375rem', cursor: 'pointer' }}>
                Exact position <span style={{ color: 'var(--color-text-secondary)' }}>· set by dragging on the canvas</span>
              </summary>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <div>
              <label style={fieldLabelStyle}>X</label>
              <input type="number" style={inputStyle} value={col.x} onChange={e => updateColumn(fi, ci, 'x', e.target.value)} />
            </div>
            <div>
              <label style={fieldLabelStyle}>Y</label>
              <input type="number" style={inputStyle} value={col.y} onChange={e => updateColumn(fi, ci, 'y', e.target.value)} />
            </div>
            <div>
              <label style={fieldLabelStyle}>Width</label>
              <input type="number" style={inputStyle} value={col.width} onChange={e => updateColumn(fi, ci, 'width', Number(e.target.value))} />
            </div>
          </div>
            </details>
          </>
        )}

        {tab === 'behaviour' && (
          <>
          <label style={{ ...fieldLabelStyle, marginTop: 0 }}>Default value</label>
          {(() => {
            const tokenError = validateTokenDefault(col.default_value, projectVars)
            return (
              <>
                <div style={{ display: 'flex', gap: 4 }}>
                  <input
                    style={{
                      ...inputStyle, flex: 1,
                      border: tokenError ? '1px solid #ef4444' : inputStyle.border,
                      boxShadow: tokenError ? '0 0 0 2px rgba(239,68,68,0.15)' : undefined,
                    }}
                    value={col.default_value}
                    placeholder="auto_generate(), auto_generate_prefixed(), :audit_add_date, :calendar_today, or a literal"
                    onChange={e => updateColumn(fi, ci, 'default_value', e.target.value)}
                  />
                  <DefaultValuePicker
                    projectVars={projectVars}
                    current={col.default_value}
                    onPick={(token) => {
                      // Clone once and apply both fields together — calling
                      // updateColumn twice in a row would have both ops use
                      // the same `model` snapshot from this closure and the
                      // second onModelChange would overwrite the first.
                      const m = cloneModel(model)
                      const c = m.sql_models[fi].sql_model.columns[ci].column
                      c.default_value = token
                      // Audit + project-variable tokens auto-protect the
                      // column; calendar tokens and auto-generate functions are
                      // user-overridable defaults, so leave protect untouched
                      // (the field stays editable; an empty submission triggers
                      // the auto-fill).
                      const isCalendar = (CALENDAR_TOKENS as readonly string[])
                        .includes(token)
                      if (token && !isCalendar && !isAutoGenToken(token)) c.protect = true
                      onModelChange(m)
                      // The prefixed token does nothing on its own — it needs
                      // parts. Open the builder rather than leaving the user to
                      // discover a second step.
                      if (isPrefixedSeriesToken(token)) {
                        setEditingSeries({ fi, ci })
                        setSeriesEditorOpen(true)
                      }
                    }}
                  />
                </div>
                {tokenError ? (
                  <div style={{
                    marginTop: 4, fontSize: '0.8125rem',
                    color: '#ef4444', fontWeight: 500,
                  }}>{tokenError}</div>
                ) : (auditTokenOf(col) || projectVarOf(col) || calendarTokenOf(col)) ? (
                  <div style={{
                    marginTop: 4, fontSize: '0.8125rem',
                    color: 'var(--color-text-secondary)', fontStyle: 'italic',
                  }}>
                    {auditTokenOf(col)
                      ? 'Auto-stamped server-side; column is marked protect.'
                      : calendarTokenOf(col)
                        ? 'Pre-filled from the server clock; the user can edit the value before saving.'
                        : 'Resolved at insert from the project variable; column is marked protect.'}
                  </div>
                ) : null}
              </>
            )
          })()}
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
              Pick ▾ offers: auto number · ID series · audit stamp · date · project variable
            </div>

          {isPrefixedSeriesToken(col.default_value) && (() => {
            const summary = seriesSummary(col)
            const openSeriesEdit = () => {
              setEditingSeries({ fi, ci })
              setSeriesEditorOpen(true)
            }
            return (
              <>
                <label style={fieldLabelStyle}>ID series</label>
                {summary ? (
                  <button
                    type="button"
                    onClick={openSeriesEdit}
                    title="Edit the id rules"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      padding: '7px 10px', textAlign: 'left',
                      background: 'var(--color-bg)',
                      border: '1px solid var(--color-border)', borderRadius: 6,
                      color: 'var(--color-text)', cursor: 'pointer',
                    }}
                  >
                    <Hash size={14} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                    <span style={{
                      flex: 1, fontSize: '0.8825rem',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{summary}</span>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      fontSize: '0.8125rem', color: 'var(--color-text-secondary)', flexShrink: 0,
                    }}>
                      <Edit3 size={12} /> Edit
                    </span>
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={openSeriesEdit}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: 6, width: '100%', padding: '8px 10px',
                        background: 'var(--color-bg)',
                        border: '1px dashed #f59e0b', borderRadius: 6,
                        color: '#f59e0b', cursor: 'pointer',
                        fontSize: '0.8425rem', fontWeight: 600,
                      }}
                    >
                      <Hash size={14} /> Set up the id rules…
                    </button>
                    <div style={{ ...fieldLabelStyle, marginTop: 4, fontStyle: 'italic' }}>
                      Not configured yet — no id can be generated until it is.
                    </div>
                  </>
                )}
              </>
            )
          })()}

            <div style={{ ...cardStyle, marginTop: 16 }}>
              <div style={cardHeadStyle}>
                <span style={{ flex: 1 }}>Lookup</span>
                {lkSummary && (
                  <button type="button" style={{ ...linkBtnStyle, textDecoration: 'none', color: 'var(--color-error)' }} onClick={() => {
                    const m = cloneModel(model)
                    const c = m.sql_models[fi].sql_model.columns[ci].column
                    c.lookup = ''
                    delete c.lookup_sql; delete c.lookup_filter; delete c.lookup_filter_rules
                    delete c.lookup_title; delete c.lookup_add_object; delete c.lookup_sql_data_col
                    delete c.lookup_add_lines
                    onModelChange(m)
                  }}>Remove</button>
                )}
              </div>
              <div style={{ padding: 12 }}>
          
          {(() => {
            const summary = lookupSummary(col)
            // Form columns available for {form.X} binding — all columns on
            // this sql_model except the field itself. Header columns =
            // the form's IMMEDIATE parent (via parent_form_no), so a
            // grandchild binds {header.X} to its own parent line, not the
            // root header. Empty when editing the header form.
            const formColumns = model.sql_models[fi].sql_model.columns
              .map(c => c.column?.name || '')
              .filter(n => n && n !== col.name)
            const headerColumns = parentColumnNames(model, fi)
            const openLookupEdit = () => {
              setEditingLookup({
                column: col,
                formColumns,
                headerColumns,
                matchRules: true,
                onSave: (patch) => {
                  const m = cloneModel(model)
                  const c = m.sql_models[fi].sql_model.columns[ci].column
                  c.lookup = patch.lookup || ''
                  if ((patch.lookup_sql || '').trim()) c.lookup_sql = patch.lookup_sql
                  else delete c.lookup_sql
                  // Keep exactly one of rules / raw-sql filter live.
                  if (patch.lookup_filter_rules) c.lookup_filter_rules = patch.lookup_filter_rules
                  else delete c.lookup_filter_rules
                  if ((patch.lookup_filter || '').trim()) c.lookup_filter = patch.lookup_filter
                  else delete c.lookup_filter
                  // Title / add-object / SQL value-column — store when set,
                  // drop the key when cleared so saved models stay lean.
                  if ((patch.lookup_title || '').trim()) c.lookup_title = patch.lookup_title
                  else delete c.lookup_title
                  if ((patch.lookup_add_object || '').trim()) c.lookup_add_object = patch.lookup_add_object
                  else delete c.lookup_add_object
                  if ((patch.lookup_sql_data_col || '').trim()) c.lookup_sql_data_col = patch.lookup_sql_data_col
                  else delete c.lookup_sql_data_col
                  // How a written value becomes the code (imports, Assistant).
                  if (patch.lookup_xref) c.lookup_xref = patch.lookup_xref
                  else delete c.lookup_xref
                  if (patch.lookup_resolve && Object.keys(patch.lookup_resolve).length) c.lookup_resolve = patch.lookup_resolve
                  else delete c.lookup_resolve
                  onModelChange(m)
                },
              })
              setLookupEditorOpen(true)
            }
            return summary ? (
              // Configured — a clickable summary card that opens the editor.
              <button
                type="button"
                onClick={openLookupEdit}
                title="Edit lookup"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '7px 10px', textAlign: 'left',
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  color: 'var(--color-text)', cursor: 'pointer',
                }}
              >
                <Search size={14} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                <span style={{
                  flex: 1, fontSize: '0.8825rem',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{summary}</span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  fontSize: '0.8125rem', color: 'var(--color-text-secondary)', flexShrink: 0,
                }}>
                  <Edit3 size={12} /> Edit
                </span>
              </button>
            ) : (
              // Not configured — an inviting call-to-action.
              <>
                <button
                  type="button"
                  onClick={openLookupEdit}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 6, width: '100%', padding: '8px 10px',
                    background: 'var(--color-bg)',
                    border: '1px dashed var(--color-border)', borderRadius: 6,
                    color: 'var(--color-accent)', cursor: 'pointer',
                    fontSize: '0.8425rem', fontWeight: 600,
                  }}
                >
                  <Search size={14} /> Set up lookup…
                </button>
                <div style={{ ...fieldLabelStyle, marginTop: 4, fontStyle: 'italic' }}>
                  Let users pick this value from another table.
                </div>
              </>
            )
          })()}
          {fi > 0 && (col.lookup || '').trim() && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 6,
              fontSize: '0.8425rem', color: 'var(--color-text)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={col.lookup_add_lines === true || col.lookup_add_lines === 'true'}
                onChange={e => {
                  const m = cloneModel(model)
                  const c = m.sql_models[fi].sql_model.columns[ci].column
                  if (e.target.checked) c.lookup_add_lines = true
                  else delete c.lookup_add_lines
                  onModelChange(m)
                }}
              />
              <span>
                Offer "Add {col.heading || col.name}…" on the lines toolbar
                <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                  Users tick many rows of this lookup and get one line each, filled by this form's own rules.
                </span>
              </span>
            </label>
          )}
              </div>
            </div>

            <div style={{ ...cardStyle, marginTop: 16 }}>
              <div style={cardHeadStyle}><span style={{ flex: 1 }}>When it changes</span></div>
              <div style={{ padding: 12 }}>
          
          {renderIcControl(
            col.item_change,
            (ic) => updateColumn(fi, ci, 'item_change', ic),
            model.sql_models[fi].sql_model.columns.map(c => c.column?.name || '').filter(Boolean),
            // Header-form columns for {header.X} tokens — the form's IMMEDIATE
            // parent (empty for the header form itself).
            parentColumnNames(model, fi),
            col.name,
          )}
              </div>
            </div>
            {fi > 0 && !(col.lookup || '').trim() && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 10 }}>
                With a lookup set, a line form can also offer “Add {col.heading || col.name}…” on its lines toolbar.
              </div>
            )}
          </>
        )}

        {tab === 'rules' && (
          <>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 8 }}>
              Validations on this column. Form-wide checks are under the form&apos;s Validations.
            </div>
          {(Array.isArray(col.validations) ? col.validations : []).map((v, vi) => (
            <div key={vi} style={listItemStyle}>
              <span style={{ flex: 1, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: (isValidationRule(v) && v.disabled) ? 0.55 : 1 }}>{validationSummary(v)}</span>
              {isValidationRule(v) && (
                <button title={v.disabled ? 'Enable validation' : 'Disable validation (skip at save)'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: v.disabled ? 'var(--color-text-secondary)' : 'var(--color-accent)', padding: 2 }}
                  onClick={() => {
                    const m = cloneModel(model)
                    const cur = m.sql_models[fi].sql_model.columns[ci].column.validations[vi]
                    if (isValidationRule(cur)) cur.disabled = !cur.disabled
                    onModelChange(m)
                  }}>
                  <Power size={13} />
                </button>
              )}
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', padding: 2 }}
                onClick={() => {
                  const formColumns = model.sql_models[fi].sql_model.columns.map(c => c.column?.name || '').filter(Boolean)
                  openValidationRuleEditor(v, formColumns, parentColumnNames(model, fi), (newVal) => {
                    const m = cloneModel(model)
                    m.sql_models[fi].sql_model.columns[ci].column.validations[vi] = newVal
                    onModelChange(m)
                  })
                }}>
                <Edit3 size={13} />
              </button>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 2 }}
                onClick={() => {
                  const m = cloneModel(model)
                  m.sql_models[fi].sql_model.columns[ci].column.validations.splice(vi, 1)
                  onModelChange(m)
                }}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <button style={addBtnStyle} onClick={() => {
            const formColumns = model.sql_models[fi].sql_model.columns.map(c => c.column?.name || '').filter(Boolean)
            openValidationRuleEditor('', formColumns, parentColumnNames(model, fi), (v) => {
              const m = cloneModel(model)
              if (!m.sql_models[fi].sql_model.columns[ci].column.validations) {
                m.sql_models[fi].sql_model.columns[ci].column.validations = []
              }
              m.sql_models[fi].sql_model.columns[ci].column.validations.push(v)
              onModelChange(m)
            })
          }}>
            <Plus size={12} /> Add validation
          </button>
          </>
        )}
        </div>

        <ValidationRuleEditor
          open={ruleValidationOpen}
          onClose={() => setRuleValidationOpen(false)}
          value={editingRuleValidation?.value ?? ''}
          formColumnNames={editingRuleValidation?.formColumns || []}
          headerColumnNames={editingRuleValidation?.headerColumns || []}
          formList={formList}
          project={projectId}
          onSave={(v) => editingRuleValidation?.onSave(v)}
          onOpenCode={onOpenCode}
        />

        {renderIcEditors()}

        <LookupEditor
          open={lookupEditorOpen}
          onClose={() => setLookupEditorOpen(false)}
          column={editingLookup?.column || null}
          formColumns={editingLookup?.formColumns || []}
          headerColumns={editingLookup?.headerColumns || []}
          project={projectId}
          matchRules={!!editingLookup?.matchRules}
          onSave={(patch) => editingLookup?.onSave(patch)}
        />

        <DisplayFormatEditor
          open={displayEditorOpen}
          onClose={() => setDisplayEditorOpen(false)}
          column={editingDisplay?.column || null}
          formColumns={editingDisplay?.formColumns || []}
          onSave={(display) => editingDisplay?.onSave(display)}
        />

        {editingSeries && (
          <SeriesEditor
            open={seriesEditorOpen}
            onClose={() => setSeriesEditorOpen(false)}
            column={model.sql_models[editingSeries.fi]?.sql_model
              ?.columns[editingSeries.ci]?.column || null}
            formColumns={(model.sql_models[editingSeries.fi]?.sql_model?.columns || [])
              .map(c => c.column?.name || '')
              .filter((n, idx) => n && idx !== editingSeries.ci)}
            project={projectId}
            onSave={(series) => {
              const m = cloneModel(model)
              m.sql_models[editingSeries.fi].sql_model
                .columns[editingSeries.ci].column.series = series
              onModelChange(m)
            }}
          />
        )}
      </div>
    )
  }


  // ---- FORM PROPERTIES ----
  // General · Data source (joins) · Access (row-level security) · List — the
  // old single panel's sections, one per tab. Row events have their own list
  // (the form ▾ menu › Row events).
  if (selectedNode.type === 'form') {
    const form = getForm(selectedNode.formIndex)
    if (!form) return null
    const fi = selectedNode.formIndex!
    const tab: FormTab = (FORM_TABS.some(t => t.key === selectedNode.tab) ? selectedNode.tab : 'general') as FormTab
    const setTab = (t: FormTab) => onSelectNode?.({ ...selectedNode, tab: t })
    const joinCount = form.joins?.join_predicates?.joins?.length || 0

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div role="tablist" aria-label="Form settings" style={tabBarStyle}>
          {FORM_TABS.map(t => (
            <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} style={tabStyle(tab === t.key)}>
              {t.label}
              {t.key === 'data' && joinCount > 0 && <span style={tabCountStyle}>{joinCount}</span>}
            </button>
          ))}
        </div>
        <div style={{ padding: '12px 14px 20px', overflow: 'auto', flex: 1 }}>

        {tab === 'general' && (
        <div style={sectionStyle}>
          <label style={fieldLabelStyle}>Form Title</label>
          <input style={inputStyle} value={form.form_title} onChange={e => updateForm(fi, 'form_title', e.target.value)} />

          <label style={fieldLabelStyle}>Description</label>
          <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} value={form.form_descr}
            onChange={e => updateForm(fi, 'form_descr', e.target.value)} />

          <label style={fieldLabelStyle}>Form Number</label>
          <input style={inputStyle} value={form.form_no} onChange={e => updateForm(fi, 'form_no', e.target.value)} />

          <label style={fieldLabelStyle}>Main Table</label>
          <input style={inputStyle} value={form.main_table} onChange={e => updateForm(fi, 'main_table', e.target.value)} />

          {fi !== 0 && (() => {
            // Parent form selector. The header (form index 0) has no parent.
            // Options exclude this form and its own descendants so a cycle
            // can never be created (forms are a single-parent forest).
            const headerNo = headerFormNo(model)
            const entries = allSqlModels(model)
            const thisFormNo = entries[fi]?.formNo ?? form.form_no
            const banned = descendantFormNos(model, thisFormNo)
            const current = parentFormNoOf(
              { formNo: thisFormNo, sm: form }, headerNo,
            )
            const missingLink = !pkColumnsCovered(model, fi)
            return (
              <>
                <label style={fieldLabelStyle}>Parent form</label>
                <select
                  style={inputStyle}
                  value={current}
                  onChange={e => updateForm(fi, 'parent_form_no', e.target.value)}
                >
                  {entries
                    .filter(en => en.formNo !== thisFormNo && !banned.has(en.formNo))
                    .map(en => (
                      <option key={en.formNo} value={en.formNo}>
                        {en.formNo === headerNo
                          ? `Header — Form ${en.formNo} (${en.sm.form_title || 'Untitled'})`
                          : `Form ${en.formNo} — ${en.sm.form_title || 'Untitled'}`}
                      </option>
                    ))}
                </select>
                <label style={{ ...fieldLabelStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={!!form.read_only}
                    onChange={e => updateForm(fi, 'read_only', e.target.checked || undefined)}
                  />
                  Read-only (rows listed for reference; no add / edit / delete)
                </label>
                {current === headerNo && (() => {
                  // Where a direct child shows on the record editor: its own
                  // tab, or an editable table inside one of the header's groups.
                  const headerGroups = [...new Set(
                    (entries[0]?.sm.columns || [])
                      .map(c => (c.column.group_name || '').trim())
                      .filter(Boolean),
                  )]
                  const inGroup = (form.display_in_group || '').trim()
                  if (inGroup && !headerGroups.includes(inGroup)) headerGroups.push(inGroup)
                  return (
                    <>
                      <label style={fieldLabelStyle}>Show on the record editor</label>
                      <select
                        style={inputStyle}
                        value={inGroup}
                        onChange={e => updateForm(fi, 'display_in_group', e.target.value || undefined)}
                      >
                        <option value="">As a tab</option>
                        {headerGroups.map(g => (
                          <option key={g} value={g}>Table inside header group “{g}”</option>
                        ))}
                      </select>
                      {inGroup && (
                        <>
                          <label style={fieldLabelStyle}>Add button label</label>
                          <input
                            style={inputStyle}
                            value={form.add_label || ''}
                            placeholder="Add row"
                            onChange={e => updateForm(fi, 'add_label', e.target.value || undefined)}
                          />
                        </>
                      )}
                    </>
                  )
                })()}
                {missingLink && (
                  <div style={{
                    fontSize: '0.8125rem', color: '#b26a00',
                    background: 'rgba(255,152,0,0.10)',
                    border: '1px solid rgba(255,152,0,0.35)',
                    borderRadius: 4, padding: '6px 8px', marginTop: 6,
                  }}>
                    This form is missing one or more of its parent's key
                    columns, so saved rows won't link to a parent. Use{' '}
                    <strong>Add missing parent-key column(s)</strong> below.
                  </div>
                )}
                {missingLink && (
                  <button style={addBtnStyle} onClick={() => {
                    const m = cloneModel(model)
                    addMissingParentKeyColumns(m, fi)
                    onModelChange(m)
                  }}>
                    <Plus size={12} /> Add missing parent-key column(s)
                  </button>
                )}
              </>
            )
          })()}

          <div style={{ ...hintBoxStyle, marginTop: 16 }}>
            <span>
              Row add, row edit and row-finished events are under{' '}
              <button type="button" style={linkBtnStyle}
                onClick={() => onSelectNode?.({ type: 'form-row-events', formIndex: fi, label: 'Row events' })}>
                Row events
              </button>.
            </span>
          </div>
        </div>
        )}

        {tab === 'data' && renderJoinsEditor(fi)}

        {tab === 'access' && (
        <div style={sectionStyle}>
          <label style={{ ...fieldLabelStyle, marginTop: 0 }}>Row-level security</label>
          {(() => {
            // Only main-table columns can carry an owner value — the engine
            // filters and stamps on the main table alone.
            const mainTable = (form.main_table || '').trim().toLowerCase()
            const rlsColumns = (form.columns || [])
              .map(c => c.column)
              .filter(c => {
                const t = (c.table_name || '').trim().toLowerCase()
                return !t || t === mainTable
              })
            return (
              <RowSecurityEditor
                form={form}
                columns={rlsColumns}
                projectVars={projectVars}
                project={projectId || ''}
                isChildForm={fi !== 0}
                onChange={rs => updateForm(fi, 'row_security', rs)}
                onHideAndProtect={(dbName) => {
                  const m = cloneModel(model)
                  const cols = m.sql_models[fi].sql_model.columns || []
                  const target = cols.find(
                    c => (c.column.db_name || c.column.name) === dbName,
                  )
                  if (target) {
                    target.column.hidden = 1 as unknown as boolean
                    target.column.protect = 1 as unknown as boolean
                    onModelChange(m)
                  }
                }}
              />
            )
          })()}
        </div>
        )}

        {tab === 'list' && (() => {
          const allCols = (form.columns || []).map(c => c.column)
          const colOf = (dbn: string) =>
            allCols.find(c => (c.db_name || c.name) === dbn)
          // Operators and the input a value is typed into both come from the
          // column's data type, through the SAME helpers the runtime filter
          // bar uses — so what the designer can author is exactly what the
          // run-time input can capture and the database can compare.
          const opsFor = (c?: ColumnDef): EnforcedFilterOp[] => filterOpsForColumn(c)
          const kindOf = (c?: ColumnDef) => filterInputKind(c)
          const defaultOpFor = (c?: ColumnDef) => opsFor(c)[0]
          // A calendar token only resolves to a date; offering one for a text
          // or numeric column is what produced a run-time comparison error.
          const calendarOk = (c?: ColumnDef) => acceptsCalendarDefault(c)
          const calendarMisuse = (c: ColumnDef | undefined, v?: string) =>
            !!v && isCalendarTokenValue(v) && !calendarOk(c)
          // Derive the filter SETS from the model, migrating legacy shapes
          // (a flat enforced_filters list, or a single enforced_filter date)
          // into one implicit set so older transactions keep working.
          const sets: EnforcedFilterSet[] = (() => {
            const lv = form.list_view
            if (lv?.enforced_filter_sets?.length) return lv.enforced_filter_sets
            if (lv?.enforced_filters?.length) {
              return [{ id: 'set1', name: 'Filter 1', conditions: lv.enforced_filters, is_default: true }]
            }
            if (lv?.enforced_filter?.column) {
              return [{
                id: 'set1', name: 'Filter 1', is_default: true,
                conditions: [{
                  column: lv.enforced_filter.column, op: 'between',
                  default_from: lv.enforced_filter.default_from,
                  default_to: lv.enforced_filter.default_to,
                  lock_values: lv.enforced_filter.lock_values,
                }],
              }]
            }
            return []
          })()

          // Unique set id from the existing ids (set1, set2, …).
          const nextSetId = () => {
            const used = new Set(sets.map(s => s.id))
            let n = sets.length + 1
            while (used.has(`set${n}`)) n += 1
            return `set${n}`
          }
          // Does this set still need a value from the user? Mirrors the run
          // time's own derivation (which reads RESOLVED values) so the
          // designer's preview of the behaviour matches what happens.
          const condHasDefault = (cd: EnforcedCondition) => cd.op === 'between'
            ? !!(cd.default_from || '').trim() || !!(cd.default_to || '').trim()
            : !!(cd.default_value || '').trim()
          const derivedExpand = (sset: EnforcedFilterSet) =>
            (sset.conditions || []).some(cd => !cd.lock_values && !condHasDefault(cd))
          const patchSet = (si: number, patch: Partial<EnforcedFilterSet>) =>
            setFilterSets(fi, sets.map((s, i) => i === si ? { ...s, ...patch } : s))
          const setConditionsOf = (si: number, conditions: EnforcedCondition[]) =>
            patchSet(si, { conditions })
          const addSet = () => {
            const first = allCols[0]
            const dbn = first ? (first.db_name || first.name) : ''
            setFilterSets(fi, [...sets, {
              id: nextSetId(), name: `Filter ${sets.length + 1}`,
              is_default: sets.length === 0,
              conditions: [{ column: dbn, op: defaultOpFor(first) }],
            }])
          }
          const removeSet = (si: number) =>
            setFilterSets(fi, sets.filter((_, i) => i !== si))
          const makeDefault = (si: number) =>
            setFilterSets(fi, sets.map((s, i) => ({ ...s, is_default: i === si })))

          return (
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>List View Settings</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                The record list paginates server-side, newest first. Define
                enforced filter sets to keep the view bounded as the transaction
                grows.
              </div>

              <label style={fieldLabelStyle}>Page size (rows per page)</label>
              <input
                type="number" min={1} style={inputStyle}
                value={form.list_view?.page_size ?? ''}
                placeholder="50"
                onChange={e => {
                  const n = parseInt(e.target.value, 10)
                  updateListView(fi, {
                    page_size: Number.isFinite(n) && n > 0 ? n : undefined,
                  })
                }}
              />

              <label style={{ ...fieldLabelStyle, marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={form.list_view?.allow_add !== false}
                  onChange={e => updateListView(fi, { allow_add: e.target.checked ? undefined : false })}
                />
                Allow Add
              </label>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                Untick for a screen whose records are created elsewhere (e.g. an
                approval screen): the Add button is hidden for everyone and the
                server refuses new records through it.
              </div>

              <label style={{ ...fieldLabelStyle, marginTop: 12 }}>Lock records from editing (view-only)</label>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                A condition that makes a matching record read-only on every
                surface — clicking it opens a view-only form (with an eye/View
                indicator) and the server refuses edits. Expression syntax (bare
                column names or {'{form.COL}'}), e.g.{' '}
                <code>{"{form.STATUS} == 'APPROVED'"}</code>. Leave blank for no lock.
              </div>
              <input
                style={inputStyle}
                value={form.list_view?.edit_lock?.expression ?? ''}
                placeholder="{form.STATUS} == 'APPROVED'"
                onChange={e => {
                  const expression = e.target.value
                  const prev = form.list_view?.edit_lock || {}
                  updateListView(fi, {
                    edit_lock: expression.trim()
                      ? { ...prev, expression }
                      : undefined,
                  })
                }}
              />
              {(form.list_view?.edit_lock?.expression || '').trim() && (
                <>
                  <label style={{ ...fieldLabelStyle, marginTop: 8 }}>Lock message (shown to the user)</label>
                  <input
                    style={inputStyle}
                    value={form.list_view?.edit_lock?.reason ?? ''}
                    placeholder="Approved records cannot be edited."
                    onChange={e => {
                      const prev = form.list_view?.edit_lock || {}
                      updateListView(fi, { edit_lock: { ...prev, reason: e.target.value } })
                    }}
                  />
                </>
              )}

              <label style={{ ...fieldLabelStyle, marginTop: 12 }}>Enforced filter sets</label>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                Each set is a group of conditions (e.g. Date + Status). At run
                time the end user picks one set — or "No filter" to browse all
                records paginated. Deploy creates one composite index per set
                (columns in the order listed). The default set applies on first
                load.
              </div>

              {sets.length === 0 && (
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontStyle: 'italic', marginBottom: 6 }}>
                  None — the list shows all rows (still newest-first).
                </div>
              )}

              {sets.map((set, si) => {
                const conditions = set.conditions || []
                const patchCond = (idx: number, patch: Partial<EnforcedCondition>) =>
                  setConditionsOf(si, conditions.map((c, i) => i === idx ? { ...c, ...patch } : c))
                const addCond = () => {
                  const first = allCols[0]
                  const dbn = first ? (first.db_name || first.name) : ''
                  setConditionsOf(si, [...conditions, {
                    column: dbn, op: defaultOpFor(first),
                  }])
                }
                const removeCond = (idx: number) =>
                  setConditionsOf(si, conditions.filter((_, i) => i !== idx))
                return (
                  <div key={set.id || si} style={{
                    border: '1px solid var(--color-border)', borderRadius: 8,
                    padding: 8, marginBottom: 10, background: 'var(--color-bg-secondary)',
                  }}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 6 }}>
                      <input
                        style={{ ...inputStyle, flex: 1, fontWeight: 600 }}
                        value={set.name}
                        placeholder={`Filter ${si + 1}`}
                        onChange={e => patchSet(si, { name: e.target.value })}
                      />
                      <label style={{ ...checkboxRowStyle, whiteSpace: 'nowrap' }} title="Applied on first load">
                        <input type="radio" name={`default-set-${fi}`}
                          checked={!!set.is_default}
                          onChange={() => makeDefault(si)} />
                        <span style={{ fontSize: '0.8125rem' }}>Default</span>
                      </label>
                      <button className="btn btn-ghost btn-sm" title="Remove filter set"
                        onClick={() => removeSet(si)}>
                        <Trash2 size={14} />
                      </button>
                    </div>

                    {/* Open or collapsed on arrival. Left alone this follows
                        the defaults: a set whose values are all defaulted has
                        its data on screen already and opens collapsed; one
                        that still needs a value from the user opens showing
                        its inputs. Ticking or clearing it fixes the choice. */}
                    {(() => {
                      const derived = derivedExpand(set)
                      const explicit = typeof set.expand_inputs === 'boolean'
                      return (
                        <div style={{ marginBottom: 6 }}>
                          <label style={{ ...checkboxRowStyle, marginTop: 0 }}>
                            <input
                              type="checkbox"
                              checked={explicit ? !!set.expand_inputs : derived}
                              onChange={e => patchSet(si, { expand_inputs: e.target.checked })}
                            />
                            <span style={{ fontSize: '0.8625rem' }}>
                              Show the filter inputs when the list opens
                            </span>
                          </label>
                          <div style={{
                            fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                            marginLeft: 22,
                          }}>
                            {explicit
                              ? <>Set explicitly.{' '}
                                <button type="button" style={linkBtnStyle}
                                  onClick={() => patchSet(si, { expand_inputs: undefined })}>
                                  Follow the defaults instead
                                </button>
                              </>
                              : derived
                                ? 'Following the defaults — a condition here has no default value, so the user is asked for one.'
                                : 'Following the defaults — every condition has a default, so the list opens with its data and the inputs collapsed.'}
                          </div>
                        </div>
                      )
                    })()}

                    {conditions.map((cond, idx) => {
                      const c = colOf(cond.column)
                      const kind = kindOf(c)
                      const opts = (kind === 'select' && c?.edit_mask?.values) || null
                      // The default is typed like the run-time input: a date
                      // column gets a calendar, an amount a number field, a
                      // picklist its own values. A token (project variable /
                      // calendar value) is still typed straight in, so the
                      // free-text control takes over the moment one is used.
                      const usesToken = (v?: string) => (v || '').trim().startsWith(':')
                      const nativeType = kind === 'date' ? 'date'
                        : kind === 'datetime' ? 'datetime-local'
                        : kind === 'time' ? 'time'
                        : kind === 'number' ? 'number' : 'text'
                      const boundPlaceholder = calendarOk(c)
                        ? ':calendar_month_start, date, :var'
                        : 'value or :variable'
                      // One bound of a range / the single default value. A
                      // column with a picklist picks its bounds from the SAME
                      // list the run-time input offers.
                      const renderBound = (
                        label: string, value: string | undefined,
                        onValue: (v: string) => void,
                      ) => (
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <label style={fieldLabelStyle}>{label}</label>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {opts && !usesToken(value) ? (
                              <select
                                style={{ ...inputStyle, flex: 1 }}
                                value={value || ''}
                                onChange={e => onValue(e.target.value)}
                              >
                                <option value="">— pick —</option>
                                {opts.map((o, i) => (
                                  <option key={i} value={String(o.data)}>
                                    {o.display || String(o.data)}
                                  </option>
                                ))}
                              </select>
                            ) : (
                            <input
                              style={{ ...inputStyle, flex: 1 }}
                              type={usesToken(value) ? 'text' : nativeType}
                              value={value || ''}
                              placeholder={boundPlaceholder}
                              onChange={e => onValue(e.target.value)}
                            />
                            )}
                            <DefaultValuePicker
                              hideAudit hideCalendar={!calendarOk(c)}
                              projectVars={projectVars}
                              current={value || ''}
                              onPick={(t) => onValue(t)}
                            />
                          </div>
                          {calendarMisuse(c, value) && (
                            <div style={tokenWarnStyle}>
                              <AlertTriangle size={12} /> A calendar value resolves
                              to a date, and {c?.heading || c?.name || 'this column'} is
                              not a date column — the list would fail at run time.
                              Use a fixed value or a project variable.
                            </div>
                          )}
                        </div>
                      )
                      return (
                        <div key={idx} style={{
                          border: '1px solid var(--color-border)', borderRadius: 6,
                          padding: 8, marginBottom: 8, background: 'var(--color-bg)',
                        }}>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            <select
                              style={{ ...inputStyle, flex: 1 }}
                              value={cond.column}
                              onChange={e => {
                                const nc = colOf(e.target.value)
                                const validOps = opsFor(nc)
                                // Defaults authored for the OLD column's type
                                // rarely fit the new one, and a stale calendar
                                // token is exactly the pairing that fails at
                                // run time — so they are cleared with it. A
                                // label captioning the old column (an imported
                                // one, say) would simply be wrong on the new
                                // one, so it goes back to its heading too.
                                patchCond(idx, {
                                  column: e.target.value,
                                  op: validOps.includes(cond.op) ? cond.op : validOps[0],
                                  default_from: undefined,
                                  default_to: undefined,
                                  default_value: undefined,
                                  lookup: undefined,
                                  label: undefined,
                                  label_from: undefined,
                                  label_to: undefined,
                                })
                              }}
                            >
                              {allCols.map((col, i) => {
                                const dbn = col.db_name || col.name
                                return <option key={i} value={dbn}>{col.heading || col.name || dbn}</option>
                              })}
                            </select>
                            <select
                              style={{ ...inputStyle, width: 110 }}
                              value={cond.op}
                              onChange={e => patchCond(idx, { op: e.target.value as EnforcedFilterOp })}
                            >
                              {opsFor(c).map(op => <option key={op} value={op}>{OP_LABELS[op]}</option>)}
                            </select>
                            <button className="btn btn-ghost btn-sm" title="Remove condition"
                              onClick={() => removeCond(idx)}>
                              <Trash2 size={14} />
                            </button>
                          </div>

                          {cond.op === 'between' ? (
                            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                              {renderBound('Default from', cond.default_from,
                                (v) => patchCond(idx, { default_from: v }))}
                              {renderBound('Default to', cond.default_to,
                                (v) => patchCond(idx, { default_to: v }))}
                            </div>
                          ) : (
                            <div style={{ marginTop: 6 }}>
                              {cond.op === 'in' ? (
                                <div style={{ flex: 1 }}>
                                  <label style={fieldLabelStyle}>Default values (comma-separated)</label>
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    <input style={{ ...inputStyle, flex: 1 }} value={cond.default_value || ''}
                                      placeholder="PENDING, APPROVED"
                                      onChange={e => patchCond(idx, { default_value: e.target.value })} />
                                    <DefaultValuePicker
                                      hideAudit hideCalendar projectVars={projectVars}
                                      current={cond.default_value || ''}
                                      onPick={(t) => patchCond(idx, { default_value: t })} />
                                  </div>
                                </div>
                              ) : (
                                renderBound('Default value', cond.default_value,
                                  (v) => patchCond(idx, { default_value: v }))
                              )}
                            </div>
                          )}

                          {/* Reference picker for the run-time input. */}
                          {renderFilterLookupRow(fi, c, cond,
                            (patch) => patchCond(idx, patch))}

                          {/* Checks on the value BEFORE the list runs. */}
                          {renderFilterValidations(fi, cond,
                            (rules) => patchCond(idx, { validations: rules }))}

                          <label style={{ ...checkboxRowStyle, marginTop: 8 }}>
                            <input type="checkbox" checked={!!cond.lock_values}
                              onChange={e => patchCond(idx, { lock_values: e.target.checked })} />
                            <span style={{ fontSize: '0.8825rem' }}>Lock value (end users can't change it)</span>
                          </label>
                        </div>
                      )
                    })}

                    <button className="btn btn-ghost btn-sm" disabled={allCols.length === 0}
                      onClick={addCond} style={{ marginTop: 2 }}>
                      <Plus size={14} /> Add condition
                    </button>
                    <div style={{
                      fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                      marginTop: 4,
                    }}>
                      A date or number RANGE is one condition with the{' '}
                      <strong>from–to</strong> operator — it draws a single
                      From→To pair at run time. Two conditions (&gt;= and &lt;=)
                      on the same column work too, and each gets its own input.
                    </div>
                  </div>
                )
              })}

              <button className="btn btn-ghost btn-sm" disabled={allCols.length === 0}
                onClick={addSet} style={{ marginTop: 2 }}>
                <Plus size={14} /> Add filter set
              </button>
            </div>
          )
        })()}
        </div>

        {/* Form-level row_add / row_edit editors (no trigger column). */}
        {renderIcEditors()}

        {/* Both are driven by shared state the filter-condition editors above
            flip open, so they must be MOUNTED in this branch too — the column
            branch's copies are not on screen here. */}
        <LookupEditor
          open={lookupEditorOpen}
          onClose={() => setLookupEditorOpen(false)}
          column={editingLookup?.column || null}
          formColumns={editingLookup?.formColumns || []}
          headerColumns={editingLookup?.headerColumns || []}
          project={projectId}
          matchRules={!!editingLookup?.matchRules}
          onSave={(patch) => editingLookup?.onSave(patch)}
        />
        <ValidationRuleEditor
          open={ruleValidationOpen}
          onClose={() => setRuleValidationOpen(false)}
          value={editingRuleValidation?.value ?? ''}
          formColumnNames={editingRuleValidation?.formColumns || []}
          headerColumnNames={editingRuleValidation?.headerColumns || []}
          formList={formList}
          project={projectId}
          onSave={(v) => editingRuleValidation?.onSave(v)}
          onOpenCode={onOpenCode}
        />
      </div>
    )
  }

  // ---- COLUMNS PARENT ----
  if (selectedNode.type === 'columns') {
    const form = getForm(selectedNode.formIndex)
    if (!form) return null
    const fi = selectedNode.formIndex!

    return (
      <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
        <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 12, color: 'var(--color-text)' }}>
          Columns
        </div>
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Summary</div>
          <p style={{ fontSize: '0.9375rem', color: 'var(--color-text)', margin: '4px 0' }}>
            Total Columns: {form.columns?.length || 0}
          </p>
          <p style={{ fontSize: '0.9375rem', color: 'var(--color-text)', margin: '4px 0' }}>
            Tables: {[...new Set((form.columns || []).map(c => c.column.table_name))].join(', ') || form.main_table}
          </p>
        </div>

        <button style={addBtnStyle} onClick={() => {
          const m = cloneModel(model)
          const cols = m.sql_models[fi].sql_model.columns
          const newId = String(cols.length + 1)
          cols.push({
            column: {
              name: `NEW_COLUMN_${newId}`,
              col_id: newId,
              group_name: 'Basic Information',
              col_type: 'VARCHAR',
              heading: `New Column ${newId}`,
              descr: '',
              key: false,
              default_value: '',
              protect: false,
              mandatory: false,
              db_name: `NEW_COLUMN_${newId}`,
              table_name: form.main_table,
              db_size: '50',
              width: 200,
              x: '10',
              y: String(50 + cols.length * 40),
              hidden: false,
              format: '',
              edit_mask: { mask: '', values: [] },
              lookup: '',
              lookup_filter: '',
              item_change: '',
              validations: [],
            }
          })
          onModelChange(m)
        }}>
          <Plus size={12} /> Add Column
        </button>
      </div>
    )
  }

  // ---- OBJECT ACTIONS ----
  if (selectedNode.type === 'object-actions') {
    // Object-level actions run against the header form's values.
    const headerCols = (model.sql_models[0]?.sql_model.columns || [])
      .map(c => c.column?.name || '').filter(Boolean)
    return (
      <ActionsListEditor
        title="Record buttons"
        actions={model.actions || []}
        focusActionIndex={selectedNode.actionIndex}
        focusKey={`object:${selectedNode.actionIndex ?? 'none'}`}
        onChange={(actions) => {
          const m = cloneModel(model)
          m.actions = actions
          onModelChange(m)
        }}
        onConvertToLineSource={(ai) => convertSelectGrid({ kind: 'object' }, ai)}
        sourceObjName={model.obj_name}
        renderBlApi={renderBlApi}
        renderEditors={renderIcEditors}
        onOpenCode={onOpenCode}
        openValidationRuleEditor={openValidationRuleEditor}
        ruleValidationOpen={ruleValidationOpen}
        setRuleValidationOpen={setRuleValidationOpen}
        editingRuleValidation={editingRuleValidation}
        formColumns={headerCols}
        headerColumns={[]}
        formList={formList}
        project={projectId}
        onGuardedNavigate={onGuardedNavigate}
      />
    )
  }

  // ---- FORM ACTIONS ----
  if (selectedNode.type === 'form-actions') {
    const form = getForm(selectedNode.formIndex)
    if (!form) return null
    const fi = selectedNode.formIndex!
    const formCols = (form.columns || []).map(c => c.column?.name || '').filter(Boolean)

    return (
      <ActionsListEditor
        title={`Form buttons · ${form.form_title || `Form ${form.form_no}`}`}
        actions={form.actions || []}
        focusActionIndex={selectedNode.actionIndex}
        focusKey={`form:${fi}:${selectedNode.actionIndex ?? 'none'}`}
        onChange={(actions) => {
          const m = cloneModel(model)
          m.sql_models[fi].sql_model.actions = actions
          onModelChange(m)
        }}
        onConvertToLineSource={(ai) => convertSelectGrid({ kind: 'form', fi }, ai)}
        sourceObjName={model.obj_name}
        renderBlApi={renderBlApi}
        renderEditors={renderIcEditors}
        onOpenCode={onOpenCode}
        openValidationRuleEditor={openValidationRuleEditor}
        ruleValidationOpen={ruleValidationOpen}
        setRuleValidationOpen={setRuleValidationOpen}
        editingRuleValidation={editingRuleValidation}
        formColumns={formCols}
        headerColumns={parentColumnNames(model, fi)}
        formList={formList}
        project={projectId}
        onGuardedNavigate={onGuardedNavigate}
      />
    )
  }

  // ---- FORM VALIDATIONS ----
  // ---- OFFLINE & SYNC ----
  // ---- VECTOR SEARCH (semantic lookup on a master) ----
  if (selectedNode.type === 'assistant-authoring') {
    const au = model.authoring
    const fromContent = !au && !!model.content?.enabled
    const enabled = au ? au.enabled !== false : fromContent
    const patch = (next: Partial<NonNullable<TransactionModel['authoring']>>) => {
      const m = cloneModel(model)
      m.authoring = { ...(m.authoring || { enabled: true }), ...next }
      onModelChange(m)
    }
    // Form keys, as the Assistant proposes values (lower-cased column names).
    const formKey = (c: ColumnDef) => String(c.name || c.db_name || '').trim().replace(/\s+/g, '_').toLowerCase()
    const headerColumns = (model.sql_models?.[0]?.sql_model?.columns || []).map(c => c.column)
    const selected = au?.fields || []
    const toggleField = (key: string) => {
      patch({ fields: selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key] })
    }
    const row: React.CSSProperties = { marginBottom: 12 }
    const vis = model.content?.visibility
    return (
      <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
        <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 6, color: 'var(--color-text)' }}>
          Assistant Authoring
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Lets the app&apos;s Assistant <em>write</em> this object&apos;s content — a
          blog post, a product email, a job description — when a user asks for
          one. A new record it writes is saved as a <strong>draft</strong> unless
          the user asked to review it first and pressed <strong>Publish</strong>.
          It still needs the Assistant&apos;s <strong>Act</strong> capability and
          the user&apos;s add right on this object.
        </p>

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Availability</div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', ...row }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={e => patch({ enabled: e.target.checked })}
              style={{ marginTop: 3 }}
            />
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text)' }}>
              The Assistant may write this object&apos;s content
              {fromContent && (
                <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                  On because this object is a content collection: its title,
                  summary, body and SEO fields and its{' '}
                  <code>{vis?.status_column || 'status'}</code> lifecycle are used
                  as they are. Change anything below to override them.
                </span>
              )}
            </span>
          </label>
        </div>

        {enabled && (
          <>
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Fields it writes</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                None ticked = every prose field: rich text, long text and free-text
                columns that are not keys, look-ups or defaulted.
              </div>
              {headerColumns.map(c => {
                const key = formKey(c)
                if (!key) return null
                return (
                  <label key={key} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.8125rem', marginBottom: 4 }}>
                    <input type="checkbox" checked={selected.includes(key)} onChange={() => toggleField(key)} />
                    <span>{c.heading || c.name}</span>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>{c.col_type}</span>
                  </label>
                )
              })}
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Draft / live lifecycle</div>
              <div style={row}>
                <label style={fieldLabelStyle}>Status column</label>
                <select
                  style={inputStyle}
                  value={au?.status_column || ''}
                  onChange={e => patch({ status_column: e.target.value || undefined })}
                >
                  <option value="">{fromContent || vis?.status_column ? `From content (${vis?.status_column || 'status'})` : 'None — saved as-is'}</option>
                  {headerColumns.map(c => {
                    const key = formKey(c)
                    return key ? <option key={key} value={key}>{c.heading || c.name}</option> : null
                  })}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8, ...row }}>
                <div style={{ flex: 1 }}>
                  <label style={fieldLabelStyle}>Draft value</label>
                  <input
                    style={inputStyle}
                    value={au?.draft_value || ''}
                    placeholder={fromContent ? 'draft' : 'e.g. draft'}
                    onChange={e => patch({ draft_value: e.target.value || undefined })}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={fieldLabelStyle}>Live value</label>
                  <input
                    style={inputStyle}
                    value={au?.live_value || ''}
                    placeholder={fromContent ? (vis?.live_values?.[0] || 'published') : 'e.g. published'}
                    onChange={e => patch({ live_value: e.target.value || undefined })}
                  />
                </div>
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                Publishing is an ordinary save with the live value, so this
                object&apos;s validations, approval workflow and post-commit
                hooks all still apply.
              </div>
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Pictures</div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                Placed inside the content as PNG images. Used only when the user asks for
                them, or the content has figures, steps or a comparison worth drawing.
              </div>
              {([
                ['infographics', 'Infographics', 'Charts, figure cards, steps and timelines drawn from the data the user gave — no image model, exact text.'],
                ['images', 'Generated images', "Illustrations and photos from the owner's image model (Settings → LLM → Image generation)."],
                ['cover', 'Cover image', 'A lead image written into the image column.'],
              ] as const).map(([key, label, hint]) => (
                <label key={key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={au?.visuals?.[key] !== false}
                    onChange={e => patch({ visuals: { ...(au?.visuals || {}), [key]: e.target.checked } })}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ fontSize: '0.8125rem', color: 'var(--color-text)' }}>
                    {label}
                    <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{hint}</span>
                  </span>
                </label>
              ))}
              <div style={{ display: 'flex', gap: 8, ...row }}>
                <div style={{ flex: 1 }}>
                  <label style={fieldLabelStyle}>Most pictures per record</label>
                  <input
                    type="number" min={0} max={6}
                    style={inputStyle}
                    value={au?.visuals?.max ?? 4}
                    onChange={e => patch({ visuals: { ...(au?.visuals || {}), max: Math.max(0, Math.min(6, Number(e.target.value) || 0)) } })}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={fieldLabelStyle}>Infographic colour</label>
                  <input
                    type="color"
                    style={{ ...inputStyle, padding: 2, height: 30 }}
                    value={au?.visuals?.accent || '#2563eb'}
                    onChange={e => patch({ visuals: { ...(au?.visuals || {}), accent: e.target.value } })}
                  />
                </div>
              </div>
              {!model.content?.enabled && (
                <div style={row}>
                  <label style={fieldLabelStyle}>Cover image column</label>
                  <select
                    style={inputStyle}
                    value={au?.image_field || ''}
                    onChange={e => patch({ image_field: e.target.value || undefined })}
                  >
                    <option value="">None</option>
                    {headerColumns.map(c => {
                      const key = formKey(c)
                      return key ? <option key={key} value={key}>{c.heading || c.name}</option> : null
                    })}
                  </select>
                </div>
              )}
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>House style</div>
              <textarea
                style={{ ...inputStyle, minHeight: 90, resize: 'vertical', fontFamily: 'inherit' }}
                value={au?.guidance || ''}
                placeholder="Voice, audience, structure, words to avoid — e.g. 'Plain, friendly British English; open with the customer's problem; end with one call to action.'"
                onChange={e => patch({ guidance: e.target.value || undefined })}
              />
            </div>
          </>
        )}
        {renderIcEditors()}
      </div>
    )
  }

  if (selectedNode.type === 'vector-search') {
    const vs = model.vector_search || { enabled: false, columns: [] }
    const patch = (next: Partial<NonNullable<TransactionModel['vector_search']>>) => {
      const m = cloneModel(model)
      m.vector_search = {
        ...(m.vector_search || { enabled: false, columns: [] }), ...next,
        // Any hand edit means the block is a decision, not a suggestion, so
        // the automatic re-derivation stops touching it.
        auto: false,
      }
      onModelChange(m)
    }
    // Only OWN-table columns can be indexed: a joined display column lives in
    // another table and can't be read off this record.
    const primary = model.sql_models?.[0]?.sql_model
    const mainTable = (primary?.main_table || '').trim().toLowerCase()
    const ownColumns = (primary?.columns || [])
      .map(c => c.column)
      .filter(c => {
        const src = String((c as { table?: string }).table || '').trim().toLowerCase()
        return !src || !mainTable || src === mainTable
      })
    const colKey = (c: ColumnDef) => String(c.name || c.db_name || '').trim()
    const selected = vs.columns || []
    const toggle = (key: string) => {
      const next = selected.includes(key)
        ? selected.filter(k => k !== key)
        : [...selected, key]
      patch({ columns: next })
    }
    const row: React.CSSProperties = { marginBottom: 12 }
    return (
      <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
        <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 6, color: 'var(--color-text)' }}>
          Vector Search
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Lets people find this object&apos;s records by describing them rather
          than spelling them — &ldquo;Bob in the Mumbai sales office&rdquo;.
          Turn it on for a MASTER (employees, customers, items, branches), not
          for a document. It also needs the app-level switch in{' '}
          <strong>Database Designer → Vector Search</strong>.
        </p>

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Availability</div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', ...row }}>
            <input
              type="checkbox"
              checked={!!vs.enabled}
              onChange={e => patch({ enabled: e.target.checked })}
              style={{ marginTop: 3 }}
            />
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text)' }}>
              Searchable by meaning
              <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                Records index themselves on save; use Re-index in the Database
                Designer for rows that were already there.
              </span>
            </span>
          </label>

          {vs.auto && vs.reason && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                          background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                          borderRadius: 6, padding: '6px 8px', ...row }}>
              Configured automatically: {vs.reason}
            </div>
          )}
        </div>

        {vs.enabled && (
          <>
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Searched by</div>
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 8px', lineHeight: 1.5 }}>
                Pick the name column <em>and</em> the columns that tell two
                records with the same name apart — department, branch, city.
                A name on its own is exactly what makes three people called
                Sharma indistinguishable, which is the problem this solves.
              </p>
              <div style={{ display: 'grid', gap: 4, ...row }}>
                {ownColumns.map((c, i) => {
                  const key = colKey(c)
                  if (!key) return null
                  const idx = selected.indexOf(key)
                  return (
                    <label key={`${key}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.8125rem' }}>
                      <input type="checkbox" checked={idx >= 0} onChange={() => toggle(key)} />
                      <span>{key}</span>
                      {idx === 0 && (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                          (identity)
                        </span>
                      )}
                    </label>
                  )
                })}
              </div>
              {selected.length === 1 && (
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-warning, #d97706)' }}>
                  Only one column selected — two records sharing this value will
                  be indistinguishable to a search. Add whatever separates them.
                </div>
              )}
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Result</div>
              <div style={row}>
                <label style={fieldLabelStyle}>Returns (code column)</label>
                <select
                  style={inputStyle}
                  value={vs.key_column || ''}
                  onChange={e => patch({ key_column: e.target.value })}
                >
                  <option value="">(primary key)</option>
                  {ownColumns.map((c, i) => (
                    <option key={`k-${i}`} value={colKey(c)}>{colKey(c)}</option>
                  ))}
                </select>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                  What a search hands back — the value other transactions store.
                </div>
              </div>
              <div style={row}>
                <label style={fieldLabelStyle}>Shows (display column)</label>
                <select
                  style={inputStyle}
                  value={vs.display_column || ''}
                  onChange={e => patch({ display_column: e.target.value })}
                >
                  <option value="">(first searched column)</option>
                  {ownColumns.map((c, i) => (
                    <option key={`d-${i}`} value={colKey(c)}>{colKey(c)}</option>
                  ))}
                </select>
              </div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', ...row }}>
                <input
                  type="checkbox"
                  checked={vs.allow_alias !== false}
                  onChange={e => patch({ allow_alias: e.target.checked })}
                  style={{ marginTop: 3 }}
                />
                <span style={{ fontSize: '0.8125rem', color: 'var(--color-text)' }}>
                  Let users add aliases
                  <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                    Adds an &ldquo;Also known as&rdquo; button to the record, so
                    a user can teach the app that Robert Sharma is called Bob.
                    The alias makes him findable without changing his data.
                  </span>
                </span>
              </label>
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Advanced</div>
              <div style={row}>
                <label style={fieldLabelStyle}>Sentence template (optional)</label>
                <input
                  style={inputStyle}
                  value={vs.template || ''}
                  placeholder="{EMP_NAME} works in {DEPARTMENT} at {BRANCH}, {CITY}"
                  onChange={e => patch({ template: e.target.value })}
                />
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                  Leave blank to join the columns as &ldquo;Label: value&rdquo;
                  pairs. Changing this needs a re-index — records already
                  indexed keep the old wording until then.
                </div>
              </div>
            </div>

            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Matching written values</div>
              <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 8px', lineHeight: 1.5 }}>
                When a file import or the Assistant meets a name or description
                instead of a code (&ldquo;Blue cotton shirt XL&rdquo;), the
                closest record is used only if it is this sure, and clearly
                ahead of the next one. Anything weaker is offered as a choice.
              </p>
              <div style={{ display: 'flex', gap: 10, ...row }}>
                <div style={{ flex: 1 }}>
                  <label style={fieldLabelStyle}>Accept a match from</label>
                  <input
                    type="number" min={0} max={1} step={0.05}
                    style={inputStyle}
                    value={vs.min_score ?? ''}
                    placeholder="0.55"
                    onChange={e => patch({ min_score: e.target.value === '' ? undefined : Math.min(1, Math.max(0, Number(e.target.value))) })}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={fieldLabelStyle}>Ahead of the next by</label>
                  <input
                    type="number" min={0} max={1} step={0.01}
                    style={inputStyle}
                    value={vs.margin ?? ''}
                    placeholder="0.05"
                    onChange={e => patch({ margin: e.target.value === '' ? undefined : Math.min(1, Math.max(0, Number(e.target.value))) })}
                  />
                </div>
              </div>
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                Similarity from 0 to 1. Blank uses the default. A lookup column
                can set its own in its Lookup editor.
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  if (selectedNode.type === 'additional-attributes') {
    return <AdditionalAttributesSettings model={model} onModelChange={onModelChange} project={project || ''} onOpenCode={onOpenCode} />
  }

  if (selectedNode.type === 'file-import') {
    const fi = model.import_settings || {}
    const patch = (next: Partial<NonNullable<TransactionModel['import_settings']>>) => {
      const m = cloneModel(model)
      const merged = { ...(m.import_settings || {}), ...next }
      const instr = Object.fromEntries(Object.entries(merged.instructions || {})
        .filter(([, v]) => (v || '').trim()))
      merged.instructions = Object.keys(instr).length ? instr : undefined
      if (!merged.sender_field) delete merged.sender_field
      if (merged.learn_profiles !== false) delete merged.learn_profiles
      if (!merged.instructions) delete merged.instructions
      const attrs = Object.fromEntries(Object.entries(merged.sender_attributes || {})
        .filter(([, v]) => (v || '').trim()))
      if (Object.keys(attrs).length) merged.sender_attributes = attrs
      else delete merged.sender_attributes
      if (!(merged.own_identifiers || []).length) delete merged.own_identifiers
      if (!(merged.sender_masters || []).length) delete merged.sender_masters
      if (merged.detect_documents !== false) delete merged.detect_documents
      if (!(merged.document_types || []).length) delete merged.document_types
      const det = Object.fromEntries(Object.entries(merged.detect || {})
        .filter(([, v]) => v !== undefined && v !== null && !Number.isNaN(v)))
      if (Object.keys(det).length) merged.detect = det
      else delete merged.detect
      m.import_settings = Object.keys(merged).length ? merged : undefined
      if (!m.import_settings) delete m.import_settings
      onModelChange(m)
    }
    const header = model.sql_models?.[0]?.sql_model
    const lookups = (header?.columns || []).map(c => c.column)
      .filter(c => c && (c.lookup || '').trim())
    const setInstr = (key: 'all' | 'pdf' | 'image' | 'docx' | 'spreadsheet', value: string) =>
      patch({ instructions: { ...(fi.instructions || {}), [key]: value } })
    const row: React.CSSProperties = { marginBottom: 12 }
    const help: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }
    const KINDS: Array<{ key: 'all' | 'pdf' | 'image' | 'docx' | 'spreadsheet'; label: string; ph: string }> = [
      { key: 'all', label: 'Every file', ph: 'e.g. Order numbers start with PO-. Ignore the terms and conditions page.' },
      { key: 'pdf', label: 'PDF documents', ph: 'e.g. The order table is on the last page.' },
      { key: 'image', label: 'Pictures / scans', ph: 'e.g. Handwritten quantities: 1 and 7 are easily confused — check against the total.' },
      { key: 'docx', label: 'Word documents', ph: '' },
      { key: 'spreadsheet', label: 'Spreadsheets (guides the Assistant’s column mapping)', ph: '' },
    ]
    return (
      <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
        <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 6, color: 'var(--color-text)' }}>
          File import
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
          How files are read when imported into this screen — by an Import button or the Assistant. PDFs,
          pictures and Word files are read by the AI; these instructions are part of every reading. Each
          sender (customer, supplier…) can also have their own profile — what their documents look like and
          how to read them — drafted by the AI after their first import and edited in the import window&apos;s
          <strong> Sender profiles</strong> by people with the <em>Manage import profiles</em> right.
        </p>

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Who sent the file</div>
          <div style={row}>
            <label style={fieldLabelStyle}>Sender field</label>
            <select style={inputStyle} value={fi.sender_field || ''}
              onChange={e => patch({ sender_field: e.target.value || undefined })}>
              <option value="">(automatic — a customer / supplier lookup)</option>
              {lookups.map((c, i) => (
                <option key={`s-${i}`} value={String(c!.name || c!.db_name || '')}>
                  {String(c!.heading || c!.name || c!.db_name)}
                </option>
              ))}
            </select>
            <div style={help}>
              The header lookup that says who sent a document. Their profile is found by it.
            </div>
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', ...row }}>
            <input type="checkbox" checked={fi.learn_profiles !== false} style={{ marginTop: 3 }}
              onChange={e => patch({ learn_profiles: e.target.checked ? undefined : false })} />
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text)' }}>
              Learn each sender&apos;s format
              <span style={{ display: 'block', ...help, marginTop: 0 }}>
                After a sender&apos;s first saved import the AI drafts their profile from the document and
                what was corrected (one extra AI call, once per sender and file type).
              </span>
            </span>
          </label>
        </div>

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Recognising the sender and the document</div>
          <div style={{ ...help, marginTop: 0, marginBottom: 10 }}>
            Before a document is read, its sender and type are recognised by KEYWORDS printed on it — the
            sender&apos;s name, GSTIN, email, phone, city; the heading &ldquo;Purchase Order&rdquo; — and the
            matching prompt is shown for review. The AI proposes keywords only when these do not match.
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', ...row }}>
            <input type="checkbox" checked={fi.detect_documents !== false} style={{ marginTop: 3 }}
              onChange={e => patch({ detect_documents: e.target.checked ? undefined : false })} />
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text)' }}>
              Recognise the sender and document type by default
              <span style={{ display: 'block', ...help, marginTop: 0 }}>
                Off: documents are read with this screen&apos;s general instructions. Each person can switch it
                in the import window; their choice is remembered for this screen.
              </span>
            </span>
          </label>
          <div style={row}>
            <label style={fieldLabelStyle}>Look the sender&apos;s name up in (objects, comma-separated)</label>
            <input style={inputStyle} value={(fi.sender_masters || []).join(', ')}
              placeholder="every indexed master — e.g. customer, travel_agent"
              onChange={e => patch({ sender_masters: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })} />
            <div style={help}>
              A name read off the document is searched in these masters&apos; vector index; the record found sets the
              sender&apos;s code and object. A sender in none of them is recognised by name.
            </div>
          </div>
          <label style={fieldLabelStyle}>Where the sender&apos;s records keep these details (column names)</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, ...row }}>
            {([['tax_id', 'GSTIN / tax ID', 'gst_no'], ['email', 'Email', 'email'], ['phone', 'Phone', 'mobile'],
               ['city', 'City', 'city'], ['postal', 'PIN / ZIP', 'pin_code']] as const).map(([k, label, ph]) => (
              <div key={k}>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{label}</div>
                <input style={inputStyle} value={(fi.sender_attributes || {})[k] || ''} placeholder={ph}
                  onChange={e => patch({ sender_attributes: { ...(fi.sender_attributes || {}), [k]: e.target.value.trim() } })} />
              </div>
            ))}
          </div>
          <div style={{ ...help, marginTop: -6, marginBottom: 12 }}>
            Columns of the sender lookup&apos;s table. A document from a customer whose GSTIN, email or phone is
            printed on it is then recognised the first time, with no AI — two of them must agree.
          </div>
          <div style={row}>
            <label style={fieldLabelStyle}>Your organization&apos;s own details (one per line)</label>
            <textarea rows={3} style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
              value={(fi.own_identifiers || []).join('\n')}
              placeholder={'Our Company Pvt Ltd\n27AAAAA0000A1Z5\naccounts@ourcompany.com'}
              onChange={e => patch({ own_identifiers: e.target.value.split('\n').map(x => x.trim()).filter(Boolean) })} />
            <div style={help}>Printed on every document you receive (bill-to, ship-to) — never a sender&apos;s keyword.</div>
          </div>
          <label style={fieldLabelStyle}>Document types this screen receives</label>
          {(fi.document_types || []).map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 6 }}>
              <input style={{ ...inputStyle, flex: '0 0 34%' }} value={t.label} placeholder="Purchase order"
                onChange={e => {
                  const list = [...(fi.document_types || [])]
                  list[i] = { ...t, label: e.target.value }
                  patch({ document_types: list })
                }} />
              <input style={{ ...inputStyle, flex: 1 }}
                value={(t.patterns || []).filter(p => p.kind === 'phrase').map(p => p.value).join(', ')}
                placeholder="purchase order, indent"
                onChange={e => {
                  const list = [...(fi.document_types || [])]
                  const others = (t.patterns || []).filter(p => p.kind !== 'phrase')
                  list[i] = { ...t, patterns: [...others, ...e.target.value.split(',').map(x => x.trim())
                    .filter(Boolean).map(v => ({ kind: 'phrase', value: v }))] }
                  patch({ document_types: list })
                }} />
              <button type="button" style={{ ...inputStyle, width: 32, padding: 0, cursor: 'pointer' }}
                title="Remove" onClick={() => patch({ document_types: (fi.document_types || []).filter((_, j) => j !== i) })}>×</button>
            </div>
          ))}
          <button type="button" style={{ ...inputStyle, width: 'auto', cursor: 'pointer', marginBottom: 4 }}
            onClick={() => patch({ document_types: [...(fi.document_types || []), { label: '', patterns: [] }] })}>
            + Add a document type
          </button>
          <div style={{ ...help, marginBottom: 12 }}>
            Its label, and the heading words that name it (comma-separated). Patterns such as
            <code> PO {'{sep}'} No {'{num}'}</code> and each sender&apos;s own wording are added in the import
            window&apos;s <strong>Keywords</strong> tab.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={fieldLabelStyle}>Kinds of keyword a sender needs</label>
              <input type="number" min={1} max={6} style={inputStyle} placeholder="2"
                value={fi.detect?.min_kinds ?? ''}
                onChange={e => patch({ detect: { ...(fi.detect || {}),
                  min_kinds: e.target.value === '' ? undefined : Math.min(6, Math.max(1, Math.round(Number(e.target.value)))) } })} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={fieldLabelStyle}>Heading lines on page 1</label>
              <input type="number" min={1} max={200} style={inputStyle} placeholder="15"
                value={fi.detect?.title_lines ?? ''}
                onChange={e => patch({ detect: { ...(fi.detect || {}),
                  title_lines: e.target.value === '' ? undefined : Math.min(200, Math.max(1, Math.round(Number(e.target.value)))) } })} />
            </div>
          </div>
          <div style={help}>
            A sender is recognised only when keywords of this many DIFFERENT kinds match (a name with a GSTIN,
            an email with a phone). Headings are searched for in the first lines of the first page.
          </div>
        </div>

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Instructions for reading</div>
          {KINDS.map(k => (
            <div key={k.key} style={row}>
              <label style={fieldLabelStyle}>{k.label}</label>
              <textarea rows={3} style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
                value={(fi.instructions || {})[k.key] || ''} placeholder={k.ph}
                onChange={e => setInstr(k.key, e.target.value)} />
            </div>
          ))}
          <div style={help}>
            A sender&apos;s own instructions are added after these and win where they disagree.
          </div>
        </div>
      </div>
    )
  }

  if (selectedNode.type === 'offline') {
    const off = model.offline || { eligible: false }
    const patch = (next: Partial<NonNullable<TransactionModel['offline']>>) => {
      const m = cloneModel(model)
      m.offline = { ...(m.offline || { eligible: false }), ...next }
      onModelChange(m)
    }
    const row: React.CSSProperties = { marginBottom: 12 }
    return (
      <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
        <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 6, color: 'var(--color-text)' }}>
          Offline &amp; Sync
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Marks this object usable on a disconnected device — a salesman
          invoicing all day with no signal. Turning it on enables extra checks
          at publish time, because the things that break offline break{' '}
          <em>silently</em>: business logic that isn&apos;t Python never runs on
          the device&apos;s SQLite, a REST validation always passes because the
          service is unreachable, and an unstamped <code>chg_date</code> means a
          conflicting edit is overwritten with no conflict reported.
        </p>

        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Availability</div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', ...row }}>
            <input
              type="checkbox"
              checked={!!off.eligible}
              onChange={e => patch({ eligible: e.target.checked })}
              style={{ marginTop: 3 }}
            />
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text)' }}>
              Works offline
              <span style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                Off = online only. Leave it off unless a device really needs
                this object; the constraints are real.
              </span>
            </span>
          </label>

          {off.eligible && (
            <>
              <div style={row}>
                <label style={fieldLabelStyle}>Direction</label>
                <select
                  style={inputStyle}
                  value={off.sync || 'push'}
                  onChange={e => patch({ sync: e.target.value as 'push' | 'pull' | 'bidirectional' })}
                >
                  <option value="push">Push — the device captures, the server is the record</option>
                  <option value="pull">Pull — reference data the device reads</option>
                  <option value="bidirectional">Both</option>
                </select>
              </div>
              <div style={row}>
                <label style={fieldLabelStyle}>On conflict</label>
                <select
                  style={inputStyle}
                  value={off.conflict || 'device_wins'}
                  onChange={e => patch({ conflict: e.target.value as NonNullable<TransactionModel['offline']>['conflict'] })}
                >
                  <option value="device_wins">Device wins — it saw the customer</option>
                  <option value="server_wins">Server wins</option>
                  <option value="last_writer_wins">Last writer wins</option>
                  <option value="review">Park for review</option>
                </select>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                  Applies when the same record changed on BOTH sides while the
                  device was away.
                </div>
              </div>
              <div style={row}>
                <label style={fieldLabelStyle}>Queue priority</label>
                <input
                  type="number"
                  style={inputStyle}
                  value={off.queue_priority ?? 0}
                  onChange={e => patch({ queue_priority: Number(e.target.value) || 0 })}
                />
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                  Higher drains first at day end — invoices before surveys.
                </div>
              </div>
            </>
          )}
        </div>

        {off.eligible && (
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>What publish will check</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.7 }}>
              <li>Business logic is <strong>Python</strong> — one source has to run on the server engine <em>and</em> on SQLite.</li>
              <li>No <code>rest_api</code> item-change or validation (unreachable offline; with <code>on_error: allow</code> it passes silently).</li>
              <li>A <code>chg_date</code> column defaulted <code>:audit_chg_date</code>, or conflict detection never fires.</li>
              <li>No <code>native_type</code> column — an engine-native type has no SQLite equivalent to create.</li>
              <li>Every <code>sql_range</code> says what &quot;no matching row&quot; means (it PASSES by default).</li>
              <li>A device series column wide enough to hold its number, with a uniqueness backstop.</li>
            </ul>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 8 }}>
              Which tables travel to the device — and the per-salesman filter —
              is set once for the whole project under{' '}
              <strong>Database &amp; Runtime → Offline</strong>, not here.
            </div>
          </div>
        )}
        {renderIcEditors()}
      </div>
    )
  }

  // ---- FORM-LEVEL CROSS UPDATES ----
  // ---- LINE ENTRY (a line form's sources + buttons) ----
  if (selectedNode.type === 'form-line-entry') {
    const fi = selectedNode.formIndex ?? 0
    const form = getForm(fi)
    if (!form) return null
    const entries = allSqlModels(model)
    const formNo = entries[fi]?.formNo
    const parentNo = form.parent_form_no && form.parent_form_no !== formNo
      ? String(form.parent_form_no) : headerFormNo(model)
    const parent = entries.find(e => e.formNo === parentNo)?.sm || entries[0]?.sm || null
    return (
      <LineCommandPanel
        model={model}
        formIndex={fi}
        parentSm={parent}
        project={projectId}
        commandIndex={selectedNode.actionIndex}
        onModelChange={onModelChange}
        onSelectCommand={(i) => onSelectNode?.({ ...selectedNode, actionIndex: i })}
      />
    )
  }

  if (selectedNode.type === 'form-cross-updates') {
    const form = getForm(selectedNode.formIndex)
    if (!form) return null
    return renderTxnHookList(
      'cross_updates', 'Cross Updates',
      `Runs once for EVERY saved row of this form, inside the save transaction. Use it to maintain a related table per row — reduce stock for each line, post a ledger entry, update an outstanding quantity. Returning an error rolls the WHOLE save back (header, all lines, and any cross-updates already applied for earlier rows), with the error pointing at the offending row. The function receives that row's columns flat, plus action, txn_id and header.`,
      'Cross Update', selectedNode.formIndex,
    )
  }

  if (selectedNode.type === 'form-validations') {
    const form = getForm(selectedNode.formIndex)
    if (!form) return null
    const fi = selectedNode.formIndex!

    return (
      <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
        <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 12, color: 'var(--color-text)' }}>
          Validations
        </div>
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>On this form</div>
          {(Array.isArray(form.validations) ? form.validations : []).map((v, vi) => (
            <div key={vi} style={listItemStyle}>
              <span style={{ flex: 1, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: (isValidationRule(v) && v.disabled) ? 0.55 : 1 }}>{validationSummary(v)}</span>
              {isValidationRule(v) && (
                <button title={v.disabled ? 'Enable validation' : 'Disable validation (skip at save)'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: v.disabled ? 'var(--color-text-secondary)' : 'var(--color-accent)', padding: 2 }}
                  onClick={() => {
                    const m = cloneModel(model)
                    const cur = m.sql_models[fi].sql_model.validations[vi]
                    if (isValidationRule(cur)) cur.disabled = !cur.disabled
                    onModelChange(m)
                  }}>
                  <Power size={13} />
                </button>
              )}
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', padding: 2 }}
                onClick={() => {
                  const formColumns = form.columns.map(c => c.column?.name || '').filter(Boolean)
                  openValidationRuleEditor(v, formColumns, parentColumnNames(model, fi), (newVal) => {
                    const m = cloneModel(model)
                    m.sql_models[fi].sql_model.validations[vi] = newVal
                    onModelChange(m)
                  })
                }}>
                <Edit3 size={13} />
              </button>
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 2 }}
                onClick={() => {
                  const m = cloneModel(model)
                  m.sql_models[fi].sql_model.validations.splice(vi, 1)
                  onModelChange(m)
                }}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <button style={addBtnStyle} onClick={() => {
            const formColumns = form.columns.map(c => c.column?.name || '').filter(Boolean)
            openValidationRuleEditor('', formColumns, parentColumnNames(model, fi), (v) => {
              const m = cloneModel(model)
              if (!m.sql_models[fi].sql_model.validations) {
                m.sql_models[fi].sql_model.validations = []
              }
              m.sql_models[fi].sql_model.validations.push(v)
              onModelChange(m)
            })
          }}>
            <Plus size={12} /> Add Validation
          </button>
        </div>

        <ValidationRuleEditor
          open={ruleValidationOpen}
          onClose={() => setRuleValidationOpen(false)}
          value={editingRuleValidation?.value ?? ''}
          formColumnNames={editingRuleValidation?.formColumns || []}
          headerColumnNames={editingRuleValidation?.headerColumns || []}
          formList={formList}
          project={projectId}
          onSave={(v) => editingRuleValidation?.onSave(v)}
          onOpenCode={onOpenCode}
        />
      </div>
    )
  }

  // ---- FORM JOINS ----
  if (selectedNode.type === 'form-joins') {
    const form = getForm(selectedNode.formIndex)
    if (!form) return null
    return (
      <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
        {renderJoinsEditor(selectedNode.formIndex!)}
      </div>
    )
  }


  // ---- FIELD EVENTS (columns of this form that react to a change) ----
  if (selectedNode.type === 'form-field-events') {
    const form = getForm(selectedNode.formIndex)
    if (!form) return null
    const fi = selectedNode.formIndex!
    const cols = (form.columns || []).map((w, ci) => ({ c: w.column, ci }))
    const withIc = cols.filter(x => x.c && normalizeItemChange(x.c.item_change))
    const without = cols.filter(x => x.c && !normalizeItemChange(x.c.item_change))
    return (
      <div style={{ padding: '12px 14px', overflow: 'auto', height: '100%' }}>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
          What happens when a field on this form changes — a Rule that fills other fields, a Code
          function, or a Call API. Each is edited on its column (Behaviour › When it changes).
        </p>
        {withIc.length === 0 && (
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', fontStyle: 'italic', marginBottom: 8 }}>
            No field reacts to a change yet.
          </div>
        )}
        {withIc.map(({ c, ci }) => (
          <button key={ci} type="button" style={inspectorRowStyle}
            onClick={() => onSelectNode?.({ type: 'column', formIndex: fi, columnIndex: ci, label: c.name, tab: 'behaviour' })}>
            <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: 500 }}>When <code>{c.name}</code> changes</span>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {itemChangeSummary(c.item_change).replace(/^Business Logic/, 'Code')}
              </span>
            </span>
            <ChevronRight size={14} style={{ color: 'var(--color-text-faint)' }} />
          </button>
        ))}
        {without.length > 0 && (
          <>
            <label style={fieldLabelStyle}>+ Add a field event on</label>
            <select style={inputStyle} value="" onChange={e => {
              const ci = Number(e.target.value)
              const c = form.columns[ci]?.column
              if (c) onSelectNode?.({ type: 'column', formIndex: fi, columnIndex: ci, label: c.name, tab: 'behaviour' })
            }}>
              <option value="">Choose a column…</option>
              {without.map(({ c, ci }) => <option key={ci} value={ci}>{c.heading || c.name} ({c.name})</option>)}
            </select>
          </>
        )}
      </div>
    )
  }

  // ---- ROW EVENTS (row add / row edit / row finished) ----
  if (selectedNode.type === 'form-row-events') {
    const form = getForm(selectedNode.formIndex)
    if (!form) return null
    const fi = selectedNode.formIndex!
    return (
      <div style={{ padding: '12px 14px', overflow: 'auto', height: '100%' }}>
        <div style={sectionStyle}>
          <div style={{ ...fieldLabelStyle, marginTop: 0, fontStyle: 'italic' }}>
            Set field values when a row is added or opened for edit — the same
            Rule / Code definition as a column&apos;s When it changes, run for the
            whole row.
          </div>
          <label style={fieldLabelStyle}>When a row is added</label>
          {renderRowEvent(fi, form, 'row_add')}

          <label style={fieldLabelStyle}>When a row is opened for edit</label>
          {renderRowEvent(fi, form, 'row_edit')}

          <label style={fieldLabelStyle}>When the row is finished</label>
          <div style={{ ...fieldLabelStyle, marginTop: 0, fontStyle: 'italic' }}>
            Runs when the user finishes a row (it passed its validation) — e.g. build
            the taxes of a sale line. Can rebuild a child form and set values on
            this row and the header.
          </div>
          {renderRowComplete(fi, form)}
        </div>
        {renderIcEditors()}
      </div>
    )
  }

  // ---- WHEN SAVING (pre-commit, post-commit, legacy record cross updates) ----
  if (selectedNode.type === 'record-saving') {
    return (
      <div style={{ overflow: 'auto', height: '100%' }}>
        {renderTxnHookList('pre_commit', 'Before saving (pre-commit)',
          'Runs after every row is staged, just before the save is committed. A hook can update other tables atomically and/or return an error to roll the WHOLE save back.',
          'Hook', undefined, false)}
        {renderTxnHookList('post_commit', 'After save (post-commit)',
          'Runs after the save has committed, each hook in its own transaction. A failure is reported as a warning but never rolls the save back.',
          'Hook', undefined, false)}
        {renderTxnHookList('cross_updates', 'Cross updates (legacy, record level)',
          'LEGACY placement — runs for every row of EVERY form and only reports a failure. Declare new cross updates on the FORM whose rows they act on.',
          'Cross Update', undefined, false)}
        {renderIcEditors()}
      </div>
    )
  }

  // ---- RECORD SETTINGS (an index into the Settings workspace) ----
  if (selectedNode.type === 'record-settings') {
    const sections = settingsSections(model).filter(sx => sx.key !== 'general')
    return (
      <div style={{ padding: '12px 14px', overflow: 'auto', height: '100%' }}>
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Settings that apply to the whole record. Each opens full width in Settings.
        </p>
        {sections.map(sx => (
          <button key={sx.key} type="button" style={inspectorRowStyle}
            onClick={() => (onOpenSettings ? onOpenSettings(sx.node) : onSelectNode?.(sx.node))}>
            <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: 500 }}>{sx.label}</span>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{sx.detail}</span>
            </span>
            <span style={{
              fontSize: '0.8125rem', fontWeight: 600, borderRadius: 10, padding: '2px 8px',
              background: sx.on ? 'var(--color-success-weak, rgba(36,161,72,.14))' : 'var(--color-surface-2)',
              color: sx.on ? 'var(--color-success, #1a7f37)' : 'var(--color-text-secondary)',
            }}>{sx.state}</span>
          </button>
        ))}
      </div>
    )
  }

  // ---- CROSS UPDATES ----
  if (selectedNode.type === 'cross-updates') {
    return renderTxnHookList(
      'cross_updates', 'Cross Updates',
      'LEGACY placement. These run for every row of EVERY form, and a failure is only reported — the save still commits. Kept working for existing apps; do not add new ones here. Declare a cross-update on the FORM whose rows it acts on (select a form in the tree) — it then fires once per row of that form and rolls the whole save back if it fails, which is what a stock / balance / ledger decrement needs.',
      'Cross Update',
    )
  }

  // ---- PRE-COMMIT LOGIC ----
  if (selectedNode.type === 'pre-commit') {
    return renderTxnHookList(
      'pre_commit', 'Before saving (pre-commit)',
      'Runs after every row is staged, just before the save is committed. A hook can update other tables atomically (PL/pgSQL, Python and Java all join this transaction) and/or return an error to roll the WHOLE save back. Use it for consistency checks and derived-table maintenance — e.g. allocate stock and write an allocation-trace row for each line of a sale.',
    )
  }

  // ---- TRANSACTION COMPLETE (POST-COMMIT) ----
  if (selectedNode.type === 'transaction-complete') {
    return renderTxnHookList(
      'post_commit', 'After save (post-commit)',
      'Runs after the save has committed, each hook in its own transaction. For cleanup or notifications that have no binding on the save — a failure here is reported as a warning but never rolls the save back.',
    )
  }

  // ---- APPROVALS ----
  if (selectedNode.type === 'approvals') {
    return (
      <ApprovalsEditor
        model={model}
        onModelChange={onModelChange}
        project={projectId}
        objName={model.obj_name}
        onGuardedNavigate={onGuardedNavigate}
      />
    )
  }

  // ---- WORK RULES ----
  if (selectedNode.type === 'work-rules') {
    return (
      <WorkRulesEditor
        model={model}
        onModelChange={onModelChange}
        project={projectId}
      />
    )
  }

  // ---- FOLLOWUP ACTIONS ----
  if (selectedNode.type === 'followup-actions') {
    return (
      <FollowupActionsEditor
        model={model}
        onModelChange={onModelChange}
        renderBlApi={renderBlApi}
        renderEditors={renderIcEditors}
      />
    )
  }

  // ---- ATTACHMENTS ----
  if (selectedNode.type === 'attachments') {
    return (
      <SimpleListEditor
        title="Document Attachments"
        items={model.attach_docs || []}
        onChange={(items) => {
          const m = cloneModel(model)
          m.attach_docs = items
          onModelChange(m)
        }}
        placeholder="e.g. Customer Signature"
      />
    )
  }


  // ---- PUBLISH ON WEBSITE (content collections) ----
  if (selectedNode.type === 'website') {
    return (
      <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
          <div style={{ marginTop: 12, padding: 10, borderRadius: 6, border: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: '0.8625rem', fontWeight: 600, marginBottom: 4 }}>Publish on website</div>
            <p style={{ fontSize: '0.8425rem', color: 'var(--color-text-secondary)', margin: '0 0 8px' }}>
              {(model as { content?: { enabled?: boolean; label?: { plural?: string } } }).content?.enabled !== false
                && (model as { content?: unknown }).content
                ? `On — each record publishes as a page of “${(model as { content?: { label?: { plural?: string } } }).content?.label?.plural || model.obj_name}”.`
                : 'Off. Turn a record into a web page — a post, a product, a department — with its own address and search-engine description.'}
            </p>
            {model.content && model.content.enabled !== false && (
              <ContentPanelSettings model={model} onModelChange={onModelChange} />
            )}
            {project && (
              <button
                style={addBtnStyle}
                onClick={() => {
                  const go = () => navigate(`/website-content/${encodeURIComponent(project)}`)
                  if (onGuardedNavigate) onGuardedNavigate(go); else go()
                }}
              >Open Website content</button>
            )}
            {hasWebsite === false && (
              <div style={{ ...tokenWarnStyle, marginTop: 8 }}>
                <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>This application has no website, so these records are not published anywhere. Add a website, or switch publishing off in Website content.</span>
              </div>
            )}
          </div>
      </div>
    )
  }

  // ---- OBJECT ROOT ----
  if (selectedNode.type === 'object') {
    return (
      <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
        <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 12, color: 'var(--color-text)' }}>
          Transaction Object
        </div>
        <div style={sectionStyle}>
          <div style={sectionTitleStyle}>Object Information</div>
          <label style={fieldLabelStyle}>Object Name</label>
          <input style={inputStyle} value={model.obj_name}
            onChange={e => {
              const m = cloneModel(model)
              m.obj_name = e.target.value
              onModelChange(m)
            }} />

          <div style={{ marginTop: 12 }}>
            <p style={{ fontSize: '0.9375rem', color: 'var(--color-text)', margin: '4px 0' }}>
              Forms: {model.sql_models?.length || 0}
            </p>
            <p style={{ fontSize: '0.9375rem', color: 'var(--color-text)', margin: '4px 0' }}>
              Record buttons: {model.actions?.length || 0}
            </p>
            <p style={{ fontSize: '0.9375rem', color: 'var(--color-text)', margin: '4px 0' }}>
              Cross Updates: {model.cross_updates?.length || 0}
            </p>
            <p style={{ fontSize: '0.9375rem', color: 'var(--color-text)', margin: '4px 0' }}>
              Follow-ups: {model.follow_up_actions?.length || 0}
            </p>
            <p style={{ fontSize: '0.9375rem', color: 'var(--color-text)', margin: '4px 0' }}>
              Attachments: {model.attach_docs?.length || 0}
            </p>
          </div>

          <button style={{ ...addBtnStyle, marginTop: 12 }} onClick={() => {
            const m = cloneModel(model)
            m.sql_models.push(makeUnlinkedForm(m))
            onModelChange(m)
          }}>
            <Plus size={12} /> Add an unlinked form (advanced)
          </button>
        </div>
      </div>
    )
  }

  return null
}


// ---- Sub-components ----

// Helper to normalize column/join_column which can be string or string[]
function toArray(v: string | string[] | undefined): string[] {
  if (!v) return []
  if (Array.isArray(v)) return v
  return v ? [v] : []
}

interface JoinFieldsProps {
  join: JoinDef
  onUpdate: (j: JoinDef) => void
}

function JoinFields({ join, onUpdate }: JoinFieldsProps) {
  const cols = toArray(join.column)
  const joinCols = toArray(join.join_column)
  const pairCount = Math.max(cols.length, joinCols.length, 1)

  return (
    <>
      <label style={fieldLabelStyle}>Join Table</label>
      <input style={inputStyle} value={join.join_table || ''}
        onChange={e => onUpdate({ ...join, join_table: e.target.value })} />

      <label style={fieldLabelStyle}>Join Type</label>
      <select style={{ ...inputStyle, cursor: 'pointer' }} value={join.join_type || 'inner'}
        onChange={e => onUpdate({ ...join, join_type: e.target.value as JoinDef['join_type'] })}>
        {JOIN_TYPES.map(t => <option key={t} value={t}>{t.toUpperCase()} JOIN</option>)}
      </select>

      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 4 }}>
          Join Columns (this table / joined table)
        </div>
        {Array.from({ length: pairCount }).map((_, cpi) => (
          <div key={cpi} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
            <input style={{ ...inputStyle, flex: 1 }} value={cols[cpi] || ''}
              onChange={e => {
                const newCols = [...cols]
                newCols[cpi] = e.target.value
                onUpdate({ ...join, column: newCols.length === 1 ? newCols[0] : newCols })
              }} />
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>=</span>
            <input style={{ ...inputStyle, flex: 1 }} value={joinCols[cpi] || ''}
              onChange={e => {
                const newJoinCols = [...joinCols]
                newJoinCols[cpi] = e.target.value
                onUpdate({ ...join, join_column: newJoinCols.length === 1 ? newJoinCols[0] : newJoinCols })
              }} />
            {pairCount > 1 && (
              <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 2 }}
                onClick={() => {
                  const newCols = cols.filter((_, i) => i !== cpi)
                  const newJoinCols = joinCols.filter((_, i) => i !== cpi)
                  onUpdate({
                    ...join,
                    column: newCols.length === 1 ? newCols[0] : newCols.length === 0 ? '' : newCols,
                    join_column: newJoinCols.length === 1 ? newJoinCols[0] : newJoinCols.length === 0 ? '' : newJoinCols,
                  })
                }}>
                <Trash2 size={13} />
              </button>
            )}
          </div>
        ))}
        <button style={addBtnStyle} onClick={() => {
          const newCols = [...cols, '']
          const newJoinCols = [...joinCols, '']
          onUpdate({ ...join, column: newCols, join_column: newJoinCols })
        }}>
          <Plus size={12} /> Add Column Pair
        </button>
      </div>
    </>
  )
}

// ---- Follow-up Actions Editor ----
// A follow-up runs after the save commits (notification / audit / side-effect).
// Each entry carries its trigger event, a display name, and what it does:
//   - Business Logic / Call API — the SAME editor (function name, description,
//     language, functional specification + Open-in-code-editor) as every
//     other surface,
//   - Call Integration Event — emits an event from the project's Integration
//     design (Workbench → Advanced → Events & APIs) to IOFlow. Bindings for
//     events defined there are maintained automatically ("managed").
// Legacy `"event,name:logic"` strings are upgraded on load.

const FOLLOWUP_EVENTS = [
  'on-add',
  'on-edit',
  'on-delete',
  'on-add-edit',
  'on-add-delete',
  'on-edit-delete',
  'on-add-edit-delete',
] as const

// A follow-up's business-logic / Call-API definition as an ItemChange, for the
// shared renderBlApi control. Returns null when nothing is configured yet.
function followupToIc(def: FollowupActionDef): ItemChange | null {
  if (def.business_logic) return { mode: 'business_logic', business_logic: def.business_logic }
  if (def.rest_api) return { mode: 'rest_api', rest_api: def.rest_api }
  return null
}

// Apply an edited ItemChange back onto the follow-up def (clearing the legacy
// string + the other modes, including an integration-event binding).
function icToFollowupPatch(ic: ItemChange): Partial<FollowupActionDef> {
  if (ic.mode === 'business_logic') return { business_logic: ic.business_logic, rest_api: undefined, logic: undefined, ioflow_event: undefined, managed: undefined, send: undefined, wizard: undefined }
  if (ic.mode === 'rest_api') return { rest_api: ic.rest_api, business_logic: undefined, logic: undefined, ioflow_event: undefined, managed: undefined, send: undefined, wizard: undefined }
  return {}
}

interface FollowupActionsEditorProps {
  model: TransactionModel
  onModelChange: (model: TransactionModel) => void
  renderBlApi: (
    raw: ItemChange | string | null | undefined,
    onChange: (ic: ItemChange) => void,
    formColumns?: string[],
    headerColumns?: string[],
  ) => React.ReactNode
  // The shared Item-Change Rule / Business-Logic modal editors — must be
  // mounted here so the BL control's "edit" can open its dialog.
  renderEditors: () => React.ReactNode
}

function FollowupActionsEditor({ model, onModelChange, renderBlApi, renderEditors }: FollowupActionsEditorProps) {
  // Normalise every entry (legacy string or partial def) to a structured def.
  const defs = (model.follow_up_actions || []).map(normalizeFollowupAction)

  // Integration events defined for this project (for the "Call Integration
  // Event" kind). Loaded once per mount; empty when none are defined.
  const { currentProject } = useProjectStore()
  const folder = currentProject?.application.folder_name || ''
  const [integrationEvents, setIntegrationEvents] = useState<IntegrationEvent[] | null>(null)
  useEffect(() => {
    let cancelled = false
    if (!folder) { setIntegrationEvents([]); return }
    getIntegrationDesign(folder)
      .then((d) => { if (!cancelled) setIntegrationEvents(d.events || []) })
      .catch(() => { if (!cancelled) setIntegrationEvents([]) })
    return () => { cancelled = true }
  }, [folder])

  const setDefs = (next: FollowupActionDef[]) => {
    const m = cloneModel(model)
    m.follow_up_actions = next
    onModelChange(m)
  }

  const patchItem = (index: number, patch: Partial<FollowupActionDef>) =>
    setDefs(defs.map((d, i) => (i === index ? { ...d, ...patch } : d)))

  // For "Send message" follow-ups: this transaction's buttons (the
  // "After action ▸" triggers), its header columns and its object name.
  const buttonNames = useMemo(() => {
    const out: string[] = []
    const add = (acts: unknown) => {
      for (const a of (Array.isArray(acts) ? acts : [])) {
        if (!a || typeof a !== 'object') continue
        const d = a as ActionDef
        if (d.handler === 'system' && d.system_op !== 'delete') continue
        if (d.name && !out.includes(d.name)) out.push(d.name)
      }
    }
    add((model as unknown as { actions?: unknown }).actions)
    for (const { sm } of allSqlModels(model)) add((sm as unknown as { actions?: unknown }).actions)
    return out
  }, [model])
  const headerColumnNames = useMemo(() => {
    const first = allSqlModels(model)[0]
    return (first?.sm.columns || []).map(w => w.column).filter(Boolean)
      .map(c => (c.name || c.db_name || '').trim()).filter(Boolean)
  }, [model])
  const sendObjName = (model as unknown as { obj_name?: string }).obj_name || ''

  return (
    <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
      <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 12, color: 'var(--color-text)' }}>
        Follow-ups
      </div>
      <div style={sectionStyle}>
        <div style={{ ...sectionTitleStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Actions ({defs.length})</span>
          <button
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-accent)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 4,
            }}
            onClick={() => setDefs([...defs, { event: 'on-add', name: '', business_logic: { method: '', description: '' } }])}
          >
            <Plus size={12} /> Add Action
          </button>
        </div>
        {defs.map((def, i) => {
          const kind: 'ioflow_event' | 'logic' | 'send' | 'wizard' = def.wizard ? 'wizard' : def.send ? 'send' : def.ioflow_event ? 'ioflow_event' : 'logic'
          const boundEvent = def.ioflow_event?.event_name || ''
          const knownEvent = (integrationEvents || []).some(
            (e) => e.event_name.toLowerCase() === boundEvent.toLowerCase(),
          )
          return (
            <div key={i} style={{
              padding: 8, background: 'var(--color-bg)', borderRadius: 4, marginBottom: 8,
              border: '1px solid var(--color-border)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <select
                  style={{ ...inputStyle, flex: '0 0 auto', width: 'auto', cursor: 'pointer' }}
                  value={def.event}
                  onChange={e => patchItem(i, { event: e.target.value })}
                >
                  {FOLLOWUP_EVENTS.map(ev => <option key={ev} value={ev}>{ev}</option>)}
                  {/* "After action ▸ <button>" — runs once that button really
                      ran (not on its confirmation prompt, not while parked for
                      approval). Send follow-ups use it; a save-trigger kind
                      ignores it. */}
                  {buttonNames.length > 0 && (
                    <optgroup label="After action ▸">
                      {buttonNames.map(b => <option key={b} value={`on-action:${b}`}>after {b}</option>)}
                    </optgroup>
                  )}
                  {def.event.startsWith('on-action:') && !buttonNames.includes(def.event.slice('on-action:'.length)) && (
                    <option value={def.event}>{def.event} (no such button)</option>
                  )}
                </select>
                <select
                  style={{ ...inputStyle, flex: '0 0 auto', width: 'auto', cursor: 'pointer' }}
                  value={kind}
                  title="What this follow-up does"
                  onChange={e => {
                    if (e.target.value === 'wizard') {
                      patchItem(i, {
                        wizard: def.wizard || { obj_name: '', param_map: {} },
                        ioflow_event: undefined, managed: undefined, send: undefined,
                        business_logic: undefined, rest_api: undefined, logic: undefined,
                      })
                    } else if (e.target.value === 'ioflow_event') {
                      patchItem(i, {
                        ioflow_event: { event_name: boundEvent },
                        business_logic: undefined, rest_api: undefined, logic: undefined, send: undefined, wizard: undefined,
                      })
                    } else if (e.target.value === 'send') {
                      patchItem(i, {
                        send: def.send || { template: '', channels: ['email'], grouping: 'per_record' },
                        ioflow_event: undefined, managed: undefined, wizard: undefined,
                        business_logic: undefined, rest_api: undefined, logic: undefined,
                      })
                    } else {
                      patchItem(i, {
                        ioflow_event: undefined, managed: undefined, send: undefined, wizard: undefined,
                        business_logic: def.business_logic || { method: '', description: '' },
                      })
                    }
                  }}
                >
                  <option value="logic">Business Logic / Call API</option>
                  <option value="ioflow_event">Call Integration Event</option>
                  <option value="send">Send message</option>
                  <option value="wizard">Run a wizard (unattended)</option>
                </select>
                {def.managed && (
                  <span
                    style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}
                    title="Maintained by the Integration design (Workbench → Advanced → Events & APIs). It is re-created on the event's source object whenever the design is saved."
                  >
                    managed
                  </span>
                )}
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 2, flexShrink: 0, marginLeft: 'auto' }}
                  title="Delete action"
                  onClick={() => setDefs(defs.filter((_, idx) => idx !== i))}>
                  <Trash2 size={13} />
                </button>
              </div>
              <label style={fieldLabelStyle}>Action Name</label>
              <input
                type="text"
                style={inputStyle}
                value={def.name}
                placeholder="Action Name"
                onChange={e => patchItem(i, { name: e.target.value })}
              />
              {kind === 'wizard' ? (
                <FollowupWizardFields value={def.wizard} onChange={(wizard) => patchItem(i, { wizard })} />
              ) : kind === 'send' ? (
                <>
                  <label style={fieldLabelStyle}>Send message</label>
                  <SendActionEditor
                    binding={def.send}
                    onChange={(send) => patchItem(i, { send })}
                    project={folder}
                    objName={sendObjName}
                    headerColumns={headerColumnNames}
                    mode="followup"
                    buttonNames={buttonNames}
                  />
                </>
              ) : kind === 'ioflow_event' ? (
                <>
                  <label style={fieldLabelStyle}>Integration Event (sent to IOFlow)</label>
                  <select
                    style={{ ...inputStyle, cursor: 'pointer' }}
                    value={knownEvent ? boundEvent : ''}
                    onChange={e => patchItem(i, { ioflow_event: { event_name: e.target.value } })}
                  >
                    <option value="">
                      {integrationEvents === null
                        ? 'Loading events…'
                        : integrationEvents.length === 0
                          ? 'No events defined (Workbench → Advanced → Events & APIs)'
                          : '— select event —'}
                    </option>
                    {(integrationEvents || []).map(e => (
                      <option key={e.event_name} value={e.event_name}>
                        {e.display_name ? `${e.display_name} (${e.event_name})` : e.event_name}
                      </option>
                    ))}
                  </select>
                  {boundEvent && integrationEvents !== null && !knownEvent && (
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-error, #cf222e)', marginTop: 4 }}>
                      Event "{boundEvent}" is not in the Integration design — this follow-up
                      will be skipped at runtime.
                    </div>
                  )}
                </>
              ) : (
                <>
                  <label style={fieldLabelStyle}>Business Logic</label>
                  {renderBlApi(
                    followupToIc(def),
                    (ic) => patchItem(i, icToFollowupPatch(ic)),
                  )}
                </>
              )}
            </div>
          )
        })}
      </div>
      {renderEditors()}
    </div>
  )
}

// A `wizard` follow-up: which wizard runs (unattended, on the server) and its
// launch inputs from the saved record ({header.COL}). The wizard's own Check
// says whether every step it can reach needs no person (NOT_UNATTENDED).
function FollowupWizardFields({ value, onChange }: {
  value: FollowupActionDef['wizard']
  onChange: (v: FollowupActionDef['wizard']) => void
}) {
  const v = value || { obj_name: '' }
  const entries = Object.entries(v.param_map || {})
  const setMap = (next: Array<[string, unknown]>) => onChange({ ...v, param_map: Object.fromEntries(next) })
  return (
    <>
      <label style={fieldLabelStyle}>Wizard (obj_name)</label>
      <input type="text" style={inputStyle} value={v.obj_name} placeholder="gst_einvoice"
        onChange={e => onChange({ ...v, obj_name: e.target.value })} />
      <label style={fieldLabelStyle}>Inputs (wizard input → {'{header.COL}'} or a literal)</label>
      {entries.map(([k, val], j) => (
        <div key={j} style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
          <input type="text" style={{ ...inputStyle, flex: '0 0 40%' }} value={k} placeholder="input"
            onChange={e => setMap(entries.map((x, n) => (n === j ? [e.target.value, x[1]] : x)))} />
          <input type="text" style={inputStyle} value={String(val ?? '')} placeholder="{header.BRANCH}"
            onChange={e => setMap(entries.map((x, n) => (n === j ? [x[0], e.target.value] : x)))} />
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)' }}
            title="Remove" onClick={() => setMap(entries.filter((_, n) => n !== j))}><Trash2 size={13} /></button>
        </div>
      ))}
      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', fontSize: '0.8125rem', padding: 0 }}
        onClick={() => setMap([...entries, ['', '']])}>
        <Plus size={12} /> Add input
      </button>
      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
        Runs on the server right after the save or the button, as the user who did it — it never undoes the
        save; its result is reported as a message. A fill wizard of this transaction writes back onto the record.
      </div>
    </>
  )
}

interface SimpleListEditorProps {
  title: string
  items: string[]
  onChange: (items: string[]) => void
  placeholder?: string
}

function SimpleListEditor({ title, items, onChange, placeholder }: SimpleListEditorProps) {
  return (
    <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
      <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 12, color: 'var(--color-text)' }}>
        {title}
      </div>
      <div style={sectionStyle}>
        <div style={{ ...sectionTitleStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Items ({items.length})</span>
          <button
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-accent)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 4,
            }}
            onClick={() => onChange([...items, ''])}
          >
            <Plus size={12} /> Add Item
          </button>
        </div>
        {items.map((item, i) => (
          <div key={i} style={{
            display: 'flex', gap: 6, marginBottom: 6, alignItems: 'flex-start',
          }}>
            <textarea
              style={{
                ...inputStyle, flex: 1, minHeight: 40, resize: 'vertical',
                fontFamily: 'monospace', fontSize: '0.875rem',
              }}
              value={item}
              placeholder={placeholder}
              onChange={e => {
                const updated = [...items]
                updated[i] = e.target.value
                onChange(updated)
              }}
            />
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 4, flexShrink: 0, marginTop: 4 }}
              title="Delete item"
              onClick={() => onChange(items.filter((_, idx) => idx !== i))}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

interface ActionsListEditorProps {
  title: string
  actions: Action[]
  onChange: (actions: Action[]) => void
  // Shared Business-Logic / Call-API control (function name, description,
  // language, functional specification + Open-in-code-editor).
  renderBlApi: (
    raw: ItemChange | string | null | undefined,
    onChange: (ic: ItemChange) => void,
    formColumns?: string[],
    headerColumns?: string[],
  ) => React.ReactNode
  // The shared Item-Change Rule / Business-Logic modal editors — mounted here so
  // the BL control's "edit" pencil can open its dialog.
  renderEditors: () => React.ReactNode
  focusActionIndex?: number
  // focusKey changes whenever a fresh focus request comes in (even if the
  // index repeats), so the auto-scroll effect re-fires.
  focusKey?: string
  onOpenCode?: (method?: string) => void
  // PRE-action validations reuse the SAME structured editor as form-level
  // validations. The host mounts ValidationRuleEditor below and routes the
  // open request / save through this state (shared with the form-validations
  // branch).
  openValidationRuleEditor: (value: Validation, formColumns: string[], headerColumns: string[], onSave: (v: Validation) => void) => void
  ruleValidationOpen: boolean
  setRuleValidationOpen: (open: boolean) => void
  editingRuleValidation: { value: Validation; formColumns: string[]; headerColumns: string[]; onSave: (v: Validation) => void } | null
  // Columns offered to the validation editor's {form.X}/{header.X} pickers.
  formColumns: string[]
  headerColumns: string[]
  formList: Array<{ form_no: string; title: string }>
  project: string
  // Wraps in-app navigation so unsaved transaction changes prompt to save
  // first — used by the report action's "Edit in Report Designer" jump.
  onGuardedNavigate?: (action: () => void) => void
  // select_grid → a Line Entry source of kind business_logic on its target
  // line form (the function is reused unchanged; the action is removed).
  onConvertToLineSource?: (index: number) => void
  // The transaction being designed — a 'wizard' button offers its wizards first.
  sourceObjName?: string
}

const ACTION_HANDLER_OPTIONS: ActionHandlerKind[] =
  ['system', 'message', 'item_change', 'html_modal', 'select_grid', 'report',
   'open', 'wizard', 'assistant', 'payment', 'import', 'send']
const ACTION_SYSTEM_OPS: Array<NonNullable<ActionDef['system_op']>> =
  ['add', 'edit', 'delete', 'save', 'view']

function ActionsListEditor({
  title, actions, onChange, renderBlApi, renderEditors, focusActionIndex, focusKey, onOpenCode,
  openValidationRuleEditor, ruleValidationOpen, setRuleValidationOpen,
  editingRuleValidation, formColumns, headerColumns, formList, project, onGuardedNavigate,
  onConvertToLineSource, sourceObjName,
}: ActionsListEditorProps) {
  const navigate = useNavigate()
  // Every action is normalised to a structured ActionDef for editing; legacy
  // "name:logic" strings upgrade on first edit. ``defs`` mirrors ``actions``.
  const defs = useMemo(() => actions.map(legacyActionToDef), [actions])

  // Report-existence probe — each report action's bound R object is checked
  // against the project (load-report-object returns content:null when the
  // model file is missing). A missing report shows a notice and a Create
  // button that scaffolds the blank model + starter JRXML and jumps to the
  // Report Designer. 'unknown' = probe failed; show nothing rather than a
  // wrong verdict.
  const [reportStatus, setReportStatus] = useState<Record<string, 'checking' | 'exists' | 'missing' | 'unknown'>>({})
  const [creatingReport, setCreatingReport] = useState<string | null>(null)
  const reportNames = useMemo(
    () => Array.from(new Set(
      defs.filter(d => d.handler === 'report')
        .map(d => (d.report?.obj_name || '').trim())
        .filter(Boolean),
    )),
    [defs],
  )
  useEffect(() => {
    if (!project) return
    const unchecked = reportNames.filter(n => reportStatus[n] === undefined)
    if (!unchecked.length) return
    let cancelled = false
    // Debounced so half-typed names aren't probed on every keystroke.
    const t = setTimeout(() => {
      if (cancelled) return
      setReportStatus(prev => ({
        ...prev,
        ...Object.fromEntries(unchecked.map(n => [n, 'checking' as const])),
      }))
      unchecked.forEach(name => {
        loadReportObject(project, name)
          .then(res => {
            if (!cancelled) setReportStatus(prev => ({ ...prev, [name]: res.content ? 'exists' : 'missing' }))
          })
          .catch(() => {
            if (!cancelled) setReportStatus(prev => ({ ...prev, [name]: 'unknown' }))
          })
      })
    }, 600)
    return () => { cancelled = true; clearTimeout(t) }
  }, [project, reportNames, reportStatus])

  const openReportDesigner = useCallback((objName: string) => {
    if (!project) return
    const go = () => navigate(
      `/designers/report/${encodeURIComponent(project)}/${encodeURIComponent(objName)}`,
    )
    if (onGuardedNavigate) onGuardedNavigate(go)
    else go()
  }, [project, navigate, onGuardedNavigate])

  // Scaffold a blank report (same starting model + JRXML the Report Designer
  // itself uses for a new object) and open it for manual design.
  const createReportAndOpen = useCallback((objName: string) => {
    if (!project || creatingReport) return
    setCreatingReport(objName)
    saveReportObject(project, objName, {
      model: blankReportModel(objName),
      jrxml_files: { 'report.jrxml': defaultReportJrxml(objName) },
    })
      .then(() => {
        setReportStatus(prev => ({ ...prev, [objName]: 'exists' }))
        toast.success(`Report "${objName}" created`)
        openReportDesigner(objName)
      })
      .catch((err) => {
        console.error('Failed to create report object', err)
        const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        toast.error(msg || `Failed to create report "${objName}"`)
      })
      .finally(() => setCreatingReport(null))
  }, [project, creatingReport, openReportDesigner])
  const defsRef = useRef(defs)
  defsRef.current = defs
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({})

  // Drag-to-reorder. Dragging is armed by mousedown on the grip handle so the
  // card's inputs keep normal text selection. While dragging, ``dropTarget``
  // marks the insertion edge (before/after the hovered card) with an accent
  // bar — mirroring the field drag-reorder on the preview canvas.
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [dragArmed, setDragArmed] = useState<number | null>(null)
  const [dropTarget, setDropTarget] = useState<{ index: number; before: boolean } | null>(null)

  // Cards render collapsed (header row only) so the list stays compact and
  // easy to drag-reorder; clicking a header expands that action's editor.
  // Keyed by index — remapped on delete/reorder so open cards follow their
  // action.
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})
  const remapExpanded = (map: (idx: number) => number | null) => {
    setExpanded(prev => {
      const next: Record<number, boolean> = {}
      for (const [k, open] of Object.entries(prev)) {
        if (!open) continue
        const nk = map(Number(k))
        if (nk !== null) next[nk] = true
      }
      return next
    })
  }

  // Stamp the stored 1-based render position on every list mutation so the
  // model always carries an explicit order for the runtimes to sort by.
  const stampOrder = (list: ActionDef[]): ActionDef[] =>
    list.map((d, i) => ({ ...d, order: i + 1 }))

  const endDrag = () => {
    setDragIdx(null)
    setDragArmed(null)
    setDropTarget(null)
  }

  const dropAction = (target: number, before: boolean) => {
    const from = dragIdx
    endDrag()
    if (from === null) return
    const insertAt = before ? target : target + 1
    if (insertAt === from || insertAt === from + 1) return // no-op move
    const next = [...defsRef.current]
    const [moved] = next.splice(from, 1)
    const to = insertAt > from ? insertAt - 1 : insertAt
    next.splice(to, 0, moved)
    remapExpanded(idx => {
      if (idx === from) return to
      let n = idx > from ? idx - 1 : idx
      if (n >= to) n += 1
      return n
    })
    onChange(stampOrder(next))
  }

  const patchAction = (index: number, patch: Partial<ActionDef>) => {
    const next: Action[] = defsRef.current.map((d, i) =>
      i === index ? { ...d, ...patch } : d)
    onChange(next)
  }

  // An action's business logic as an ItemChange, for the shared renderBlApi
  // control; and the patch to apply an edited ItemChange back onto the action.
  const actionToIc = (def: ActionDef): ItemChange | null => {
    if (def.business_logic) return { mode: 'business_logic', business_logic: def.business_logic }
    if (def.rest_api) return { mode: 'rest_api', rest_api: def.rest_api }
    return null
  }
  const icToActionPatch = (ic: ItemChange): Partial<ActionDef> => {
    if (ic.mode === 'business_logic') return { business_logic: ic.business_logic, rest_api: undefined, logic: undefined }
    if (ic.mode === 'rest_api') return { rest_api: ic.rest_api, business_logic: undefined, logic: undefined }
    return {}
  }

  // When a specific action is focused (clicked from the preview), expand its
  // card and scroll to it.
  useEffect(() => {
    if (focusActionIndex === undefined || focusActionIndex < 0) return
    setExpanded(prev => prev[focusActionIndex] ? prev : { ...prev, [focusActionIndex]: true })
    const item = itemRefs.current[focusActionIndex]
    if (item) item.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey])

  return (
    <div style={{ padding: '8px 12px', overflow: 'auto', height: '100%' }}>
      <div style={{ fontSize: '0.9125rem', fontWeight: 600, marginBottom: 12, color: 'var(--color-text)' }}>
        {title}
      </div>
      <div style={sectionStyle}>
        <div style={{ ...sectionTitleStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Actions ({defs.length})</span>
          <button
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--color-accent)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 4,
            }}
            onClick={() => {
              // New action opens expanded so the name can be typed right away.
              setExpanded(prev => ({ ...prev, [defs.length]: true }))
              onChange(stampOrder([
                ...defs,
                { name: '', handler: 'item_change', business_logic: { method: '', description: '' }, validations: [] },
              ]))
            }}
          >
            <Plus size={12} /> Add Action
          </button>
        </div>
        {defs.map((def, i) => {
          const isFocused = focusActionIndex === i
          const isOpen = !!expanded[i]
          const nameMissing = !(def.name || '').trim()
          const vlist = Array.isArray(def.validations) ? def.validations : []
          return (
            <div
              key={i}
              ref={el => { itemRefs.current[i] = el }}
              draggable={dragArmed === i}
              onMouseUp={() => setDragArmed(null)}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move'
                setDragIdx(i)
              }}
              onDragEnd={endDrag}
              onDragOver={(e) => {
                if (dragIdx === null) return
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                const before = (e.clientY - rect.top) < rect.height / 2
                setDropTarget(prev =>
                  prev?.index === i && prev.before === before ? prev : { index: i, before })
              }}
              onDragLeave={() => {
                setDropTarget(prev => (prev?.index === i ? null : prev))
              }}
              onDrop={(e) => {
                e.preventDefault()
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                dropAction(i, (e.clientY - rect.top) < rect.height / 2)
              }}
              style={{
                padding: 8, background: 'var(--color-bg)', borderRadius: 4, marginBottom: 8,
                border: isFocused ? '1px solid var(--color-accent)' : '1px solid var(--color-border)',
                boxShadow: isFocused ? '0 0 0 2px var(--color-accent-weak)' : 'none',
                opacity: dragIdx === i ? 0.45 : 1,
                position: 'relative',
              }}>
              {/* Insertion indicator: an accent bar on the edge where the
                  dragged action will land. */}
              {dropTarget?.index === i && dragIdx !== null && dragIdx !== i && (
                <div
                  aria-hidden
                  style={{
                    position: 'absolute', left: -2, right: -2, height: 3,
                    top: dropTarget.before ? -6 : 'auto',
                    bottom: dropTarget.before ? 'auto' : -6,
                    background: 'var(--color-accent)', borderRadius: 2,
                    pointerEvents: 'none',
                  }}
                />
              )}
              {/* Header row — always visible; click toggles the editor body.
                  Kept to a single compact line so collapsed cards are easy to
                  drag-reorder. */}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                onClick={() => setExpanded(prev => ({ ...prev, [i]: !isOpen }))}
              >
                <span
                  title="Drag to reorder"
                  onMouseDown={() => setDragArmed(i)}
                  onMouseUp={() => setDragArmed(null)}
                  onClick={e => e.stopPropagation()}
                  style={{
                    display: 'flex', alignItems: 'center', cursor: 'grab',
                    color: 'var(--color-text-secondary)', flexShrink: 0,
                  }}>
                  <GripVertical size={14} />
                </span>
                <ChevronDown
                  size={13}
                  style={{
                    flexShrink: 0, color: 'var(--color-text-secondary)',
                    transform: isOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s',
                  }}
                />
                <span style={{
                  flex: 1, fontWeight: 600, fontSize: '0.8425rem',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  color: nameMissing ? '#ef5350' : 'var(--color-text)',
                }}>
                  {(def.name || '').trim() || 'Unnamed action'}
                </span>
                {!isOpen && (
                  <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', flexShrink: 0 }}>
                    {def.handler === 'system'
                      ? `system: ${def.system_op || 'add'}`
                      : def.handler === 'report'
                        ? `report: ${def.report?.obj_name || '(unset)'}`
                        : def.handler === 'assistant'
                          ? `assistant: ${def.assistant?.mode === 'prompt' ? 'AI prompt' : (def.assistant?.intent_id || '(unset)')}`
                          : def.handler === 'payment'
                            ? `payment: ${def.payment?.amount_column || '(no amount column)'}`
                            : def.handler === 'import'
                              ? `import: ${def.import?.mode || 'upsert'}`
                              : def.handler === 'open'
                                ? `open: ${def.open?.obj_name || '(unset)'}`
                                : def.handler === 'wizard'
                                  ? `wizard: ${def.wizard?.obj_name || '(unset)'}`
                                  : def.handler === 'send'
                                    ? `send: ${def.send?.default_template || '—'}`
                                    : ACTION_HANDLER_LABELS[def.handler]}
                  </span>
                )}
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 2, flexShrink: 0 }}
                  title="Delete action"
                  onClick={(e) => {
                    e.stopPropagation()
                    remapExpanded(idx => idx === i ? null : idx > i ? idx - 1 : idx)
                    onChange(stampOrder(defs.filter((_, idx) => idx !== i)))
                  }}>
                  <Trash2 size={13} />
                </button>
              </div>

              {isOpen && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ marginBottom: 6 }}>
                    <input
                      type="text"
                      required
                      aria-invalid={nameMissing}
                      style={{
                        ...inputStyle, width: '100%', fontWeight: 600,
                        border: nameMissing ? '1px solid #ef5350' : inputStyle.border,
                      }}
                      value={def.name}
                      placeholder="Action Name (required)"
                      onChange={e => patchAction(i, { name: e.target.value })}
                    />
                    {nameMissing && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-error, #cf222e)', marginTop: 3 }}>
                        Action name is required — unnamed actions are not shown at runtime.
                      </div>
                    )}
                  </div>

                  {/* Handler — what the button does when its validations pass. */}
                  <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>On click</label>
                  <select
                    style={{ ...inputStyle, width: '100%', marginBottom: 6 }}
                    value={def.handler}
                    onChange={e => patchAction(i, { handler: e.target.value as ActionHandlerKind })}
                  >
                    {ACTION_HANDLER_OPTIONS.map(h => (
                      <option key={h} value={h}>{ACTION_HANDLER_LABELS[h]}</option>
                    ))}
                  </select>
                  {def.handler === 'select_grid' && (
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Picked rows go to</label>
                      <select
                        style={{ ...inputStyle, width: '100%', marginBottom: 6 }}
                        value={def.target_form_no || ''}
                        onChange={e => patchAction(i, { target_form_no: e.target.value || undefined })}
                      >
                        <option value="">The first line form</option>
                        {formList.filter((_, fi) => fi > 0).map(f => (
                          <option key={f.form_no} value={f.form_no}>{f.form_no} — {f.title}</option>
                        ))}
                      </select>
                      {onConvertToLineSource && (
                        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', display: 'flex',
                          alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          Prefer a Line Entry source: it runs the line form's own rules and is checked again at save.
                          <button type="button" className="btn btn-ghost btn-xs"
                            disabled={!def.business_logic?.method}
                            title={def.business_logic?.method ? undefined : 'Only a business-logic select grid converts'}
                            onClick={() => onConvertToLineSource(i)}
                            style={{ padding: '2px 8px', fontSize: '0.8125rem', border: '1px solid var(--color-border)', borderRadius: 4 }}>
                            Convert to line source
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Scope — where the button is drawn at runtime. Form-scoped
                      actions render before "Add"; row-scoped actions live in each
                      row's 3-dot menu. "Auto" infers from the action type.
                      Hidden for system add/edit: those merge into the built-in
                      control, so placement isn't configurable. */}
                  {!isMergedSystemAction(def) && (
                    <>
                      <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Applies to</label>
                      <select
                        style={{ ...inputStyle, width: '100%', marginBottom: 6 }}
                        value={def.scope || ''}
                        onChange={e => patchAction(i, { scope: (e.target.value || undefined) as ActionScope | undefined })}
                      >
                        <option value="">Auto — {ACTION_SCOPE_LABELS[actionScope({ ...def, scope: undefined })]} (inferred)</option>
                        <option value="form">{ACTION_SCOPE_LABELS.form}</option>
                        <option value="row">{ACTION_SCOPE_LABELS.row}</option>
                      </select>
                    </>
                  )}

                  {/* Bulk — may this action run against many records ticked in
                      the list at once? On by default; the confirmation (if any)
                      is asked once for the whole selection. A system Delete
                      can run in bulk too, but is OFF until switched on here.
                      Other system ops and select-grid / report / assistant /
                      import handlers are inherently single-record, so the option is
                      hidden for them. */}
                  {def.handler === 'system' && def.system_op === 'delete' && (
                    <label
                      style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6, cursor: 'pointer' }}
                      title="Lets users tick several rows in the list and delete them together. Each row's pre-action validations still run, and the user confirms once for the whole selection."
                    >
                      <input
                        type="checkbox"
                        checked={def.allow_bulk === true}
                        onChange={e => patchAction(i, { allow_bulk: e.target.checked ? true : undefined })}
                      />
                      Allow deleting multiple selected records
                    </label>
                  )}
                  {def.handler !== 'system' && def.handler !== 'select_grid' && def.handler !== 'report' && def.handler !== 'assistant' && def.handler !== 'import' && def.handler !== 'open' && def.handler !== 'wizard' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={def.allow_bulk !== false}
                        onChange={e => patchAction(i, { allow_bulk: e.target.checked ? undefined : false })}
                      />
                      Allow running on multiple selected records (bulk)
                    </label>
                  )}

                  {/* Show-when — a row-scoped action that cannot apply to a
                      record is better left out of its menu than shown and
                      refused. The engine evaluates this per list row (same
                      expression language as a validation) and the row's menu
                      drops the action when it is false. Presentation only:
                      pair it with the matching pre-action validation below,
                      which is what actually refuses a direct call. */}
                  {actionScope(def) === 'row' && !isMergedSystemAction(def) && (
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                        Show in a row&rsquo;s menu only when
                      </label>
                      <input
                        type="text"
                        style={{ ...inputStyle, width: '100%', fontFamily: 'monospace' }}
                        value={def.visible_when || ''}
                        placeholder={'always shown — e.g. {form.status} != "Closed"'}
                        onChange={e => patchAction(i, { visible_when: e.target.value.trim() || undefined })}
                      />
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 3 }}>
                        Hides the button on rows it cannot apply to. It does not
                        block the action &mdash; add the same condition as a
                        pre-action validation below so a direct call is refused too.
                      </div>
                    </div>
                  )}

                  {/* Ask first — a scanned or typed code the user gives
                      before the action runs, posted beside the form values
                      for its business logic (ActionDef.capture). Only for
                      handlers that run business logic. */}
                  {(def.handler === 'item_change' || def.handler === 'message' || def.handler === 'html_modal') && (
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                        Ask the user first for
                      </label>
                      <select
                        style={{ ...inputStyle, width: '100%' }}
                        value={def.capture?.mode || ''}
                        onChange={e => {
                          const mode = e.target.value as 'scan' | 'text' | ''
                          patchAction(i, { capture: mode ? { ...(def.capture || {}), mode } : undefined })
                        }}
                      >
                        <option value="">Nothing</option>
                        <option value="scan">A scanned code (camera or scanner)</option>
                        <option value="text">A typed code</option>
                      </select>
                      {def.capture && (
                        <>
                          <input
                            type="text"
                            style={{ ...inputStyle, width: '100%', marginTop: 4 }}
                            value={def.capture.prompt || ''}
                            placeholder="Question shown — e.g. Scan the asset tag"
                            onChange={e => patchAction(i, { capture: { ...def.capture!, prompt: e.target.value || undefined } })}
                          />
                          <input
                            type="text"
                            style={{ ...inputStyle, width: '100%', marginTop: 4, fontFamily: 'monospace' }}
                            value={def.capture.field || ''}
                            placeholder="Sent to the logic as — CAPTURED_VALUE"
                            onChange={e => patchAction(i, { capture: { ...def.capture!, field: e.target.value.trim() || undefined } })}
                          />
                          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 3 }}>
                            The business logic reads the value under this name and
                            must check it. Cancelling the prompt runs nothing.
                          </div>
                          {def.handler === 'item_change' && def.capture.mode === 'scan' && (
                            <>
                              <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, fontSize: '0.8125rem' }}>
                                <input
                                  type="checkbox"
                                  checked={!!def.capture.loop}
                                  onChange={e => patchAction(i, { capture: { ...def.capture!, loop: e.target.checked || undefined } })}
                                />
                                Scan one line after another (lines toolbar, confirm each)
                              </label>
                              {def.capture.loop && (
                                <input
                                  type="text"
                                  style={{ ...inputStyle, width: '100%', marginTop: 4, fontFamily: 'monospace' }}
                                  value={(def.capture.match || []).join(', ')}
                                  placeholder="Line found by — the form's key columns"
                                  onChange={e => {
                                    const match = e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                                    patchAction(i, { capture: { ...def.capture!, match: match.length ? match : undefined } })
                                  }}
                                />
                              )}
                              {def.capture.loop && (
                                <input
                                  type="text"
                                  style={{ ...inputStyle, width: '100%', marginTop: 4, fontFamily: 'monospace' }}
                                  value={def.capture.pending_while_blank || ''}
                                  placeholder="Finished when every line has — e.g. STATUS"
                                  onChange={e => patchAction(i, { capture: { ...def.capture!, pending_while_blank: e.target.value.trim() || undefined } })}
                                />
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {def.handler === 'system' ? (
                    <>
                      <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Built-in operation</label>
                      <select
                        style={{ ...inputStyle, width: '100%' }}
                        value={def.system_op || 'add'}
                        onChange={e => patchAction(i, { system_op: e.target.value as ActionDef['system_op'] })}
                      >
                        {ACTION_SYSTEM_OPS.map(op => (
                          <option key={op} value={op}>{op}</option>
                        ))}
                      </select>
                      {/* Add / edit already have a built-in control, so the
                          action merges into it instead of drawing a second
                          button — explain what the user will actually see. */}
                      {isMergedSystemAction(def) && (
                        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                          {(def.system_op || 'add') === 'add'
                            ? <>No separate button is added — the built-in <strong>Add</strong> button takes this action's name as its label, and the pre-action validations below run before the add form opens.</>
                            : (def.system_op === 'view')
                              ? <>No separate button is added — this action's name becomes the built-in <strong>View</strong> control's mouse-over text (shown when a record is read-only), and the pre-action validations below run before the record opens for viewing. The <strong>Edit</strong> action's conditions do NOT apply when viewing.</>
                              : <>No separate button is added — this action's name becomes the built-in <strong>Edit</strong> control's mouse-over text, and the pre-action validations below decide whether the record opens for edit. A record that fails one of them is shown as <strong>view-only</strong> (the eye / View control, with the rule's message as its mouse-over) and opens read-only, rather than offering an Edit that only ever errors. Plain <em>expression</em> rules are worked out for every row up front; a business-logic, REST or master-lookup rule can only refuse on the click, and the record then turns view-only at that point.</>}
                        </div>
                      )}
                      {def.system_op === 'delete' && (
                        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 4 }}>
                          No second Delete is added to the list — the built-in <strong>Delete</strong> in each row's menu takes this action's name, and the pre-action validations below (and any confirmation) run before the record is deleted. The open record's action bar shows it as a button.
                        </div>
                      )}
                    </>
                  ) : def.handler === 'report' ? (
                    <>
                      {/* Report binding — which R object opens, and how its
                          prompt criteria are filled from the clicked row. */}
                      {(() => {
                        const repName = (def.report?.obj_name || '').trim()
                        const repStatus = repName && project ? reportStatus[repName] : undefined
                        const missing = repStatus === 'missing'
                        return (
                          <>
                            <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Report object (obj_type R)</label>
                            <div style={{ display: 'flex', gap: 4, marginBottom: missing ? 2 : 6, alignItems: 'center' }}>
                              <input
                                style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                                value={def.report?.obj_name || ''}
                                placeholder="e.g. sales_invoice_print"
                                onChange={e => patchAction(i, { report: { ...(def.report || { obj_name: '' }), obj_name: e.target.value } })}
                              />
                              {missing ? (
                                // Scaffold the missing report and open it for design.
                                <button
                                  type="button"
                                  title={`Create ${repName} and open it in the Report Designer`}
                                  disabled={creatingReport !== null}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 3, padding: '5px 8px',
                                    background: 'var(--color-bg)', border: '1px solid var(--color-accent)',
                                    borderRadius: 4, fontSize: '0.8125rem', whiteSpace: 'nowrap',
                                    color: 'var(--color-accent)',
                                    cursor: creatingReport !== null ? 'wait' : 'pointer',
                                  }}
                                  onClick={() => createReportAndOpen(repName)}
                                >
                                  <Plus size={11} /> {creatingReport === repName ? 'Creating…' : 'Create'}
                                </button>
                              ) : (
                                // Jump to the Report Designer for the bound R object —
                                // guarded so unsaved transaction edits prompt first.
                                <button
                                  type="button"
                                  title={repName
                                    ? `Edit ${repName} in the Report Designer`
                                    : 'Enter a report object name first'}
                                  disabled={!repName || !project}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 3, padding: '5px 8px',
                                    background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                                    borderRadius: 4, fontSize: '0.8125rem', whiteSpace: 'nowrap',
                                    color: (repName && project) ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                                    cursor: (repName && project) ? 'pointer' : 'not-allowed',
                                  }}
                                  onClick={() => { if (repName) openReportDesigner(repName) }}
                                >
                                  <ExternalLink size={11} /> Edit
                                </button>
                              )}
                            </div>
                            {missing && (
                              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, fontSize: '0.8125rem', color: '#e6a23c', marginBottom: 6 }}>
                                <AlertTriangle size={11} style={{ flexShrink: 0, marginTop: 1 }} />
                                <span>
                                  This report object does not exist yet — <strong>Create</strong> scaffolds
                                  a blank Jasper report and opens it in the Report Designer for you to design.
                                </span>
                              </div>
                            )}
                          </>
                        )
                      })()}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                          Parameters (report prompt ← row value)
                        </span>
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 3 }}
                          onClick={() => {
                            const pm = { ...(def.report?.param_map || {}) }
                            let n = Object.keys(pm).length + 1
                            while (pm[`PARAM_${n}`] !== undefined) n += 1
                            pm[`PARAM_${n}`] = ''
                            patchAction(i, { report: { ...(def.report || { obj_name: '' }), param_map: pm } })
                          }}>
                          <Plus size={11} /> Add
                        </button>
                      </div>
                      {Object.entries(def.report?.param_map || {}).map(([pname, pval], pi) => (
                        <div key={pi} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
                          <input
                            style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                            value={pname}
                            placeholder="Prompt name"
                            onChange={e => {
                              const entries = Object.entries(def.report?.param_map || {})
                              entries[pi] = [e.target.value, pval]
                              patchAction(i, { report: { ...(def.report || { obj_name: '' }), param_map: Object.fromEntries(entries) } })
                            }}
                          />
                          <input
                            style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                            value={String(pval ?? '')}
                            placeholder="{form.COLUMN} or literal"
                            onChange={e => {
                              const entries = Object.entries(def.report?.param_map || {})
                              entries[pi] = [pname, e.target.value]
                              patchAction(i, { report: { ...(def.report || { obj_name: '' }), param_map: Object.fromEntries(entries) } })
                            }}
                          />
                          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 2 }}
                            title="Remove parameter"
                            onClick={() => {
                              const entries = Object.entries(def.report?.param_map || {}).filter((_, xi) => xi !== pi)
                              patchAction(i, { report: { ...(def.report || { obj_name: '' }), param_map: Object.fromEntries(entries) } })
                            }}>
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                        Each row maps one of the report's prompt criteria to a value —
                        use <code>{'{form.COLUMN}'}</code> to bind the clicked record's column,
                        or type a literal. Unmapped prompts are asked in the viewer.
                      </div>
                    </>
                  ) : def.handler === 'assistant' ? (
                    <>
                      {/* Assistant binding — run a defined intent deterministically,
                          or seed the Assistant chat with a rendered prompt. Values
                          bind from the screen via {form.X} / {header.X} tokens. */}
                      {(() => {
                        const asst = def.assistant || {}
                        const patchAsst = (p: Partial<NonNullable<ActionDef['assistant']>>) =>
                          patchAction(i, { assistant: { ...asst, ...p } })
                        const mode = asst.mode === 'prompt' ? 'prompt' : 'intent'
                        return (
                          <>
                            <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Mode</label>
                            <select
                              style={{ ...inputStyle, width: '100%', marginBottom: 6 }}
                              value={mode}
                              onChange={e => patchAsst({ mode: e.target.value as 'intent' | 'prompt' })}
                            >
                              <option value="intent">Run a defined intent (deterministic, no AI call)</option>
                              <option value="prompt">Ask the AI Assistant a question</option>
                            </select>
                            {mode === 'intent' ? (
                              <>
                                <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Intent ID</label>
                                <input
                                  style={{ ...inputStyle, width: '100%', marginBottom: 4 }}
                                  value={asst.intent_id || ''}
                                  placeholder="e.g. CUSTOMER_CREDIT_POSITION"
                                  onChange={e => patchAsst({ intent_id: e.target.value.toUpperCase().replace(/[^A-Z0-9_]+/g, '_') })}
                                />
                                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                                  An intent defined in <strong>Configure AI → Intents</strong>. It runs
                                  exactly as designed there (visual / report / SQL / proposed action…)
                                  and shows the result in a modal — no AI call is made.
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                  <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                                    Parameters (intent parameter ← screen value)
                                  </span>
                                  <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 3 }}
                                    onClick={() => {
                                      const pm = { ...(asst.param_map || {}) }
                                      let n = Object.keys(pm).length + 1
                                      while (pm[`param_${n}`] !== undefined) n += 1
                                      pm[`param_${n}`] = ''
                                      patchAsst({ param_map: pm })
                                    }}>
                                    <Plus size={11} /> Add
                                  </button>
                                </div>
                                {Object.entries(asst.param_map || {}).map(([pname, pval], pi) => (
                                  <div key={pi} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
                                    <input
                                      style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                                      value={pname}
                                      placeholder="parameter"
                                      onChange={e => {
                                        const entries = Object.entries(asst.param_map || {})
                                        entries[pi] = [e.target.value, pval]
                                        patchAsst({ param_map: Object.fromEntries(entries) })
                                      }}
                                    />
                                    <input
                                      style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                                      value={String(pval ?? '')}
                                      placeholder="{form.COLUMN} / {header.COLUMN} or literal"
                                      onChange={e => {
                                        const entries = Object.entries(asst.param_map || {})
                                        entries[pi] = [pname, e.target.value]
                                        patchAsst({ param_map: Object.fromEntries(entries) })
                                      }}
                                    />
                                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 2 }}
                                      title="Remove parameter"
                                      onClick={() => {
                                        const entries = Object.entries(asst.param_map || {}).filter((_, xi) => xi !== pi)
                                        patchAsst({ param_map: Object.fromEntries(entries) })
                                      }}>
                                      <X size={12} />
                                    </button>
                                  </div>
                                ))}
                                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                                  <code>{'{form.COLUMN}'}</code> binds the current form's value,{' '}
                                  <code>{'{header.COLUMN}'}</code> the parent/header form's (on a list
                                  row both are the row). Literals pass through.
                                </div>
                              </>
                            ) : (
                              <>
                                <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Prompt</label>
                                <textarea
                                  style={{ ...inputStyle, width: '100%', minHeight: 56, resize: 'vertical', marginBottom: 4 }}
                                  value={asst.prompt || ''}
                                  placeholder={'e.g. Show the credit position of customer {form.CUSTOMER_CODE}'}
                                  onChange={e => patchAsst({ prompt: e.target.value })}
                                />
                                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                                  Sent to the app's <strong>AI Assistant</strong> (the one end-users chat with
                                  at runtime) as their question, with <code>{'{form.COLUMN}'}</code> /{' '}
                                  <code>{'{header.COLUMN}'}</code> tokens filled from the screen. Needs the
                                  app's AI configured.
                                </div>
                              </>
                            )}
                            <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Window title (optional)</label>
                            <input
                              style={{ ...inputStyle, width: '100%' }}
                              value={asst.title || ''}
                              placeholder="Defaults to the action name"
                              onChange={e => patchAsst({ title: e.target.value })}
                            />
                          </>
                        )
                      })()}
                    </>
                  ) : def.handler === 'payment' ? (
                    <>
                      {/* Take payment for THIS record through the application's
                          own gateway (Advanced → Payments).

                          Every field names a COLUMN, never a value. The
                          binding reaches the browser and comes back with the
                          checkout request, so a value here would be a value a
                          customer could edit; the server re-reads the saved
                          record and resolves the amount from `amount_column`
                          itself. */}
                      {(() => {
                        const pay = def.payment || { amount_column: '' }
                        const patchPay = (p: Partial<NonNullable<ActionDef['payment']>>) =>
                          patchAction(i, { payment: { ...pay, ...p } })
                        const patchWb = (p: Record<string, string>) =>
                          patchPay({ writeback: { ...(pay.writeback || {}), ...p } })
                        const colOptions = [...new Set([...(formColumns || []), ...(headerColumns || [])])]
                        const colInput = (
                          label: string, value: string, set: (v: string) => void,
                          hint?: string, placeholder?: string,
                        ) => (
                          <>
                            <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{label}</label>
                            <input
                              list={`paycols-${i}`}
                              style={{ ...inputStyle, width: '100%', marginBottom: hint ? 2 : 6 }}
                              value={value}
                              placeholder={placeholder || ''}
                              onChange={e => set(e.target.value)}
                            />
                            {hint && (
                              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                                {hint}
                              </div>
                            )}
                          </>
                        )
                        return (
                          <>
                            <datalist id={`paycols-${i}`}>
                              {colOptions.map(c => <option key={c} value={c} />)}
                            </datalist>
                            {colInput('Amount column', pay.amount_column || '',
                              v => patchPay({ amount_column: v }),
                              'Required. Read from the SAVED record on the server, in '
                              + 'major units (3200.50). The browser never sends an amount.',
                              'TOTAL_AMOUNT')}
                            {colInput('Who may pay — customer column', pay.owner_column || '',
                              v => patchPay({ owner_column: v }),
                              'The customer on the record must match the signed-in '
                              + 'caller. Leave empty and only signed-in users of the '
                              + 'application may use this button.',
                              'CUSTOMER_ID')}
                            {colInput('Reference column (gateway receipt)',
                              pay.reference_column || '',
                              v => patchPay({ reference_column: v }), undefined, 'ORDER_NO')}
                            {colInput('Currency column (optional)', pay.currency_column || '',
                              v => patchPay({ currency_column: v }),
                              'Empty ⇒ the gateway’s configured currency.')}
                            {colInput('Payer email column (prefill)',
                              pay.payer_email_column || '',
                              v => patchPay({ payer_email_column: v }))}
                            <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px dashed var(--color-border)' }}>
                              <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 4 }}>
                                Stamp the record when the money is captured
                              </div>
                              {colInput('Status column', pay.writeback?.status_column || '',
                                v => patchWb({ status_column: v }), undefined, 'PAY_STATUS')}
                              {colInput('Status value', pay.writeback?.status_value || '',
                                v => patchWb({ status_value: v }),
                                'A record already reading this value cannot be charged again.',
                                'PAID')}
                              {colInput('Payment id column', pay.writeback?.payment_id_column || '',
                                v => patchWb({ payment_id_column: v }), undefined, 'PAYMENT_REF')}
                              {colInput('Paid-at column', pay.writeback?.paid_at_column || '',
                                v => patchWb({ paid_at_column: v }), undefined, 'PAID_ON')}
                              {colInput('Payment method column', pay.writeback?.method_column || '',
                                v => patchWb({ method_column: v }), undefined, 'PAY_METHOD')}
                              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                                These columns are written ONLY by the payment process —
                                a form save or an API call may re-post them unchanged
                                but never change them.
                              </div>
                              <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                                Cash-on-delivery status value
                              </label>
                              <input
                                style={{ ...inputStyle, width: '100%', marginBottom: 2 }}
                                value={pay.cod_status_value || ''}
                                placeholder="COD"
                                onChange={e => patchPay({ cod_status_value: e.target.value || undefined })}
                              />
                              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                                Set it to offer cash on delivery (when the project’s
                                Payments setup allows it): the status column is stamped
                                with this value and the method column with COD. Empty ⇒
                                not offered.
                              </div>
                            </div>
                          </>
                        )
                      })()}
                    </>
                  ) : def.handler === 'send' ? (
                    // Send a message built from this record (and, in bulk,
                    // the selected records grouped per entity): templates,
                    // channels, attachable R/V/D, lookup masters. The click
                    // opens the preview; the server re-reads this block.
                    <SendActionEditor
                      binding={def.send}
                      onChange={(send) => patchAction(i, { send })}
                      project={project}
                      objName={sourceObjName || ''}
                      headerColumns={headerColumns}
                      mode="button"
                    />
                  ) : def.handler === 'open' ? (
                    // Open ANOTHER object with values from this record — a
                    // transaction (add / edit / edit-or-add, columns set in
                    // order with their item changes, then lines pulled), or a
                    // process / dashboard / visual / report with its criteria.
                    <OpenActionEditor
                      def={def}
                      onPatch={(p) => patchAction(i, p)}
                      project={project}
                      formColumns={formColumns}
                      headerColumns={headerColumns}
                      actionIndex={i}
                    />
                  ) : def.handler === 'wizard' ? (
                    // Start a Transaction Wizard with launch inputs taken from
                    // this record (its declared inputs ← {form.X} / literals).
                    <WizardActionEditor
                      def={def}
                      onPatch={(p) => patchAction(i, p)}
                      project={project}
                      sourceObjName={sourceObjName}
                      formColumns={formColumns}
                      headerColumns={headerColumns}
                      actionIndex={i}
                    />
                  ) : def.handler === 'import' ? (
                    <>
                      {/* Import records from a file into THIS screen — the
                          Assistant's file import without the AI: the user
                          picks a file, columns are matched to fields by name
                          and can be corrected, then the side-by-side preview
                          opens and Save All writes through the engine. The
                          server re-reads these settings from the model, and
                          the user still needs the add / edit right the mode
                          implies. Put the button on the header form. */}
                      {(() => {
                        const imp = def.import || {}
                        const patchImp = (p: Partial<NonNullable<ActionDef['import']>>) =>
                          patchAction(i, { import: { ...imp, ...p } })
                        return (
                          <>
                            <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Default mode</label>
                            <select
                              style={{ ...inputStyle, marginBottom: 6 }}
                              value={imp.mode || 'upsert'}
                              onChange={e => patchImp({ mode: e.target.value as 'add' | 'edit' | 'upsert' })}
                            >
                              <option value="upsert">Add new and update existing (matched by key)</option>
                              <option value="add">Add new records only</option>
                              <option value="edit">Update existing records only</option>
                            </select>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6, cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={!imp.lock_mode}
                                onChange={e => patchImp({ lock_mode: e.target.checked ? undefined : true })}
                              />
                              Let the user change the mode
                            </label>
                            <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Window title (optional)</label>
                            <input
                              style={{ ...inputStyle, marginBottom: 6 }}
                              value={imp.title || ''}
                              placeholder={def.name || 'Import records'}
                              onChange={e => patchImp({ title: e.target.value || undefined })}
                            />
                            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.4, marginBottom: 6 }}>
                              Opens a window to choose a CSV, Excel or JSON file, match its columns to this
                              screen's fields (and its row grids), then review and save the records side by side.
                              Place it on the header form.
                            </div>
                          </>
                        )
                      })()}
                    </>
                  ) : (
                    <>
                      <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Business Logic</label>
                      {renderBlApi(
                        actionToIc(def),
                        (ic) => patchAction(i, icToActionPatch(ic)),
                        formColumns,
                        headerColumns,
                      )}
                    </>
                  )}

                  {/* Pre-action validations — same structured editor as form-level. */}
                  <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px dashed var(--color-border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>Pre-action validations ({vlist.length})</span>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: 3 }}
                        onClick={() => openValidationRuleEditor('', formColumns, headerColumns, (v) => {
                          patchAction(i, { validations: [...vlist, v] })
                        })}>
                        <Plus size={11} /> Add
                      </button>
                    </div>
                    {vlist.map((v, vi) => (
                      <div key={vi} style={listItemStyle}>
                        <span style={{ flex: 1, fontSize: '0.8125rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: (isValidationRule(v) && v.disabled) ? 0.55 : 1 }}>{validationSummary(v)}</span>
                        {isValidationRule(v) && (
                          <button title={v.disabled ? 'Enable validation' : 'Disable validation'}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: v.disabled ? 'var(--color-text-secondary)' : 'var(--color-accent)', padding: 2 }}
                            onClick={() => {
                              const next = vlist.map((x, xi) => {
                                if (xi !== vi || !isValidationRule(x)) return x
                                return { ...x, disabled: !x.disabled }
                              })
                              patchAction(i, { validations: next })
                            }}>
                            <Power size={12} />
                          </button>
                        )}
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)', padding: 2 }}
                          onClick={() => openValidationRuleEditor(v, formColumns, headerColumns, (newVal) => {
                            patchAction(i, { validations: vlist.map((x, xi) => xi === vi ? newVal : x) })
                          })}>
                          <Edit3 size={12} />
                        </button>
                        <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)', padding: 2 }}
                          onClick={() => patchAction(i, { validations: vlist.filter((_, xi) => xi !== vi) })}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Confirmation gate — runs AFTER the pre-action validations
                      pass and BEFORE the action's effect. Verified server-side
                      (the typed phrase, an emailed one-time code, or the user's
                      account password). */}
                  <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px dashed var(--color-border)' }}>
                    <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                      Confirmation before running
                    </label>
                    <select
                      style={{ ...inputStyle, width: '100%', marginBottom: 6 }}
                      value={def.confirmation?.mode || 'none'}
                      onChange={e => {
                        const mode = e.target.value as ConfirmMode
                        patchAction(i, {
                          confirmation: mode === 'none'
                            ? undefined
                            : { ...(def.confirmation || {}), mode },
                        })
                      }}
                    >
                      {(Object.keys(CONFIRM_MODE_LABELS) as ConfirmMode[]).map(m => (
                        <option key={m} value={m}>{CONFIRM_MODE_LABELS[m]}</option>
                      ))}
                    </select>

                    {def.confirmation && def.confirmation.mode !== 'none' && (
                      <>
                        <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                          Message (optional)
                        </label>
                        <textarea
                          style={{ ...inputStyle, width: '100%', marginBottom: 6, minHeight: 46, resize: 'vertical' }}
                          value={def.confirmation.message || ''}
                          placeholder={'e.g. Are you sure you want to edit customer {form.customer_name}?'}
                          onChange={e => patchAction(i, {
                            confirmation: { ...def.confirmation!, message: e.target.value },
                          })}
                        />
                        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                          Use <code>{'{form.COLUMN}'}</code> / <code>{'{header.COLUMN}'}</code> to drop in the record's values.
                        </div>

                        {def.confirmation.mode === 'phrase' && (
                          <>
                            <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                              Phrase the user must type
                            </label>
                            <input
                              type="text"
                              style={{ ...inputStyle, width: '100%', marginBottom: 6 }}
                              value={def.confirmation.phrase || ''}
                              placeholder={'{form.txn_id} or a fixed word like DELETE'}
                              onChange={e => patchAction(i, {
                                confirmation: { ...def.confirmation!, phrase: e.target.value },
                              })}
                            />
                            <label style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                              Field label (what to type)
                            </label>
                            <input
                              type="text"
                              style={{ ...inputStyle, width: '100%' }}
                              value={def.confirmation.phrase_label || ''}
                              placeholder="e.g. transaction id"
                              onChange={e => patchAction(i, {
                                confirmation: { ...def.confirmation!, phrase_label: e.target.value },
                              })}
                            />
                          </>
                        )}
                        {def.confirmation.mode === 'otp' && (
                          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                            A 6-digit code is emailed to the signed-in user; they type it back to proceed.
                          </div>
                        )}
                        {def.confirmation.mode === 'password' && (
                          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                            The signed-in user must re-enter their own account password to proceed.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Structured pre-action validation editor (shared with form validations). */}
      <ValidationRuleEditor
        open={ruleValidationOpen}
        onClose={() => setRuleValidationOpen(false)}
        value={editingRuleValidation?.value ?? ''}
        formColumnNames={editingRuleValidation?.formColumns || []}
        headerColumnNames={editingRuleValidation?.headerColumns || []}
        formList={formList}
        project={project}
        onSave={(v) => editingRuleValidation?.onSave(v)}
        onOpenCode={onOpenCode}
      />

      {/* Business-Logic / Call-API modal for the action's own logic. */}
      {renderEditors()}
    </div>
  )
}

